import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatCurrency, formatDate, formatDateTime, formatDateTimeSeconds, formatFileSize } from "@/lib/api";
import { resolveGuarantee, buildGuaranteeCode } from "@/lib/guaranteeMatch";
import { VariantPartnerEmailDialog } from "@/components/VariantPartnerEmailDialog";
import { PartnerReplyDialog } from "@/components/PartnerReplyDialog";
import { VariantsCompareTable } from "@/components/VariantsCompareTable";
import PaymentMethodSelect from "@/components/PaymentMethodSelect";
import { useFirstTimeHint } from "@/lib/useFirstTimeHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { MoneyInput } from "@/components/MoneyInput";
import { DropZone } from "@/components/DropZone";
import { ScanButton } from "@/components/ScanButton";
import { CameraButton } from "@/components/CameraButton";
import { CoveragePicker } from "@/components/CoveragePicker";
import { Plus, Send, FileText, Trash2, Check, X as XIcon, Euro, Copy, AlertCircle, RotateCcw, Trash, Eye, EyeOff, Bell, TrendingUp, Clock, AlertTriangle, Banknote, Download, FileSpreadsheet, Files, ListPlus, Printer, ChevronUp, ChevronDown, Pencil, Zap, ExternalLink, Mail, CheckCircle2, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import { EmailPreviewDialog, shouldSkipEmailPreview } from "@/components/EmailPreviewDialog";
import { PdfPreview } from "@/components/PdfPreview";
import { downloadWithProgress, runExtractionWithProgress, uploadWithProgress } from "@/lib/downloadWithProgress";

const STATUS_LABEL = {
  draft: { label: "Bozza", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  inviata: { label: "Inviata", cls: "bg-blue-50 text-blue-800 border-blue-300" },
  accettata: { label: "Accettata", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  pagata: { label: "Pagata", cls: "bg-green-50 text-green-800 border-green-300" },
  scaduta: { label: "Scaduta", cls: "bg-gray-100 text-gray-600 border-gray-300" },
  annullata: { label: "Annullata", cls: "bg-rose-50 text-rose-800 border-rose-300" },
  eliminata: { label: "Cestino", cls: "bg-slate-200 text-slate-600 border-slate-400" },
};

const emptyCov = { name: "", sum_insured: "", premium: "", deductible: "", description: "", client_visible: true, client_price_visible: false };

export default function QuoteOffers() {
  useFirstTimeHint("crm_dblclick_hint_v1", "💡 Suggerimento: doppio click sul nome del cliente per aprire la scheda in modifica.");
  // 🆕 Helper: aggiunge esattamente 1 anno a una data ISO YYYY-MM-DD (gestisce 29 feb → 28 feb)
  const addOneYearIso = (iso) => {
    const s = (iso || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return "";
    const ny = y + 1;
    const isLeap = (ny % 4 === 0 && ny % 100 !== 0) || ny % 400 === 0;
    const daysInMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const clampedD = Math.min(d, daysInMonth[m - 1]);
    return `${ny}-${String(m).padStart(2, "0")}-${String(clampedD).padStart(2, "0")}`;
  };
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [offers, setOffers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // 🆕 Se true, l'offerta in editing è appena stata creata automaticamente (placeholder).
  // Cliccando "Annulla" senza salvare, l'offerta verrà cancellata per evitare orfani.
  const [editingFresh, setEditingFresh] = useState(false);
  // 🆕 Autosave intelligente: salva la bozza ogni 30s dopo l'ultimo cambio del form
  const [autoSaveAt, setAutoSaveAt] = useState(null);
  const formChangedSinceOpenRef = useRef(false);
  const [form, setForm] = useState({ quote_id: "", amount: "", coverages: [{ ...emptyCov }], selected_coverages: [], notes: "", frazionamento: "annuale", driving_type: "", valid_until: "" });
  const [busy, setBusy] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  const [groupByClient, setGroupByClient] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showOnlyFromPolicy, setShowOnlyFromPolicy] = useState(false); // 🆕 16/07/2026 — Filtro "Solo offerte generate da polizze"
  const [showTrendChart, setShowTrendChart] = useState(() => {
    try { return localStorage.getItem("offers_trend_visible") !== "0"; } catch { return true; }
  });
  const [markPaidOpen, setMarkPaidOpen] = useState(null);
  // 🆕 Dialog "Forza pagamento" — segna pagata immediatamente con messaggio di conferma esplicito
  const [forcePaidOpen, setForcePaidOpen] = useState(null);
  const [forcePaidMethod, setForcePaidMethod] = useState("bonifico");
  const [forcePaidBusy, setForcePaidBusy] = useState(false);
  // 🆕 Feb 2026 — Opt-in esplicito per notificare il cliente (email di conferma + push)
  const [forcePaidNotifyClient, setForcePaidNotifyClient] = useState(false);
  // 🆕 Dialog "Offerta accettata" (richiede 2 flag + crea polizza + navigate)
  const [acceptedOpen, setAcceptedOpen] = useState(null);
  const [acceptedFlags, setAcceptedFlags] = useState({ accepted: false, paid: false });
  const [paidMethod, setPaidMethod] = useState("bonifico");
  const [sendOpen, setSendOpen] = useState(null);  // {offer, alsoWhatsapp}
  // 🆕 Contesto per l'anteprima email prima dell'invio
  const [emailPreviewCtx, setEmailPreviewCtx] = useState(null); // { typeKey, title, fetchPreview, onConfirm }
  // 🆕 09/07/2026 — Dialog "Offerta pagata → polizza creata" (avvisa e chiede se aprire ora o dopo)
  const [policyCreatedInfo, setPolicyCreatedInfo] = useState(null); // { policyId, offerNumber, clientName }
  const [viewOpen, setViewOpen] = useState(null);  // offer per riepilogo compatto
  const [previewOpen, setPreviewOpen] = useState(false); // 🆕 dialog anteprima cliente iframe
  const [previewVersion, setPreviewVersion] = useState(0); // 🆕 cache-buster per refresh iframe anteprima
  const [view, setView] = useState("active");  // "active" | "trash"
  const [stats, setStats] = useState(null);
  const [lineItems, setLineItems] = useState([]);  // voci offerta riutilizzabili
  const [policyCoverages, setPolicyCoverages] = useState([]); // 🆕 lista garanzie da gestione polizze
  // 🆕 Lista garanzie ordinata secondo il campo `sort_order` della lookup list padre (fallback: order → label alfabetico)
  const sortedPolicyCoverages = useMemo(() => {
    return [...(policyCoverages || [])].sort((a, b) => {
      const oa = a.sort_order != null ? Number(a.sort_order) : (a.order != null ? Number(a.order) : 9999);
      const ob = b.sort_order != null ? Number(b.sort_order) : (b.order != null ? Number(b.order) : 9999);
      if (oa !== ob) return oa - ob;
      return (a.label || a.code || "").localeCompare(b.label || b.code || "");
    });
  }, [policyCoverages]);
  // 🆕 Filtra le garanzie in base al TIPO di preventivo collegato (auto/casa/vita/…)
  // Se il preventivo non specifica tipo, mostra tutte. Se una garanzia non specifica policy_type,
  // viene considerata "universale" e resta sempre visibile.
  const linkedQuoteType = useMemo(() => {
    const q = quotes.find((qq) => qq.id === form.quote_id);
    return (q?.type || "").toLowerCase();
  }, [quotes, form.quote_id]);
  // 🆕 Feb 2026 — Preventivo collegato completo (per pre-compilare date decorrenza/scadenza nelle nuove varianti)
  const linkedQuote = useMemo(() => quotes.find((qq) => qq.id === form.quote_id) || null, [quotes, form.quote_id]);
  const linkedQuoteStart = linkedQuote?.details?.start_date || "";
  const linkedQuoteEnd = linkedQuote?.details?.end_date || "";
  // 🆕 Feb 2026 — RIPRISTINO LISTA COMPLETA: il filtro per `policy_type` tagliava la maggior
  // parte delle garanzie (nel DB ogni voce ha un tipo specifico, quindi `pt === linkedQuoteType`
  // rimuoveva anche le universali / di altri rami). Ora mostriamo TUTTE le garanzie attive e
  // utilizziamo l'ordinamento per far salire quelle del tipo del preventivo collegato.
  const sortedPolicyCoveragesForType = useMemo(() => {
    const base = sortedPolicyCoverages.filter((p) => p.is_active !== false);
    if (!linkedQuoteType) return base;
    // Ordina: prima le garanzie del tipo matching, poi le universali (policy_type vuoto), poi il resto.
    const score = (p) => {
      const pt = String(p.policy_type || p.applies_to || p.type || "").toLowerCase();
      if (pt === linkedQuoteType) return 0;
      if (!pt) return 1;
      return 2;
    };
    return [...base].sort((a, b) => {
      const sa = score(a);
      const sb = score(b);
      if (sa !== sb) return sa - sb;
      // mantieni l'ordinamento di sortedPolicyCoverages (per sort_order/label)
      return 0;
    });
  }, [sortedPolicyCoverages, linkedQuoteType]);
  const [accessoryItems, setAccessoryItems] = useState([]);   // 🆕 voci accessorie da gestione agenzia
  const [brokers, setBrokers] = useState([]);   // 🆕 broker/agenzie partner per varianti
  const [companies, setCompanies] = useState([]);   // 🆕 compagnie assicurative
  const [partnerCompanyLinks, setPartnerCompanyLinks] = useState([]);   // 🆕 link broker↔compagnia
  // 🆕 Feb 2026 — Catalogo Prodotti + visibilità margini per varianti
  const [products, setProducts] = useState([]);
  const [marginConfig, setMarginConfig] = useState({ can_see: false });
  const canSeeMargins = !!marginConfig.can_see;
  const [lineItemsMenuOpen, setLineItemsMenuOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportForm, setExportForm] = useState({ fmt: "xlsx", date_from: "", date_to: "", status: "all" });
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ fmt: exportForm.fmt });
      if (exportForm.date_from) params.set("date_from", exportForm.date_from);
      if (exportForm.date_to) params.set("date_to", exportForm.date_to);
      if (exportForm.status && exportForm.status !== "all") params.set("status", exportForm.status);
      const res = await api.get(`/quote-offers/export?${params.toString()}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : `offerte_finali.${exportForm.fmt}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Esportato ${filename}`);
      setExportOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [o, q, c, s, li, pc, ac, br, cmp, pcl, prd, mcfg] = await Promise.all([
        api.get(`/quote-offers${view === "trash" ? "?trash=true" : ""}`),
        api.get("/quotes"),
        api.get("/clients?kind=all"),
        api.get("/quote-offers/stats").catch(() => ({ data: null })),
        api.get("/offer-line-items").catch(() => ({ data: [] })),
        api.get("/policy-coverages").catch(() => ({ data: [] })),
        Promise.resolve({ data: [] }), // 🆕 accessory-items unificati in offer-line-items — placeholder per non rompere gli indici
        api.get("/brokers").catch(() => ({ data: [] })),
        api.get("/companies").catch(() => ({ data: [] })),
        api.get("/partner-company-links").catch(() => ({ data: [] })),
        api.get("/products", { params: { active_only: true } }).catch(() => ({ data: [] })),
        api.get("/product-margins-config").catch(() => ({ data: { can_see: false } })),
      ]);
      setOffers(o.data);
      setQuotes(q.data);
      const map = {};
      (c.data || []).forEach((cl) => { map[cl.id] = cl; });
      setClientsMap(map);
      setStats(s.data);
      setLineItems(li.data || []);
      setPolicyCoverages(pc.data || []);
      // 🆕 Accessory items ora unificati con offer_line_items — usa la stessa lista
      setAccessoryItems(li.data || []);
      setBrokers(br.data || []);
      setCompanies(cmp.data || []);
      setPartnerCompanyLinks(pcl.data || []);
      setProducts(prd.data || []);
      setMarginConfig(mcfg.data || { can_see: false });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [view]);

  // 🆕 Salva una voce accessoria custom nell'elenco riutilizzabile dell'agenzia
  const saveLineItemToList = async ({ name, default_premium, description, policy_type }) => {
    const nm = String(name || "").trim();
    if (!nm) { toast.error("Inserisci prima il nome della voce"); return; }
    // Se già presente (case-insensitive), non ricreare
    const exists = (lineItems || []).find((li) => (li.name || "").trim().toLowerCase() === nm.toLowerCase());
    if (exists) { toast.info(`"${nm}" è già in elenco`); return; }
    try {
      const payload = {
        name: nm,
        default_premium: default_premium != null && default_premium !== "" ? Number(default_premium) : null,
        description: (description || "").trim(),
        policy_type: (policy_type || "").trim(),
        sort_order: 100,
        is_active: true,
      };
      const { data } = await api.post("/offer-line-items", payload);
      setLineItems((prev) => [...(prev || []), data]);
      setAccessoryItems((prev) => [...(prev || []), data]);
      toast.success(`"${nm}" salvata in elenco voci accessorie`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // 🆕 Salva una garanzia custom nella lookup list "policy_coverages" per riutilizzarla
  const savePolicyCoverageToList = async ({ label, description, policy_type, sum_insured, deductible }) => {
    const lb = String(label || "").trim();
    if (!lb) { toast.error("Inserisci prima il nome della garanzia"); return; }
    // Se già presente (case-insensitive per label), non ricreare
    const exists = (policyCoverages || []).find((p) => (p.label || "").trim().toLowerCase() === lb.toLowerCase());
    if (exists) { toast.info(`"${lb}" è già in elenco`); return; }
    // Genera un code auto slug: lowercase, senza accenti, snake_case (no troncamento)
    const code = buildGuaranteeCode(lb);
    if (!code) { toast.error("Nome garanzia non valido"); return; }
    try {
      const payload = {
        code,
        label: lb,
        description: (description || "").trim(),
        policy_type: (policy_type || "").trim(),
        sort_order: 100,
        priority: "utile",
        is_active: true,
        default_sum_insured: sum_insured != null && sum_insured !== "" ? Number(sum_insured) : null,
        default_deductible: deductible ? String(deductible).trim() : null,
      };
      const { data } = await api.post("/policy-coverages", payload);
      setPolicyCoverages((prev) => [...(prev || []), data]);
      toast.success(`"${lb}" salvata in elenco garanzie`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  // 🆕 Auto-refresh: quando la scheda torna in primo piano (visibilitychange/focus) e polling ogni 30s
  useEffect(() => {
    const onVis = () => { if (!document.hidden) load(); };
    const onFocus = () => load();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    const iv = setInterval(load, 30000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      clearInterval(iv);
    };
  }, [view]);

  // 🆕 Auto-apre l'offerta passata via ?focus=<id> (es. dal pulsante "Apri offerta" nei preventivi)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || !offers || offers.length === 0) return;
    const o = offers.find((x) => x.id === focusId);
    if (!o) return;
    const fresh = searchParams.get("fresh") === "1";
    const fromBrokers = searchParams.get("from_brokers") === "1";
    openEdit(o, fresh);
    if (fromBrokers) {
      toast.success("Compagnie aggiornate — dropdown ricaricato");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    next.delete("fresh");
    next.delete("from_brokers");
    setSearchParams(next, { replace: true });
  }, [offers, searchParams]);

  // 🆕 Chiusura dialog: se l'offerta è "fresh" (auto-creata e non ancora salvata)
  //    la elimina automaticamente per evitare orfani.
  const closeOfferDialog = async (opts = {}) => {
    const { skipFreshDelete = false } = opts;
    if (editingFresh && editing?.id && !skipFreshDelete) {
      const ok = window.confirm(
        `Stai abbandonando una bozza di offerta non salvata${editing.offer_number ? ` (${editing.offer_number})` : ""}.\n\nVuoi cancellarla?\n\n• OK = cancella e chiudi\n• Annulla = torna alla compilazione`
      );
      if (!ok) return; // resta nel dialog
      try {
        await api.delete(`/quote-offers/${editing.id}`);
        toast.info(`Offerta ${editing.offer_number || ""} annullata (non salvata)`);
      } catch (e) {
        toast.warning(`Impossibile cancellare l'offerta non salvata: ${formatApiError(e)}`);
      }
    }
    setEditingFresh(false);
    setOpen(false);
    load();
  };

  const openCreate = () => {
    setEditing(null);
    setEditingFresh(false);
    formChangedSinceOpenRef.current = false;
    setAutoSaveAt(null);
    setForm({ quote_id: "", amount: "", coverages: [{ ...emptyCov }], selected_coverages: [], notes: "", frazionamento: "annuale", driving_type: "", valid_until: "" });
    setOpen(true);
  };

  const openEdit = (o, fresh = false) => {
    setEditing(o);
    setEditingFresh(!!fresh);
    formChangedSinceOpenRef.current = false;
    setAutoSaveAt(null);
    // 🆕 Se l'offerta non ha ancora coperture selezionate, eredita da quelle del preventivo
    const linkedQ = quotes.find((qq) => qq.id === o.quote_id);
    const inheritedSelectedCovs = Array.isArray(o.selected_coverages) && o.selected_coverages.length > 0
      ? o.selected_coverages
      : (linkedQ?.details?.selected_coverages || []);
    setForm({
      quote_id: o.quote_id,
      // 🆕 Se NON c'è una variante finalizzata, lascia il campo Importo VUOTO (l'agente può
      // comunque inserire manualmente un valore prima di finalizzare). Se c'è winner, mostra l'importo.
      amount: o.winner_variant_id ? String(o.amount ?? "") : "",
      coverages: (o.coverages || []).map((c) => ({
        ...emptyCov,
        ...c,
        sum_insured: c.sum_insured || "",
        premium: c.premium || "",
        kind: c.kind || "coverage",
        // 🆕 Ripristina marker auto_code dal campo persistito `code` (per sync con CoveragePicker)
        _auto_code: c._auto_code || c.code || null,
      })),
      notes: o.notes || "",
      selected_coverages: inheritedSelectedCovs,
      frazionamento: o.frazionamento || linkedQ?.details?.frazionamento || "annuale",
      driving_type: o.driving_type || linkedQ?.details?.driving_type || "",
      valid_until: o.valid_until ? String(o.valid_until).slice(0, 10) : "",
    });
    setVariants(o.variants || []);
    setWinnerVariantId(o.winner_variant_id || null);
    setOpen(true);
  };

  // 🆕 Autosave: ogni cambio significativo del form schedula un salvataggio dopo 30s di inattività.
  // Funziona solo per offerte già esistenti in editing (editing.id). All'apertura non parte (skip first).
  useEffect(() => {
    if (!open || !editing?.id) return;
    // Skip alla prima esecuzione (subito dopo openEdit/openCreate)
    if (!formChangedSinceOpenRef.current) {
      formChangedSinceOpenRef.current = true;
      return;
    }
    const handle = setTimeout(async () => {
      try {
        await persistOfferFromForm();
        setAutoSaveAt(new Date());
      } catch { /* silent */ }
    }, 30000);
    return () => clearTimeout(handle);
  }, [form, open, editing?.id]);

  // 🆕 Varianti compagnia annidate nell'offerta
  const [variants, setVariants] = useState([]);
  const [winnerVariantId, setWinnerVariantId] = useState(null);
  // 🆕 Quando esiste una variante finalizzata, l'importo da pagare è auto-calcolato come:
  //   amount = somma dei premi delle garanzie della variante finalizzata (coverage + line_item).
  // La sezione "Dettaglio coperture" è stata rimossa: tutti gli importi vengono dalle varianti.
  useEffect(() => {
    if (!winnerVariantId) return;
    const winner = (variants || []).find((v) => v.id === winnerVariantId);
    if (!winner) return;
    const sum = (winner.coverages || [])
      .map((c) => Number(c.premium) || 0)
      .reduce((a, b) => a + b, 0);
    const rounded = sum.toFixed(2);
    if (rounded !== form.amount && sum > 0) {
      setForm((f) => ({ ...f, amount: rounded }));
    }
  }, [winnerVariantId, variants, form.amount]);
  const [newVariant, setNewVariant] = useState({ company: "", amount: "", notes: "", broker_id: "", company_id: "", frazionamento: "", installments_count: "", installments_amounts: null, effective_date: "", expiry_date: "", product_id: "", product_buy_cost: "", product_sell_cost: "", driving_type: "" });
  // 🆕 Mappa frazionamento → numero di rate default (usato come suggerimento; l'utente può sovrascrivere)
  const FRAZ_RATE_MAP = { annuale: 1, semestrale: 2, quadrimestrale: 3, trimestrale: 4, mensile: 12 };
  // 🆕 Quando l'offerta viene aperta/cambiata, precompila il frazionamento della nuova variante
  // eredidandolo dal frazionamento dell'offerta (che a sua volta viene dal preventivo).
  useEffect(() => {
    if (form.frazionamento) {
      setNewVariant((nv) => ({ ...nv, frazionamento: nv.frazionamento || form.frazionamento }));
    }
  }, [form.frazionamento]);
  // 🆕 09/07/2026 — Pre-compila il Tipo di guida della nuova variante ereditandolo
  // dall'offerta (form.driving_type) o dal preventivo collegato (details.driving_type),
  // così quando compare il badge "✓ ereditato" il valore è effettivamente valorizzato.
  useEffect(() => {
    const inherited = form.driving_type || linkedQuote?.details?.driving_type || "";
    if (!inherited) return;
    setNewVariant((nv) => ({ ...nv, driving_type: nv.driving_type || inherited }));
  }, [form.driving_type, linkedQuote?.details?.driving_type]);
  // 🆕 Feb 2026 — Pre-compila effective_date/expiry_date della nuova variante con le date
  // del preventivo collegato (details.start_date / details.end_date). L'utente può sempre
  // sovrascrivere manualmente. Non tocca il valore se l'utente ha già scritto qualcosa.
  useEffect(() => {
    if (!linkedQuoteStart && !linkedQuoteEnd) return;
    setNewVariant((nv) => ({
      ...nv,
      effective_date: nv.effective_date || linkedQuoteStart || "",
      expiry_date: nv.expiry_date || linkedQuoteEnd || "",
    }));
  }, [linkedQuoteStart, linkedQuoteEnd]);

  // 🆕 Feb 2026 — Pre-carica le garanzie richieste dal preventivo (selected_coverages + requested_coverages)
  // dentro `newVariantCovs`, così l'agente le vede nell'UI e può aggiungere solo premi/franchigie prima
  // di creare la variante. Si applica SOLO se l'array è ancora vuoto (non sovrascrive input utente).
  useEffect(() => {
    if (!linkedQuote) return;
    setNewVariantCovs((cur) => {
      if (Array.isArray(cur) && cur.length > 0) return cur; // rispetta input utente
      const qDet = linkedQuote.details || {};
      const codes = Array.isArray(qDet.selected_coverages) ? qDet.selected_coverages : [];
      const req = Array.isArray(qDet.requested_coverages) ? qDet.requested_coverages : [];
      const rows = [];
      const seen = new Set();
      codes.forEach((code) => {
        const meta = (policyCoverages || []).find((p) => p.code === code);
        const label = meta?.label || code;
        seen.add((label || "").trim().toLowerCase());
        rows.push({
          name: label, code, sum_insured: "", premium: "", deductible: "",
          description: meta?.description || "", kind: "coverage",
          client_visible: true, client_price_visible: false,
          company_id: null, company_name: null,
        });
      });
      req.forEach((rc) => {
        const nm = (rc?.name || "").trim();
        if (!nm || seen.has(nm.toLowerCase())) return;
        seen.add(nm.toLowerCase());
        rows.push({
          name: nm, code: null,
          sum_insured: rc.sum_insured != null && rc.sum_insured !== "" ? String(rc.sum_insured) : "",
          premium: "", deductible: rc.deductible || "",
          description: rc.description || "", kind: "coverage",
          client_visible: true, client_price_visible: false,
          company_id: null, company_name: null,
        });
      });
      return rows;
    });
  }, [linkedQuote?.id]);
  // 🆕 Aggiorna il numero rate di default quando cambia il frazionamento (solo se l'utente non ha già scritto un valore custom)
  useEffect(() => {
    const fr = newVariant.frazionamento || form.frazionamento;
    if (!fr) return;
    const def = FRAZ_RATE_MAP[fr];
    if (!def) return;
    setNewVariant((nv) => {
      const cur = String(nv.installments_count || "").trim();
      // Se vuoto o coincide con uno dei default noti (1/2/3/4/12) lo aggiorno al default del nuovo frazionamento
      if (cur === "" || ["1", "2", "3", "4", "12"].includes(cur)) {
        return { ...nv, installments_count: String(def) };
      }
      return nv;
    });
  }, [newVariant.frazionamento, form.frazionamento]);
  // 🆕 Modalità inserimento Compagnia: "select" mostra dropdown compagnie collegate al broker;
  //     "manual" mostra Input libero (per casi in cui la compagnia non è nell'elenco)
  const [newVariantCompanyMode, setNewVariantCompanyMode] = useState("select");
  const [editVariantCompanyMode, setEditVariantCompanyMode] = useState("select");
  // 🆕 Garanzie per la NUOVA variante (opzionali: se vuote, si ereditano da form.selected_coverages)
  const [newVariantCovs, setNewVariantCovs] = useState([]);
  // 🆕 Template rapido da ultima variante della stessa compagnia
  const [companyTemplate, setCompanyTemplate] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const cid = newVariant.company_id;
    if (!cid) { setCompanyTemplate(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/companies/${cid}/latest-variant-template`, {
          params: { exclude_offer_id: editing?.id || "" },
        });
        if (!cancelled) setCompanyTemplate(data?.found ? data : null);
      } catch { if (!cancelled) setCompanyTemplate(null); }
    })();
    return () => { cancelled = true; };
  }, [newVariant.company_id, editing?.id]);
  const applyCompanyTemplate = () => {
    if (!companyTemplate) return;
    // Copia garanzie (svuota premi per rifletere che sono da rivalidare)
    const covs = (companyTemplate.coverages || []).map((c) => ({
      name: c.name || "",
      code: c.code || null,
      sum_insured: c.sum_insured != null ? String(c.sum_insured) : "",
      premium: "", // premi VUOTI: da riscrivere per questa quotazione
      deductible: c.deductible || "",
      description: c.description || "",
      kind: c.kind || "coverage",
      client_visible: c.client_visible !== false,
      client_price_visible: c.client_price_visible !== false,
    }));
    setNewVariantCovs(covs);
    // Se l'agente non ha già scelto frazionamento/rate, applica quelli del template
    setNewVariant((nv) => ({
      ...nv,
      frazionamento: nv.frazionamento || companyTemplate.frazionamento || "",
      installments_count: nv.installments_count || (companyTemplate.installments_count ? String(companyTemplate.installments_count) : ""),
    }));
    toast.success(`Copiate ${covs.length} garanzie da variante precedente (${companyTemplate.source_offer_number || ""}). Premi da re-inserire.`);
  };
  const addNewVariantCov = (kind = "coverage") => setNewVariantCovs((arr) => [
    ...arr,
    { name: "", code: null, sum_insured: "", premium: "", deductible: "", description: "", kind, client_visible: true, client_price_visible: false, company_id: null, company_name: null, _added_in_session: true },
  ]);
  const updateNewVariantCov = (idx, field, val) => {
    setNewVariantCovs((arr) => {
      const copy = [...arr];
      const prev = copy[idx] || {};
      copy[idx] = { ...prev, [field]: val };
      // 🆕 10/07/2026 — Nuova semantica: premi garanzie sono PER RATA (non totale annuo).
      // amount (premio per rata) = Σ premi visibili (senza divisione).
      // Totale annuo = amount × N (calcolato in submit).
      if (field === "premium" && prev.client_visible !== false) {
        const raten = parseInt(newVariant.installments_count, 10);
        const rate = Number.isFinite(raten) && raten >= 1 ? raten : 1;
        const sumAll = copy.reduce((s, c) => {
          if (c.client_visible === false) return s;
          const p = Number(c.premium);
          return s + (Number.isFinite(p) && p > 0 ? p : 0);
        }, 0);
        if (sumAll > 0) {
          const newPerRata = +sumAll.toFixed(2);
          const totAnnuo = +(newPerRata * rate).toFixed(2);
          setNewVariant((n) => ({ ...n, amount: String(newPerRata) }));
          setTimeout(() => {
            toast.info(
              `🔄 Premio per rata = ${formatCurrency(newPerRata)} · Totale annuo = ${formatCurrency(totAnnuo)} (${rate} rate)`,
              { duration: 3500 }
            );
          }, 0);
        }
      }
      return copy;
    });
  };
  const removeNewVariantCov = (idx) => setNewVariantCovs((arr) => arr.filter((_, i) => i !== idx));
  // 🆕 Coperture selezionate dal form principale da copiare nella nuova variante (default: tutte selezionate)
  const [selectedCovIdx, setSelectedCovIdx] = useState(new Set());
  useEffect(() => {
    // Quando cambia il form.coverages, di default seleziona tutte
    setSelectedCovIdx(new Set((form.coverages || []).map((_, i) => i)));
  }, [form.coverages?.length]);
  const toggleCovSelected = (i) => {
    setSelectedCovIdx((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };
  const refreshVariantsFromBackend = async () => {
    if (!editing?.id) return;
    try {
      // 🆕 Ricarica in parallelo: singola offerta + lista completa (per stats + counters riga).
      const [{ data }, { data: allOffers }, { data: freshStats }] = await Promise.all([
        api.get(`/quote-offers/${editing.id}`),
        api.get(`/quote-offers${view === "trash" ? "?trash=true" : ""}`).catch(() => ({ data: null })),
        api.get("/quote-offers/stats").catch(() => ({ data: null })),
      ]);
      if (allOffers) setOffers(allOffers);
      if (freshStats) setStats(freshStats);
      setVariants(data.variants || []);
      setWinnerVariantId(data.winner_variant_id || null);
      // 🆕 Sincronizza anche form.amount/coverages/frazionamento dal backend così che
      // un eventuale `persistOfferFromForm()` successivo invii dati freschi (non stale).
      setForm((f) => ({
        ...f,
        // 🆕 Se non c'è winner, NON sovrascrivere l'amount (l'utente potrebbe averlo digitato manualmente
        // o volerlo lasciare vuoto). Aggiorna solo se c'è una variante finalizzata.
        amount: data.winner_variant_id ? (data.amount != null ? String(data.amount) : f.amount) : f.amount,
        coverages: Array.isArray(data.coverages) && data.coverages.length > 0
          ? data.coverages.map((c) => ({
              ...emptyCov,
              ...c,
              sum_insured: c.sum_insured ?? "",
              premium: c.premium ?? "",
              kind: c.kind || "coverage",
              _auto_code: c._auto_code || c.code || null,
            }))
          : f.coverages,
        frazionamento: data.frazionamento || f.frazionamento,
        driving_type: data.driving_type ?? f.driving_type,
      }));
      // Forza il refresh dell'anteprima cliente
      setPreviewVersion((v) => v + 1);
    } catch { /* silent */ }
  };
  const sortedVariants = useMemo(() => {
    return [...variants].sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
  }, [variants]);
  // Lista visualizzata: varianti "in evidenza" in cima, poi per prezzo
  const displayVariants = useMemo(
    () => [...sortedVariants].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0)),
    [sortedVariants],
  );
  const [selectedVariantIds, setSelectedVariantIds] = useState(() => new Set());
  const [variantBulkBusy, setVariantBulkBusy] = useState(false);
  const [partnerEmailVariant, setPartnerEmailVariant] = useState(null);
  const [variantsView, setVariantsView] = useState(() => localStorage.getItem("offer_variants_view") || "cards");
  const changeVariantsView = (vw) => { setVariantsView(vw); localStorage.setItem("offer_variants_view", vw); };
  const [partnerReply, setPartnerReply] = useState(null);
  const closePartnerReply = useCallback(() => setPartnerReply(null), []);
  useEffect(() => { setSelectedVariantIds(new Set()); }, [editing?.id]);
  const toggleVariantSelected = (id) => setSelectedVariantIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectedVariants = sortedVariants.filter((v) => selectedVariantIds.has(v.id));
  const allSelectedFeatured = selectedVariants.length > 0 && selectedVariants.every((v) => v.is_featured);
  const toggleFeaturedSelected = async () => {
    if (!editing?.id || selectedVariants.length === 0) return;
    setVariantBulkBusy(true);
    try {
      const featured = !allSelectedFeatured;
      const { data } = await api.post(`/quote-offers/${editing.id}/variants/featured`, {
        variant_ids: selectedVariants.map((v) => v.id), featured,
      });
      setVariants(data.variants || []);
      toast.success(featured ? `${data.updated} offerte messe in evidenza` : `Evidenza rimossa da ${data.updated} offerte`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVariantBulkBusy(false); }
  };
  const printSelectedComparison = async () => {
    if (!editing?.id || selectedVariants.length === 0) return;
    setVariantBulkBusy(true);
    try {
      const r = await api.get(`/quote-offers/${editing.id}/variants-comparison-pdf`, {
        params: { variant_ids: selectedVariants.map((v) => v.id).join(",") }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `confronto_selezionate_${editing.offer_number || editing.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVariantBulkBusy(false); }
  };

  // 🆕 Compagnie filtrate per broker/agenzia selezionato (via partner_company_links).
  // Se nessun broker è selezionato, ritorna TUTTE le compagnie. Se il broker non ha
  // link configurati, comunque ritorna tutte per non bloccare l'operatore.
  const getCompaniesForBroker = (brokerId) => {
    if (!brokerId) return companies;
    const links = (partnerCompanyLinks || []).filter(
      (l) => l.partner_id === brokerId && l.active !== false
    );
    if (links.length === 0) return companies;
    const ids = new Set(links.map((l) => l.linked_company_id));
    return (companies || []).filter((c) => ids.has(c.id));
  };
  // Ricava company_id da un nome tipizzato (matching case-insensitive)
  const findCompanyIdByName = (name, brokerId) => {
    const norm = (name || "").trim().toLowerCase();
    if (!norm) return "";
    const list = getCompaniesForBroker(brokerId);
    const hit = (list || []).find((c) => (c.name || "").trim().toLowerCase() === norm);
    return hit?.id || "";
  };
  // 🆕 Numero di compagnie ATTIVAMENTE collegate al broker via partner_company_links.
  // Torna null se il broker non ha alcun link attivo (siamo in fallback "tutte le compagnie").
  const getLinkedCompaniesCount = (brokerId) => {
    if (!brokerId) return null;
    const count = (partnerCompanyLinks || []).filter(
      (l) => l.partner_id === brokerId && l.active !== false
    ).length;
    return count > 0 ? count : null;
  };
  const _sanitizeCoverages = (covs) => (covs || [])
    .filter((c) => (c.name || "").trim() || c.premium || c.sum_insured)
    .map((c) => ({
      name: (c.name || "").trim() || "(senza nome)",
      sum_insured: c.sum_insured === "" || c.sum_insured == null ? null : Number(c.sum_insured),
      premium: c.premium === "" || c.premium == null ? null : Number(c.premium),
      deductible: c.deductible || null,
      description: c.description || "",
      kind: c.kind || "coverage",
      client_visible: c.client_visible !== false,
      client_price_visible: c.client_price_visible !== false,
    }));
  const addVariant = async () => {
    if (!editing?.id) { toast.error("Salva prima l'offerta per aggiungere varianti"); return; }
    const perRateAmt = parseFloat(newVariant.amount);
    const rateN = (() => {
      const n = parseInt(newVariant.installments_count, 10);
      return Number.isFinite(n) && n >= 1 ? n : 1;
    })();
    // 🆕 Piano rateale personalizzato valido: fraz≠annuale, N≥2, tutte rate >0, non uniformi.
    const _fraz = newVariant.frazionamento;
    const _amts = Array.isArray(newVariant.installments_amounts)
      ? newVariant.installments_amounts.map((x) => Number(x) || 0)
      : [];
    const _customValid =
      _fraz && _fraz !== "annuale" && rateN >= 2 && _amts.length === rateN &&
      _amts.every((a) => a > 0) && !_amts.every((a) => Math.abs(a - _amts[0]) < 0.005);
    // Validazione: se piano custom valido → NON serve perRateAmt > 0 (totale = somma rate).
    if (!newVariant.company.trim() || (!_customValid && !(perRateAmt > 0))) {
      toast.error("Compila compagnia e importo per rata");
      return;
    }
    // 🆕 Totale = SOMMA delle rate se piano custom valido, altrimenti perRate × N
    const totalAmt = _customValid
      ? Math.round(_amts.reduce((s, a) => s + a, 0) * 100) / 100
      : Math.round(perRateAmt * rateN * 100) / 100;
    // Payload rate personalizzate SOLO se piano completo (tutte >0) e non uniforme
    const installmentsAmountsPayload = (() => {
      if (!_fraz || _fraz === "annuale" || rateN < 2) return null;
      if (!Array.isArray(_amts) || _amts.length !== rateN) return null;
      if (!_amts.every((a) => a > 0)) return null;
      const perR = Number(perRateAmt) || 0;
      const allEqual = _amts.every((a) => Math.abs(a - perR) < 0.005);
      return allEqual ? null : _amts;
    })();
    try {
      // 🆕 Se l'agente ha specificato garanzie inline nel form nuova variante, usa quelle.
      // Altrimenti, eredita da entrambe le fonti del preventivo collegato:
      //   1. `details.selected_coverages` — codici garanzia scelti dall'agente/wizard
      //   2. `details.requested_coverages` — garanzie richieste dal cliente con massimale/franchigia
      // Il fallback ex-`form.selected_coverages` resta come ultima risorsa.
      let inheritedCovs;
      if (Array.isArray(newVariantCovs) && newVariantCovs.some((c) => (c.name || "").trim())) {
        inheritedCovs = newVariantCovs
          .filter((c) => (c.name || "").trim())
          .map((c) => ({
            name: c.name.trim(),
            code: c.code || null,
            sum_insured: c.sum_insured === "" || c.sum_insured == null ? null : Number(c.sum_insured),
            premium: c.premium === "" || c.premium == null ? null : Number(c.premium),
            deductible: c.deductible || null,
            description: c.description || null,
            kind: c.kind || "coverage",
            client_visible: c.client_visible !== false,
            client_price_visible: c.client_price_visible !== false,
            company_id: c.company_id || null,
            company_name: c.company_name || null,
          }));
      } else {
        inheritedCovs = [];
        const qDet = (linkedQuote && linkedQuote.details) || {};
        // 1. selected_coverages: array di codici → mappa su policyCoverages per etichetta/descrizione
        const codes = Array.isArray(qDet.selected_coverages)
          ? qDet.selected_coverages
          : (Array.isArray(form.selected_coverages) ? form.selected_coverages : []);
        codes.forEach((code) => {
          const meta = (policyCoverages || []).find((p) => p.code === code);
          inheritedCovs.push({
            name: meta?.label || code,
            code,
            sum_insured: null,
            premium: null,
            deductible: null,
            description: meta?.description || "",
            kind: "coverage",
            client_visible: true,
            client_price_visible: false,
          });
        });
        // 2. requested_coverages: array di oggetti {name, sum_insured, deductible, ...}
        //    Copia fedelmente name/massimale/franchigia dedotti dal wizard cliente, senza duplicare
        //    quelle già presenti in selected_coverages (matching case-insensitive sul nome).
        const already = new Set(inheritedCovs.map((c) => (c.name || "").trim().toLowerCase()));
        const reqCovs = Array.isArray(qDet.requested_coverages) ? qDet.requested_coverages : [];
        reqCovs.forEach((rc) => {
          const nm = (rc?.name || "").trim();
          if (!nm || already.has(nm.toLowerCase())) return;
          already.add(nm.toLowerCase());
          inheritedCovs.push({
            name: nm,
            code: null,
            sum_insured: rc.sum_insured != null && rc.sum_insured !== "" ? Number(rc.sum_insured) : null,
            premium: null,
            deductible: rc.deductible || null,
            description: rc.description || "",
            kind: "coverage",
            client_visible: true,
            client_price_visible: false,
          });
        });
      }
      const brokerObj = newVariant.broker_id
        ? (brokers || []).find((b) => b.id === newVariant.broker_id)
        : null;
      const companyId = newVariant.company_id
        || findCompanyIdByName(newVariant.company, newVariant.broker_id);
      await api.post(`/quote-offers/${editing.id}/variants`, {
        company: newVariant.company.trim(),
        amount: totalAmt,
        coverages: inheritedCovs,
        notes: newVariant.notes || null,
        broker_id: brokerObj?.id || null,
        broker_name: brokerObj?.name || null,
        company_id: companyId || null,
        frazionamento: newVariant.frazionamento || null,
        installments_count: rateN,
        installments_amounts: installmentsAmountsPayload,
        effective_date: newVariant.effective_date || null,
        expiry_date: newVariant.expiry_date || null,
        product_id: newVariant.product_id || null,
        product_buy_cost: canSeeMargins && newVariant.product_buy_cost !== "" ? Number(newVariant.product_buy_cost) : null,
        product_sell_cost: canSeeMargins && newVariant.product_sell_cost !== "" ? Number(newVariant.product_sell_cost) : null,
        driving_type: newVariant.driving_type || null,
      });
      toast.success(`Variante "${newVariant.company}" aggiunta${inheritedCovs.length ? ` con ${inheritedCovs.length} garanzie ereditate dal cliente` : ""}`);
      setNewVariant({ company: "", amount: "", notes: "", broker_id: "", company_id: "", frazionamento: form.frazionamento || "", installments_count: "", installments_amounts: null, effective_date: linkedQuoteStart || "", expiry_date: linkedQuoteEnd || "", product_id: "", product_buy_cost: "", product_sell_cost: "", driving_type: form.driving_type || (linkedQuote?.details?.driving_type) || "" });
      setNewVariantCovs([]);
      setEditingFresh(false); // 🆕 modifica reale: l'offerta non è più "fresh"
      await refreshVariantsFromBackend();
      // 🆕 Salva completamente l'offerta dopo aggiunta variante
      await persistOfferFromForm();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  // 🆕 Edit inline di una variante (company, amount, notes)
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [editVariantDraft, setEditVariantDraft] = useState({ company: "", amount: "", notes: "", broker_id: "", company_id: "", frazionamento: "", installments_count: "", effective_date: "", expiry_date: "", driving_type: "", coverages: [], product_id: "", product_buy_cost: "", product_sell_cost: "" });
  const startEditVariant = (v) => {
    setEditingVariantId(v.id);
    // Se la compagnia salvata coincide con una compagnia nota → modalità select.
    // Altrimenti → modalità manuale (input libero).
    const hasKnownCompany = v.company_id && (companies || []).some((c) => c.id === v.company_id);
    setEditVariantCompanyMode(hasKnownCompany || !v.company ? "select" : "manual");
    const fr = v.frazionamento || form.frazionamento || "";
    const rawInst = v.installments_count != null && v.installments_count !== ""
      ? parseInt(v.installments_count, 10)
      : (FRAZ_RATE_MAP[fr] || null);
    const instN = Number.isFinite(rawInst) && rawInst >= 1 ? rawInst : 1;
    // 🆕 In edit mode, mostro il "premio per rata" nel campo amount:
    // per_rata = totale_amount / installments_count (arrotondato a 2 decimali)
    const totalAmt = parseFloat(v.amount) || 0;
    const perRata = instN > 0 ? Math.round((totalAmt / instN) * 100) / 100 : totalAmt;
    setEditVariantDraft({
      company: v.company || "",
      amount: perRata > 0 ? String(perRata) : String(v.amount || ""),
      notes: v.notes || "",
      broker_id: v.broker_id || "",
      company_id: v.company_id || "",
      frazionamento: fr,
      installments_count: String(instN),
      effective_date: v.effective_date || "",
      expiry_date: v.expiry_date || "",
      // 🆕 09/07/2026 — Se la variante non ha ancora un driving_type, pre-compila con
      // quello ereditato dall'offerta o dal preventivo collegato. In questo modo
      // salvando SENZA modificare il campo il valore corretto viene comunque persistito.
      driving_type: v.driving_type || form.driving_type || (linkedQuote?.details?.driving_type) || "",
      coverages: (v.coverages || []).map((c) => ({
        name: c.name || "",
        code: c.code || null,
        sum_insured: c.sum_insured ?? "",
        premium: c.premium ?? "",
        deductible: c.deductible || "",
        description: c.description || "",
        kind: c.kind || "coverage",
        client_visible: c.client_visible !== false,
        client_price_visible: c.client_price_visible !== false,
        // 🛠 Feb 2026 fix — carica anche `company_id`/`company_name` per singola garanzia/voce
        // accessoria, altrimenti riaprendo la variante il campo mostra sempre "stessa variante"
        // anche se salvato correttamente.
        company_id: c.company_id || null,
        company_name: c.company_name || null,
      })),
      // 🆕 Carica gli importi personalizzati per rata (se presenti sul variant)
      installments_amounts: Array.isArray(v.installments_amounts) && v.installments_amounts.length === instN
        ? v.installments_amounts.map((x) => Number(x) || 0)
        : null,
      // 🆕 Feb 2026 — Prodotto catalogo + snapshot margini
      product_id: v.product_id || "",
      product_buy_cost: v.product_buy_cost ?? "",
      product_sell_cost: v.product_sell_cost ?? "",
    });
  };
  const updateEditVariantCov = (idx, field, val) => {
    setEditVariantDraft((d) => {
      const copy = [...(d.coverages || [])];
      const prev = copy[idx] || {};
      copy[idx] = { ...prev, [field]: val };
      // 🆕 10/07/2026 — SEMANTICA CORRETTA: gli importi Premio delle garanzie/voci
      // accessorie sono considerati come importo della SINGOLA RATA (non totale annuo).
      // Quindi il "Premio per rata" della variante = Σ garanzie (senza divisione).
      // Il totale annuo si ottiene poi come amount × N_rate (fatto in submit).
      if (field === "premium" && prev.client_visible !== false) {
        // 🆕 Modalità auto-derivata: se l'utente non ha scelto esplicitamente, deriva da presenza premi.
        let mode;
        if (d.sum_mode === "replace" || d.sum_mode === "add") {
          mode = d.sum_mode;
        } else {
          const hasPremiumsNow = copy.some((c) => c.client_visible !== false && Number(c.premium) > 0);
          mode = hasPremiumsNow ? "replace" : "add";
        }
        if (mode === "replace") {
          // Sostituisci: amount (per rata) = Σ premi visibili (SENZA divisione, sono già per-rata)
          const sumAll = copy.reduce((s, c) => {
            if (c.client_visible === false) return s;
            const p = Number(c.premium);
            return s + (Number.isFinite(p) && p > 0 ? p : 0);
          }, 0);
          const newPerRata = +sumAll.toFixed(2);
          const raten = parseInt(d.installments_count, 10);
          const rate = Number.isFinite(raten) && raten >= 1 ? raten : 1;
          const totAnnuo = +(newPerRata * rate).toFixed(2);
          setTimeout(() => {
            toast.info(
              `🔄 Premio per rata = ${formatCurrency(newPerRata)} · Totale annuo = ${formatCurrency(totAnnuo)} (${rate} rate)`,
              { duration: 4500 }
            );
          }, 0);
          return { ...d, coverages: copy, amount: String(newPerRata) };
        }
        // mode "add" (comportamento incrementale storico) — solo se la coverage è stata aggiunta in questa sessione
        if (prev._added_in_session === true) {
          const currentPerRata = Number(d.amount) || 0;
          const oldPremium = Number(prev.premium) || 0;
          const newPremium = Number(val) || 0;
          const delta = +(newPremium - oldPremium).toFixed(2);
          if (currentPerRata > 0 && delta !== 0) {
            // 🆕 SEMANTICA: premi sono già per-rata → il delta è direttamente somma al per-rata
            const newPerRata = +(currentPerRata + delta).toFixed(2);
            const covLabel = prev.name || val || "Garanzia";
            setTimeout(() => {
              toast.info(
                delta > 0
                  ? `➕ +${formatCurrency(delta)} al Premio per rata (${covLabel}). Nuovo Premio per rata: ${formatCurrency(newPerRata)}`
                  : `➖ -${formatCurrency(Math.abs(delta))} dal Premio per rata (${covLabel}). Nuovo Premio per rata: ${formatCurrency(newPerRata)}`,
                { duration: 4500 }
              );
            }, 0);
            return { ...d, coverages: copy, amount: String(newPerRata) };
          }
        }
      }
      return { ...d, coverages: copy };
    });
  };
  const addEditVariantCov = (kind = "coverage") => {
    setEditVariantDraft((d) => ({
      ...d,
      coverages: [...(d.coverages || []), { name: "", code: null, sum_insured: "", premium: "", deductible: "", description: "", kind, client_visible: true, client_price_visible: false, _added_in_session: true }],
    }));
  };
  const removeEditVariantCov = (idx) => {
    setEditVariantDraft((d) => ({ ...d, coverages: (d.coverages || []).filter((_, i) => i !== idx) }));
  };
  // 🆕 Modalità "Nuove garanzie" derivata automaticamente dallo stato delle coverage:
  //   - Se almeno una garanzia visibile ha un premio > 0 → "replace" (Solo aggiunte)
  //   - Altrimenti → "add" (Somma)
  // La scelta esplicita dell'utente (sum_mode) ha SEMPRE la precedenza sull'auto-default.
  const editVariantEffectiveSumMode = useMemo(() => {
    if (editVariantDraft.sum_mode === "replace" || editVariantDraft.sum_mode === "add") {
      return editVariantDraft.sum_mode;
    }
    const covs = editVariantDraft.coverages || [];
    const hasPremiums = covs.some((c) => c.client_visible !== false && Number(c.premium) > 0);
    return hasPremiums ? "replace" : "add";
  }, [editVariantDraft.sum_mode, editVariantDraft.coverages]);
  // 🆕 10/07/2026 — Auto-fill "Premio per rata" al primo inserimento.
  // Con la nuova semantica: premi delle garanzie/voci accessorie sono considerati
  // per SINGOLA RATA → amount (per rata) = Σ premi (senza divisione per N).
  // Il totale annuo verrà calcolato come amount × N_rate.
  useEffect(() => {
    const covs = editVariantDraft.coverages || [];
    if (covs.length === 0) return;
    const covsNotIncremental = covs.filter((c) => c._added_in_session !== true);
    const sumCovs = covsNotIncremental.reduce((s, c) => {
      const p = Number(c.premium);
      return s + (Number.isFinite(p) && p > 0 ? p : 0);
    }, 0);
    if (sumCovs <= 0) return;
    const currentPerRata = Number(editVariantDraft.amount) || 0;
    if (sumCovs > currentPerRata) {
      const newPerRata = Math.round(sumCovs * 100) / 100;
      setEditVariantDraft((d) => ({ ...d, amount: String(newPerRata) }));
    }
  }, [editVariantDraft.coverages, editVariantDraft.installments_count]);
  // 🆕 Stessa logica per la nuova variante manuale
  useEffect(() => {
    if (!newVariantCovs || newVariantCovs.length === 0) return;
    const sumCovs = newVariantCovs.reduce((s, c) => {
      const p = Number(c.premium);
      return s + (Number.isFinite(p) && p > 0 ? p : 0);
    }, 0);
    if (sumCovs <= 0) return;
    const raten = parseInt(newVariant.installments_count, 10);
    const rate = Number.isFinite(raten) && raten >= 1 ? raten : 1;
    const currentPerRata = Number(newVariant.amount) || 0;
    const currentTotal = Math.round(currentPerRata * rate * 100) / 100;
    if (sumCovs > currentTotal) {
      const newPerRata = Math.round((sumCovs / rate) * 100) / 100;
      setNewVariant((n) => ({ ...n, amount: String(newPerRata) }));
    }
  }, [newVariantCovs, newVariant.installments_count]);
  const saveEditVariant = async () => {
    const perRateAmt = parseFloat(editVariantDraft.amount);
    const rateN = (() => {
      const n = parseInt(editVariantDraft.installments_count, 10);
      return Number.isFinite(n) && n >= 1 ? n : 1;
    })();
    // 🆕 Se il piano rateale personalizzato è VALIDO (tutte le rate > 0 e non uniformi),
    // il totale della variante è la SOMMA delle rate, non perRate × rateN (che è ambiguo
    // perché il campo "Premio per rata" viene auto-riempito con la somma stessa).
    const _fraz = editVariantDraft.frazionamento;
    const _amts = Array.isArray(editVariantDraft.installments_amounts)
      ? editVariantDraft.installments_amounts.map((x) => Number(x) || 0)
      : [];
    const _customValid =
      _fraz && _fraz !== "annuale" && rateN >= 2 && _amts.length === rateN &&
      _amts.every((a) => a > 0) && !_amts.every((a) => Math.abs(a - _amts[0]) < 0.005);
    // 🆕 Validazione: se piano custom valido → NON serve perRateAmt > 0 (totale = somma rate).
    if (!editVariantDraft.company.trim() || (!_customValid && !(perRateAmt > 0))) {
      toast.error("Compila compagnia e importo per rata");
      return;
    }
    const totalAmt = _customValid
      ? Math.round(_amts.reduce((s, a) => s + a, 0) * 100) / 100
      : Math.round(perRateAmt * rateN * 100) / 100;
    try {
      const orig = variants.find((x) => x.id === editingVariantId);
      const cleanCovs = (editVariantDraft.coverages || [])
        .filter((c) => (c.name || "").trim())
        .map((c) => ({
          name: c.name,
          code: c.code || null,
          sum_insured: c.sum_insured === "" || c.sum_insured == null ? null : Number(c.sum_insured),
          premium: c.premium === "" || c.premium == null ? null : Number(c.premium),
          deductible: c.deductible || null,
          description: c.description || null,
          kind: c.kind || "coverage",
          client_visible: c.client_visible !== false,
          client_price_visible: c.client_price_visible !== false,
          // 🛠 Feb 2026 fix — preserva la compagnia dedicata per singola garanzia/voce accessoria.
          // Bug precedente: `company_id`/`company_name` venivano scartati in saveEditVariant, quindi la
          // selezione "Compagnia diversa dalla variante" spariva al salvataggio e la descrizione non
          // usciva nel PDF cliente.
          company_id: c.company_id || null,
          company_name: c.company_name || null,
        }));
      const brokerObj = editVariantDraft.broker_id
        ? (brokers || []).find((b) => b.id === editVariantDraft.broker_id)
        : null;
      const companyId = editVariantDraft.company_id
        || findCompanyIdByName(editVariantDraft.company, editVariantDraft.broker_id);
      // 🆕 Usa il nuovo endpoint PUT (preserva id/created_at/is_winner lato server)
      await api.put(`/quote-offers/${editing.id}/variants/${editingVariantId}`, {
        company: editVariantDraft.company.trim(),
        amount: totalAmt,
        coverages: cleanCovs,
        notes: editVariantDraft.notes || null,
        source_filename: orig?.source_filename || null,
        broker_id: brokerObj?.id || null,
        broker_name: brokerObj?.name || null,
        company_id: companyId || null,
        frazionamento: editVariantDraft.frazionamento || null,
        installments_count: rateN,
        effective_date: editVariantDraft.effective_date || null,
        expiry_date: editVariantDraft.expiry_date || null,
        driving_type: editVariantDraft.driving_type || null,
        // 🆕 Rate personalizzate: salva solo se il frazionamento è ≠ annuale, ci sono N importi validi
        // e TUTTI gli importi sono > 0 (piano completo). Altrimenti resta uniforme.
        installments_amounts: (() => {
          const fraz = editVariantDraft.frazionamento;
          if (!fraz || fraz === "annuale" || rateN < 2) return null;
          const amts = editVariantDraft.installments_amounts;
          if (!Array.isArray(amts) || amts.length !== rateN) return null;
          const clean = amts.map((a) => Number(a) || 0);
          // 🛠 Solo se TUTTE le rate sono > 0 il piano è considerato personalizzato/valido.
          const allFilled = clean.every((a) => a > 0);
          if (!allFilled) return null;
          // Se sono tutti uguali al Premio per rata, salva null (rate uniformi, default)
          const perR = Number(editVariantDraft.amount) || 0;
          const allEqual = clean.every((a) => Math.abs(a - perR) < 0.005);
          return allEqual ? null : clean;
        })(),
        product_id: editVariantDraft.product_id || null,
        product_buy_cost: canSeeMargins && editVariantDraft.product_buy_cost !== "" && editVariantDraft.product_buy_cost != null ? Number(editVariantDraft.product_buy_cost) : null,
        product_sell_cost: canSeeMargins && editVariantDraft.product_sell_cost !== "" && editVariantDraft.product_sell_cost != null ? Number(editVariantDraft.product_sell_cost) : null,
      });
      toast.success(`Variante aggiornata${cleanCovs.length ? ` con ${cleanCovs.length} garanzie` : ""}`);
      setEditingVariantId(null);
      await refreshVariantsFromBackend();
      // 🆕 Salva completamente l'offerta (come se l'utente avesse premuto "Conferma e salva")
      // così l'email/PDF inviato al cliente contiene gli ultimi dati persistiti.
      await persistOfferFromForm();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeVariant = async (variantId, company) => {
    if (!window.confirm(`Rimuovere la variante "${company}"?`)) return;
    try {
      await api.delete(`/quote-offers/${editing.id}/variants/${variantId}`);
      toast.success("Variante rimossa");
      await refreshVariantsFromBackend();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // 🆕 Template varianti (set riutilizzabili)
  const [variantTemplates, setVariantTemplates] = useState([]);
  const [templatesMenuOpen, setTemplatesMenuOpen] = useState(false);
  const [templatesSearch, setTemplatesSearch] = useState("");
  const loadVariantTemplates = async () => {
    try { const { data } = await api.get("/offer-variant-templates"); setVariantTemplates(data || []); }
    catch { /* silent */ }
  };
  useEffect(() => { if (open && editing) loadVariantTemplates(); }, [open, editing]);
  const saveVariantsAsTemplate = async () => {
    if (variants.length === 0) { toast.error("Aggiungi almeno una variante prima di salvare"); return; }
    const name = window.prompt("Nome template (es. 'Set RCA standard'):");
    if (!name || !name.trim()) return;
    try {
      await api.post("/offer-variant-templates", { name: name.trim(), variants });
      toast.success(`Template "${name}" salvato`);
      loadVariantTemplates();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const applyVariantTemplate = async (templateId, templateName) => {
    if (!editing?.id) return;
    try {
      const { data } = await api.post(`/quote-offers/${editing.id}/apply-template/${templateId}`);
      toast.success(`Template "${templateName}" applicato (${data.added} varianti)`);
      setTemplatesMenuOpen(false);
      await refreshVariantsFromBackend();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteVariantTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Eliminare il template "${templateName}"?`)) return;
    try {
      await api.delete(`/offer-variant-templates/${templateId}`);
      toast.success("Template eliminato");
      loadVariantTemplates();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const finalizeVariant = async (variantId, company) => {
    if (!window.confirm(`Finalizzare la variante "${company}"?\n\nL'offerta principale verrà aggiornata con i dati di questa variante (compagnia, premio, garanzie).\nPotrai cambiarla finché il cliente non paga.`)) return;
    try {
      await api.post(`/quote-offers/${editing.id}/variants/${variantId}/finalize`);
      toast.success(`Variante "${company}" finalizzata. Puoi ora inviarla al cliente.`);
      await refreshVariantsFromBackend();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // 🆕 Togli finalizzazione: riporta l'offerta a stato "in confronto" (winner=none) e ripristina
  //     l'importo alla variante più economica.
  const unfinalizeVariant = async () => {
    if (!editing?.id) return;
    if (!window.confirm("Togliere la finalizzazione?\n\nL'offerta tornerà in stato di confronto varianti e l'importo verrà ricalcolato sulla variante più economica. Le varianti restano tutte in memoria.")) return;
    try {
      await api.post(`/quote-offers/${editing.id}/unfinalize`);
      toast.success("Finalizzazione rimossa. Puoi scegliere nuovamente la variante.");
      // 🆕 Svuota l'Importo da pagare nel form (dopo unfinalize non c'è più valore ereditato)
      setForm((f) => ({ ...f, amount: "" }));
      await refreshVariantsFromBackend();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // 🆕 Aggiungi variante da estrazione AI di un PDF preventivo compagnia
  const [variantExtracting, setVariantExtracting] = useState(false);
  // 🆕 Allegati dell'offerta finale (PDF preventivi compagnia estratti + upload manuali)
  const [offerAttachments, setOfferAttachments] = useState([]);
  const [attUploadBusy, setAttUploadBusy] = useState(false);
  const [attachDesc, setAttachDesc] = useState("");
  // 🆕 10/07/2026 — Sezione allegati offerta: collassata di default (per compattare la UI)
  const [attSectionOpen, setAttSectionOpen] = useState(false);
  const loadOfferAttachments = useCallback(async () => {
    if (!editing?.id) { setOfferAttachments([]); return; }
    try {
      const { data } = await api.get(`/quote-offers/${editing.id}/attachments`);
      setOfferAttachments(Array.isArray(data) ? data : []);
    } catch { setOfferAttachments([]); }
  }, [editing?.id]);
  useEffect(() => { if (open && editing?.id) loadOfferAttachments(); else if (!open) setOfferAttachments([]); }, [open, editing?.id, loadOfferAttachments]);
  const uploadOfferAttachment = async (file) => {
    if (!file || !editing?.id) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File troppo grande (max 25 MB)"); return; }
    // 🆕 Non caricare subito: apri dialog di rinomina
    queueOfferAttachmentUploads([file]);
  };
  // 🆕 10/07/2026 — Upload rapido multi-file (drag & drop / scanner / camera / picker):
  // salta il dialog di rinomina e usa il nome originale + descrizione condivisa.
  const uploadOfferAttachmentsBatch = async (fileList) => {
    if (!editing?.id) return;
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setAttUploadBusy(true);
    let ok = 0;
    try {
      for (const f of files) {
        if (f.size > 25 * 1024 * 1024) { toast.error(`"${f.name}" troppo grande (max 25 MB)`); continue; }
        try {
          // 🆕 Feb 2026 — Upload con progress bar (byte reali)
          await uploadWithProgress({
            label: `Upload ${f.name}`,
            run: (cfg) => {
              const fd = new FormData();
              fd.append("file", f, f.name);
              fd.append("filename", f.name);
              if (attachDesc) fd.append("description", attachDesc);
              return api.post(`/quote-offers/${editing.id}/attachments`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: cfg.onUploadProgress,
              });
            },
          });
          ok++;
        } catch (err) {
          // Il toast d'errore è già stato mostrato da uploadWithProgress; log per debug
          console.warn(`Upload "${f.name}" fallito:`, err);
        }
      }
      if (ok > 0) {
        toast.success(`${ok} allegat${ok === 1 ? "o caricato" : "i caricati"} ✓`);
        setAttachDesc("");
        await loadOfferAttachments();
      }
    } finally {
      setAttUploadBusy(false);
    }
  };
  // 🆕 Rename dialog: prima di caricare, l'utente può rinominare i file allegati
  const [pendingOfferUpload, setPendingOfferUpload] = useState(null); // { files: [{file, newName}] } | null
  const queueOfferAttachmentUploads = (files) => {
    const arr = Array.from(files || []).filter(Boolean);
    if (arr.length === 0) return;
    const items = arr.map((f) => ({ file: f, newName: f.name }));
    setPendingOfferUpload({ files: items });
  };
  const confirmOfferAttachmentUpload = async () => {
    if (!pendingOfferUpload || !editing?.id) return;
    const { files } = pendingOfferUpload;
    setAttUploadBusy(true);
    let ok = 0;
    try {
      for (const it of files) {
        const finalName = (it.newName || it.file.name).trim() || it.file.name;
        const origExt = (it.file.name.split(".").pop() || "").toLowerCase();
        const hasExt = /\.[a-zA-Z0-9]{1,6}$/.test(finalName);
        const nameWithExt = hasExt ? finalName : (origExt ? `${finalName}.${origExt}` : finalName);
        const renamed = new File([it.file], nameWithExt, { type: it.file.type });
        try {
          // 🆕 Feb 2026 — Upload con progress bar (byte reali)
          await uploadWithProgress({
            label: `Upload ${nameWithExt}`,
            run: (cfg) => {
              const fd = new FormData();
              fd.append("file", renamed, nameWithExt);
              fd.append("filename", nameWithExt);
              return api.post(`/quote-offers/${editing.id}/attachments`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: cfg.onUploadProgress,
              });
            },
          });
          ok++;
        } catch (err) {
          // Il toast d'errore è già stato mostrato da uploadWithProgress; log per debug
          console.warn(`Upload "${nameWithExt}" fallito:`, err);
        }
      }
      if (ok > 0) {
        toast.success(`${ok} allegat${ok === 1 ? "o caricato" : "i caricati"} ✓`);
        await loadOfferAttachments();
      }
    } finally {
      setAttUploadBusy(false);
      setPendingOfferUpload(null);
    }
  };
  const deleteOfferAttachment = async (attId) => {
    if (!editing?.id) return;
    if (!window.confirm("Eliminare questo allegato?")) return;
    try {
      await api.delete(`/quote-offers/${editing.id}/attachments/${attId}`);
      toast.success("Allegato eliminato");
      await loadOfferAttachments();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // 🆕 Rinomina un allegato offerta già caricato
  const renameOfferAttachment = async (att) => {
    if (!editing?.id) return;
    const proposed = window.prompt("Nuovo nome file (estensione preservata):", att.filename || "");
    if (proposed === null) return; // annullato
    const newName = proposed.trim();
    if (!newName || newName === att.filename) return;
    try {
      const { data } = await api.patch(`/quote-offers/${editing.id}/attachments/${att.id}`, { filename: newName });
      toast.success(`Rinominato in "${data.filename}"`);
      await loadOfferAttachments();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadOfferAttachment = async (att) => {
    if (!editing?.id) return;
    // 🆕 Feb 2026 — Download con progress bar (helper globale)
    await downloadWithProgress({
      label: att.filename || "allegato",
      blobPath: `/quote-offers/${editing.id}/attachments/${att.id}/download`,
      filename: att.filename,
    }).catch(() => { /* toast già mostrato dall'helper */ });
  };
  // 🆕 Anteprima inline dell'allegato (PDF/immagine) in un dialog — senza forzare il download
  const [previewAtt, setPreviewAtt] = useState(null); // { url, att }
  const previewOfferAttachment = async (att) => {
    if (!editing?.id) return;
    try {
      const response = await api.get(`/quote-offers/${editing.id}/attachments/${att.id}/download`, { responseType: "blob" });
      // 🛠 Forziamo il MIME type sul Blob perché alcuni proxy/browser lo perdono e l'<iframe>
      // scarica il file invece di renderizzarlo inline. Il content_type "vero" arriva dal doc DB.
      const mime = att.content_type || response.headers?.["content-type"] || "application/pdf";
      const typed = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(typed);
      setPreviewAtt({ url, att, mime });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const closePreview = () => {
    if (previewAtt?.url) {
      try { window.URL.revokeObjectURL(previewAtt.url); } catch { /* ignore */ }
    }
    setPreviewAtt(null);
  };
  const addVariantFromFile = async (file) => {
    if (!file) return;
    if (!editing?.id) { toast.error("Salva prima l'offerta per aggiungere varianti"); return; }
    setVariantExtracting(true);
    try {
      // 🆕 Feb 2026 — Progress bar per estrazione AI (0→90% simulato, 100% al termine)
      const ex = await runExtractionWithProgress({
        label: `Estrazione variante · ${file.name}`,
        run: () => {
          const fd = new FormData();
          fd.append("file", file, file.name);
          return api.post("/utils/extract-pdf?kind=polizza", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
        },
      });
      const extracted = ex?.extracted || {};
      const company = (extracted.company || extracted.compagnia || extracted.compagnia_assicurativa || "").toString().trim();
      const amount = Number(extracted.premium || extracted.gross || extracted.totale_lordo || extracted.amount || 0);
      if (!company && !amount) {
        toast.error("Estrazione AI non ha individuato compagnia/premio. Inserisci manualmente.");
        return;
      }
      const coverages = Array.isArray(extracted.coverages)
        ? extracted.coverages.map((c) => ({
          name: c.name || c.nome || "",
          sum_insured: c.sum_insured || c.massimale || "",
          premium: c.premium || c.premio || "",
          deductible: c.deductible || c.franchigia || "",
          description: c.description || c.note || "",
          kind: "coverage",
          client_visible: true,
          client_price_visible: false,
        })).filter((c) => c.name) : [];
      await api.post(`/quote-offers/${editing.id}/variants`, {
        company: company || "Compagnia (da PDF)",
        amount: amount > 0 ? amount : 1,
        coverages: _sanitizeCoverages(coverages),
        notes: extracted.notes || null,
        source_filename: file.name,
      });
      // 🆕 Allega il file estratto anche come attachment dell'offerta (visibile in tab "Allegati") con upload progress
      let attachOk = true;
      try {
        await uploadWithProgress({
          label: `Upload ${file.name}`,
          run: (cfg) => {
            const fd2 = new FormData();
            fd2.append("file", file, file.name);
            fd2.append("filename", file.name);
            fd2.append("description", `Preventivo compagnia · ${company || "estratto AI"}`);
            return api.post(`/quote-offers/${editing.id}/attachments`, fd2, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: cfg.onUploadProgress,
            });
          },
        });
        await loadOfferAttachments();
      } catch (attErr) {
        // 🆕 28/07/2026 — Non nascondere l'errore: mostra il messaggio reale.
        attachOk = false;
        console.warn("Upload allegato offerta fallito:", attErr);
      }
      if (attachOk) {
        toast.success(`Variante "${company}" creata da PDF (premio ${formatCurrency(amount)})`);
      }
      await refreshVariantsFromBackend();
    } catch (_e) {
      // toast d'errore già gestito da runExtractionWithProgress / uploadWithProgress
    } finally {
      setVariantExtracting(false);
    }
  };

  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
  // 🆕 Prompt "Vuoi rinviare al cliente l'offerta aggiornata?" via dialog UI (sostituisce window.confirm)
  const [resendPrompt, setResendPrompt] = useState(null); // { offerId, offerNumber }
  const [resending, setResending] = useState(false);
  const [confirmAlsoSend, setConfirmAlsoSend] = useState(false);

  const TYPE_LBL = { auto: "Auto", moto: "Moto", casa: "Casa", vita: "Vita", salute: "Salute", infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "RC" };
  // Mappa quote_id → { plate, type } (per mostrare targa + icona veicolo nell'elenco offerte)
  const quotePlateMap = useMemo(() => {
    const m = {};
    for (const q of quotes) {
      const plate = (q.details && (q.details.vehicle_plate || q.details.targa)) || "";
      if (plate) m[q.id] = {
        plate: String(plate).toUpperCase(), type: q.type || "auto",
        brand: (q.details.vehicle_brand || q.details.marca || "").toString().trim(),
        model: (q.details.vehicle_model || q.details.modello || "").toString().trim(),
      };
    }
    return m;
  }, [quotes]);
  // Riquadro veicolo (marca+modello in evidenza + targa) — stesso stile del campo "Preventivo collegato"
  const renderVehicleChip = (o, testid) => {
    const qp = quotePlateMap[o.quote_id] || {};
    const plate = (o.vehicle_plate || qp.plate || "").toString().toUpperCase();
    const brandModel = [o.vehicle_brand || qp.brand, o.vehicle_model || qp.model].filter(Boolean).join(" ");
    if (!plate && !brandModel) return null;
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-sky-50 px-1.5 py-0.5 align-middle" data-testid={testid} title="Veicolo del preventivo">
        <span className="text-[12px] leading-none">{vehicleIcon(o.quote_type || qp.type)}</span>
        {brandModel && <span className="text-[13px] font-semibold text-sky-900">{brandModel}</span>}
        {plate && <span className="font-mono text-[12px] font-bold tracking-wider text-slate-900 bg-white border border-slate-400 rounded px-1.5">{plate}</span>}
      </span>
    );
  };
  // Icona veicolo in base alla tipologia preventivo
  const vehicleIcon = (type) => {
    if (type === "moto") return "🏍️";
    if (type === "auto") return "🚗";
    return "🚙"; // fallback per altri veicoli (es. autocarro, camper)
  };
  // Filtro testuale: matcha targa, numero offerta, nome/email cliente, numero preventivo
  // Ordine logico per stato (priorità: cose da fare in cima, completate/cestino in fondo)
  const STATUS_ORDER = {
    draft: 0,         // Bozza → da inviare
    inviata: 1,       // Inviata → in attesa cliente
    accettata: 2,     // Accettata e firmata → in attesa pagamento
    pagata: 3,        // Completata
    annullata: 4,     // Chiusa
    eliminata: 5,     // Cestino
  };
  // 🆕 Toggle per mostrare/nascondere le offerte già pagate (default: nascoste).
  // Le offerte pagate sono comunque visibili nella scheda del preventivo collegato.
  const [showPaid, setShowPaid] = useState(false);
  // 🆕 Feb 2026 — Ordinamento tabella Offerte Finali (click su intestazione colonna).
  // Chiavi supportate: created_at, offer_number, client_name, quote_number, amount, end_date, status.
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const raw = localStorage.getItem("quote_offers_sort");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { key: "created_at", dir: "desc" };
  });
  useEffect(() => {
    try { localStorage.setItem("quote_offers_sort", JSON.stringify(sortConfig)); } catch { /* ignore */ }
  }, [sortConfig]);
  const requestSort = (key) => {
    // 🆕 Quando l'utente clicca su un'intestazione ordinabile, disattiva il raggruppamento
    // per cliente (altrimenti il rendering group-by sovrascriverebbe l'ordinamento e la
    // tabella sembrerebbe "bloccata"). L'utente può ri-attivarlo manualmente.
    setGroupByClient(false);
    setSortConfig((cur) => {
      if (cur.key === key) return { key, dir: cur.dir === "asc" ? "desc" : "asc" };
      // default direction per column type
      const numericKeys = ["amount", "created_at", "end_date"];
      return { key, dir: numericKeys.includes(key) ? "desc" : "asc" };
    });
  };
  const getSortValue = (o, key) => {
    switch (key) {
      case "offer_number":
      case "quote_number":
      case "client_name":
      case "status":
        return String(o[key] || "").toLowerCase();
      case "amount": {
        const winner = (o.variants || []).find((v) => v.id === o.winner_variant_id) || (o.variants || [])[0];
        return Number((winner || o).amount || 0);
      }
      case "created_at":
      case "end_date":
        return new Date(o[key] || 0).getTime();
      default:
        return "";
    }
  };

  const filteredOffers = useMemo(() => {
    const q = (searchText || "").trim().toLowerCase();
    const base = !q ? offers : offers.filter((o) => {
      const plate = ((o.vehicle_plate || quotePlateMap[o.quote_id]?.plate) || "").toLowerCase();
      return (
        plate.includes(q)
        || (o.offer_number || "").toLowerCase().includes(q)
        || (o.client_name || "").toLowerCase().includes(q)
        || (o.client_email || "").toLowerCase().includes(q)
        || (o.quote_number || "").toLowerCase().includes(q)
      );
    });
    // 🆕 Nascondi le offerte pagate dalla lista principale (saranno visibili nel preventivo collegato).
    // In vista cestino il filtro non si applica.
    const afterPaidFilter = (view === "trash" || showPaid)
      ? base
      : base.filter((o) => o.status !== "pagata");
    // 🆕 16/07/2026 — Filtro "Solo offerte generate da polizza": mostra solo offerte con source_policy_id valorizzato
    const afterSourceFilter = showOnlyFromPolicy
      ? afterPaidFilter.filter((o) => !!o.source_policy_id)
      : afterPaidFilter;
    // Ordinamento dinamico secondo sortConfig
    const sign = sortConfig.dir === "asc" ? 1 : -1;
    return [...afterSourceFilter].sort((a, b) => {
      const va = getSortValue(a, sortConfig.key);
      const vb = getSortValue(b, sortConfig.key);
      if (va < vb) return -1 * sign;
      if (va > vb) return 1 * sign;
      return 0;
    });
  }, [offers, searchText, quotePlateMap, view, showPaid, showOnlyFromPolicy, sortConfig]);

  // === Selezione multipla offerte (bulk delete → cestino) ===
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkCancelReason, setBulkCancelReason] = useState("");
  // Reset selezione al cambio vista (active/trash) o al reload
  useEffect(() => { setSelectedIds(new Set()); }, [view]);

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  // ID effettivamente eliminabili nella vista attiva (esclude già nel cestino) — filtrati dal search
  const selectableOffers = filteredOffers.filter((o) => view === "trash" ? true : o.status !== "eliminata");
  const allSelected = selectableOffers.length > 0 && selectableOffers.every((o) => selectedIds.has(o.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectableOffers.map((o) => o.id)));
  };

  const doBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    const isTrashView = view === "trash";
    for (const id of selectedIds) {
      try {
        if (isTrashView) {
          await api.delete(`/quote-offers/${id}/permanent`);
        } else {
          await api.delete(`/quote-offers/${id}`);
        }
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkBusy(false);
    setBulkConfirmOpen(false);
    setSelectedIds(new Set());
    if (ok > 0) {
      toast.success(
        isTrashView
          ? `${ok} offert${ok === 1 ? "a eliminata" : "e eliminate"} definitivamente${fail ? ` · ${fail} errori` : ""}`
          : `${ok} offert${ok === 1 ? "a spostata" : "e spostate"} nel cestino${fail ? ` · ${fail} errori` : ""}`,
      );
    }
    if (ok === 0 && fail > 0) toast.error(`Errore durante l'eliminazione (${fail} falliti)`);
    load();
  };

  // 🆕 Helper: salva l'offerta principale dal form corrente (PUT + sync garanzie sul preventivo
  // + refresh anteprima). NON apre il prompt di "rinvia al cliente" né trigger l'invio.
  // Usato dopo modifiche a una variante per assicurare che l'offerta sia completamente persistita.
  const persistOfferFromForm = async () => {
    if (!editing?.id) return;
    const payload = {
      quote_id: form.quote_id,
      amount: Number(form.amount) || 0,
      coverages: (form.coverages || [])
        .filter((c) => (c.name || "").trim())
        .map((c) => ({
          name: c.name,
          code: c._auto_code || c.code || null,
          sum_insured: c.sum_insured === "" || c.sum_insured == null ? null : Number(c.sum_insured),
          premium: c.premium === "" || c.premium == null ? null : Number(c.premium),
          deductible: c.deductible || null,
          description: c.description || null,
          kind: c.kind || "coverage",
          client_visible: c.client_visible !== false,
          client_price_visible: c.client_price_visible !== false,
        })),
      selected_coverages: Array.isArray(form.selected_coverages) ? form.selected_coverages : [],
      frazionamento: form.frazionamento || "annuale",
      driving_type: form.driving_type || null,
      notes: form.notes || null,
      valid_until: form.valid_until || null,
    };
    try {
      await api.put(`/quote-offers/${editing.id}`, payload);
      setEditingFresh(false); // 🆕 salvataggio avvenuto: non è più un'offerta "fresh"
      setPreviewVersion((v) => v + 1);
      // Sync garanzie sul preventivo collegato (best-effort)
      if (form.quote_id) {
        try {
          await api.patch(`/quotes/${form.quote_id}/selected-coverages`, {
            selected_coverages: payload.selected_coverages,
          });
        } catch { /* non bloccante */ }
      }
      // 🆕 Refresh contatori globali (stats) e riga in tabella dopo autosave
      try {
        const [{ data: allOffers }, { data: freshStats }] = await Promise.all([
          api.get(`/quote-offers${view === "trash" ? "?trash=true" : ""}`),
          api.get("/quote-offers/stats"),
        ]);
        if (allOffers) setOffers(allOffers);
        if (freshStats) setStats(freshStats);
      } catch { /* non bloccante */ }
    } catch (e) {
      toast.warning(`Salvataggio completo offerta fallito: ${formatApiError(e)}`);
    }
  };

  const submit = async () => {
    if (!form.quote_id) { toast.error("Seleziona un preventivo"); return; }
    // 🆕 L'Importo è opzionale: se non c'è variante finalizzata, il campo può restare vuoto
    // (offerta interlocutoria). Blocchiamo solo se il valore è < 0.
    if (form.amount !== "" && form.amount != null && Number(form.amount) < 0) {
      toast.error("Importo non valido (deve essere ≥ 0)");
      return;
    }
    // L'operatore sceglie esplicitamente se inviare: di default NON spunato.
    setConfirmAlsoSend(false);
    setConfirmCreateOpen(true);
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      const payload = {
        quote_id: form.quote_id,
        amount: Number(form.amount),
        coverages: form.coverages
          .filter((c) => c.name?.trim())
          .map((c) => ({
            name: c.name,
            code: c._auto_code || null,
            sum_insured: c.sum_insured ? Number(c.sum_insured) : null,
            premium: c.premium ? Number(c.premium) : null,
            deductible: c.deductible || null,
            description: c.description || null,
            kind: c.kind || "coverage",
          })),
        selected_coverages: Array.isArray(form.selected_coverages) ? form.selected_coverages : [],
        frazionamento: form.frazionamento || "annuale",
        driving_type: form.driving_type || null,
        notes: form.notes || null,
        valid_until: form.valid_until || null,
      };
      let createdId = editing ? editing.id : null;
      const wasSent = editing && (editing.status === "inviata" || !!editing.sent_at);
      if (editing) {
        await api.put(`/quote-offers/${editing.id}`, payload);
        toast.success("Offerta aggiornata");
        setPreviewVersion((v) => v + 1); // 🆕 forza refresh anteprima cliente
      } else {
        const { data } = await api.post("/quote-offers", payload);
        createdId = data?.id;
        toast.success("Offerta creata");
        setPreviewVersion((v) => v + 1);
      }
      // 🆕 Sincronizza le garanzie selezionate sul preventivo collegato (esplicito, con toast su errore)
      if (form.quote_id) {
        try {
          await api.patch(`/quotes/${form.quote_id}/selected-coverages`, {
            selected_coverages: payload.selected_coverages,
          });
        } catch (e) {
          // Non-blocking: avvisa l'utente ma non interrompe il flusso
          toast.warning(`Offerta salvata. Sync garanzie sul preventivo non riuscita: ${formatApiError(e)}`);
        }
      }
      // 🆕 Se ho modificato un'offerta GIÀ INVIATA, apri il dialog per chiedere all'operatore se rinviarla
      if (editing && wasSent) {
        setResendPrompt({ offerId: createdId, offerNumber: editing.offer_number });
      }
      // Invio automatico al cliente se richiesto (solo se offerta non già inviata)
      const canSend = confirmAlsoSend && createdId && (!editing || editing.status === "draft");
      if (canSend) {
        try {
          await api.post(`/quote-offers/${createdId}/send`);
          toast.success("Email + notifica inviate al cliente");
        } catch (e) {
          toast.error(`Offerta salvata ma invio fallito: ${formatApiError(e)}`);
        }
      }
      setConfirmCreateOpen(false);
      setEditingFresh(false); // 🆕 salvataggio esplicito riuscito: non più "fresh"
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendOffer = (o) => {
    // Pre-spunta WhatsApp se il cliente ha un numero
    setSendOpen({ offer: o, alsoWhatsapp: !!o.client_mobile, requestSignature: true, includeCoverages: true });
  };

  const resendOffer = (o) => {
    if (!confirm(`Reinviare l'offerta ${o.offer_number} a ${o.client_email || "cliente"}?\n\nIl cliente riceverà nuovamente l'email con il PDF allegato (verrà segnato come "rinvio").`)) return;
    setSendOpen({ offer: o, alsoWhatsapp: !!o.client_mobile, requestSignature: true, includeCoverages: true, isResend: true });
  };

  const confirmSend = async () => {
    if (!sendOpen) return;
    const o = sendOpen.offer;
    // 🆕 Se l'utente non ha disabilitato l'anteprima, aprila prima di inviare
    if (!shouldSkipEmailPreview("offer_to_client")) {
      setEmailPreviewCtx({
        typeKey: "offer_to_client",
        title: `Anteprima offerta ${o.offer_number} → ${o.client_name || o.client_email}`,
        fetchPreview: async () => {
          const inc = sendOpen.includeCoverages !== false;
          const { data } = await api.post(`/quote-offers/${o.id}/send?include_coverages=${inc}&dry_run=true`);
          return data;
        },
        onConfirm: async () => { await actuallySendOffer(); },
      });
      return;
    }
    await actuallySendOffer();
  };

  const actuallySendOffer = async () => {
    if (!sendOpen) return;
    const o = sendOpen.offer;
    try {
      const inc = sendOpen.includeCoverages !== false;
      await api.post(`/quote-offers/${o.id}/send?include_coverages=${inc}`);
      toast.success(inc ? "Email inviata al cliente (con coperture)" : "Email inviata al cliente (solo importo)");
      // 🆕 Crea richiesta firma OTP se l'operatore l'ha richiesta
      if (sendOpen.requestSignature && o.client_id) {
        try {
          const { data: sig } = await api.post("/signatures/request", {
            doc_type: "quote_offer",
            doc_id: o.id,
            client_id: o.client_id,
          });
          const signUrl = `${window.location.origin}/sign/${sig.id}`;
          toast.success(`✍️ Richiesta firma OTP creata · link: ${signUrl}`, { duration: 9000 });
          // Copia link nella clipboard per facilitare la condivisione
          try { await navigator.clipboard.writeText(signUrl); toast.info("Link firma copiato negli appunti"); } catch { /* noop */ }
        } catch (errSig) {
          toast.error("Errore creazione richiesta firma: " + formatApiError(errSig));
        }
      }
      if (sendOpen.alsoWhatsapp) {
        const digits = (o.client_mobile || "").replace(/[^\d]/g, "");
        if (!digits) {
          toast.warning("Cliente senza numero: WhatsApp non aperto");
        } else {
          const intl = digits.startsWith("00") ? digits.slice(2)
            : (digits.startsWith("39") ? digits
            : (digits.startsWith("3") || digits.startsWith("0") ? `39${digits.replace(/^0/, "")}` : digits));
          const url = `${window.location.origin}/offerta/${o.share_token}`;
          const msg = `Ciao ${(o.client_name || "").split(" ")[0] || ""},\nti ho appena inviato per email l'offerta finale ${o.offer_number} per la tua polizza (importo da pagare: ${formatCurrency(o.amount)}).\n\nPer pagare e procedere all'emissione: ${url}\n\nGrazie!`;
          window.open(`whatsapp://send?phone=${intl}&text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
        }
      }
      setSendOpen(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelOffer = async (o) => {
    const reason = prompt("Motivo annullamento (opzionale):");
    if (reason === null) return;
    try {
      await api.post(`/quote-offers/${o.id}/cancel`, { reason });
      toast.success("Offerta annullata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remindOffer = async (o) => {
    if (!confirm(`Inviare un promemoria al cliente ${o.client_name || ""} per l'offerta ${o.offer_number}?`)) return;
    try {
      const { data } = await api.post(`/quote-offers/${o.id}/remind`);
      toast.success(`Promemoria inviato a ${data.to}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicateOffer = async (o) => {
    if (!confirm(`Duplicare l'offerta ${o.offer_number}? Verrà creata una nuova bozza modificabile con gli stessi dati.`)) return;
    try {
      const { data } = await api.post(`/quote-offers/${o.id}/duplicate`);
      toast.success(`Creata bozza ${data.offer_number} duplicata da ${o.offer_number}`);
      await load();
      // Apri immediatamente in modalità modifica per personalizzare la copia
      openEdit(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteOffer = async (o) => {
    const isPaid = o.status === "pagata";
    const msg = isPaid
      ? `⚠️ ATTENZIONE: l'offerta ${o.offer_number} risulta PAGATA (${formatCurrency(o.amount)}).\n\nSpostarla nel cestino non rimborsa il pagamento e non cancella le ricevute. Procedere?`
      : `Spostare l'offerta ${o.offer_number} nel cestino?`;
    if (!confirm(msg)) return;
    try {
      await api.delete(`/quote-offers/${o.id}`);
      toast.success("Offerta spostata nel cestino");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restoreOffer = async (o) => {
    try {
      await api.post(`/quote-offers/${o.id}/restore`);
      toast.success("Offerta ripristinata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const permanentDelete = async (o) => {
    if (!confirm(`Eliminare DEFINITIVAMENTE ${o.offer_number}? Non sarà più recuperabile dal cestino.`)) return;
    try {
      await api.delete(`/quote-offers/${o.id}/permanent`);
      toast.success("Offerta eliminata definitivamente");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markPaid = async () => {
    if (!markPaidOpen) return;
    try {
      const { data } = await api.post(`/quote-offers/${markPaidOpen.id}/mark-paid`, { payment_method: paidMethod });
      const policyId = data?.policy_id || null;
      const offerNumber = markPaidOpen.offer_number;
      const clientName = markPaidOpen.client_name;
      setMarkPaidOpen(null);
      load();
      if (policyId) {
        setPolicyCreatedInfo({ policyId, offerNumber, clientName });
      } else {
        toast.success("Offerta segnata come pagata");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Forza pagamento: marca subito l'offerta come pagata, crea la polizza e mostra il dialog di conferma.
  const confirmForcePaid = async () => {
    if (!forcePaidOpen) return;
    setForcePaidBusy(true);
    try {
      const { data } = await api.post(`/quote-offers/${forcePaidOpen.id}/mark-paid`, {
        payment_method: forcePaidMethod,
        notify_client: forcePaidNotifyClient,  // 🆕 Feb 2026
      });
      const policyId = data?.policy_id || null;
      const offerNumber = forcePaidOpen.offer_number;
      const clientName = forcePaidOpen.client_name;
      setForcePaidOpen(null);
      setForcePaidNotifyClient(false);  // 🆕 reset opt-in per la prossima apertura
      load();
      if (policyId) {
        setPolicyCreatedInfo({ policyId, offerNumber, clientName });
      } else {
        toast.success(`Offerta ${offerNumber} forzata a "pagata"`);
        toast.warning("Polizza non ancora collegata: verifica la sezione Polizze.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setForcePaidBusy(false);
    }
  };

  // 🆕 Conferma "Offerta accettata": flagga accettata+pagata, crea/recupera polizza e apre il dialog di conferma.
  const confirmOfferAccepted = async () => {
    if (!acceptedOpen) return;
    if (!acceptedFlags.accepted || !acceptedFlags.paid) {
      toast.error("Conferma entrambi i flag (Accettata + Pagata) per procedere");
      return;
    }
    try {
      // Se l'offerta è già pagata, non rifare il mark-paid; recupera comunque la polizza collegata.
      let policyId = acceptedOpen.linked_policy_id || null;
      if (acceptedOpen.status !== "pagata") {
        const { data } = await api.post(`/quote-offers/${acceptedOpen.id}/mark-paid`, { payment_method: paidMethod });
        policyId = data?.policy_id || policyId;
      }
      const offerNumber = acceptedOpen.offer_number;
      const clientName = acceptedOpen.client_name;
      setAcceptedOpen(null);
      load();
      if (policyId) {
        setPolicyCreatedInfo({ policyId, offerNumber, clientName });
      } else {
        toast.warning("Polizza non ancora collegata: verifica la sezione Polizze.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyLink = (o) => {
    const url = `${window.location.origin}/offerta/${o.share_token}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Link copiato"),
      () => toast.error("Errore copia"),
    );
  };

  const updateCov = (idx, field, val) => {
    const copy = [...form.coverages];
    const prev = copy[idx] || {};
    copy[idx] = { ...prev, [field]: val };
    // 🆕 Auto-somma: se stiamo MODIFICANDO un'offerta esistente (editing) con importo già presente,
    // e la riga è stata aggiunta in questa sessione (_added_in_session=true),
    // e stiamo cambiando il "premium" → somma la differenza al form.amount e avvisa.
    if (
      field === "premium"
      && editing
      && !winnerVariantId
      && prev._added_in_session === true
    ) {
      const currentAmount = Number(form.amount) || 0;
      const oldPremium = Number(prev.premium) || 0;
      const newPremium = Number(val) || 0;
      const delta = +(newPremium - oldPremium).toFixed(2);
      if (currentAmount > 0 && delta !== 0) {
        const newTotal = +(currentAmount + delta).toFixed(2);
        setForm({ ...form, coverages: copy, amount: newTotal });
        const rowLabel = (prev.name || val) ? (prev.name || `Garanzia`) : "Garanzia";
        toast.info(
          delta > 0
            ? `➕ Aggiunto ${formatCurrency(delta)} all'importo dell'offerta (${rowLabel}). Nuovo totale: ${formatCurrency(newTotal)}`
            : `➖ Sottratto ${formatCurrency(Math.abs(delta))} dall'importo dell'offerta (${rowLabel}). Nuovo totale: ${formatCurrency(newTotal)}`,
          { duration: 4500 }
        );
        return;
      }
    }
    setForm({ ...form, coverages: copy });
  };
  const addCov = () => setForm({ ...form, coverages: [...form.coverages, { ...emptyCov, _added_in_session: true }] });
  const removeCov = (idx) => {
    const row = form.coverages[idx];
    setForm((f) => {
      const newCoverages = f.coverages.filter((_, i) => i !== idx);
      // 🆕 Se la riga aveva un codice (auto-aggiunta da CoveragePicker), togli anche
      // la spunta nelle "Garanzie richieste dal cliente" e propaga il sync.
      let newSelected = f.selected_coverages || [];
      if (row?._auto_code) {
        newSelected = newSelected.filter((c) => c !== row._auto_code);
        // Sync best-effort sul preventivo collegato
        if (f.quote_id) {
          api.patch(`/quotes/${f.quote_id}/selected-coverages`, { selected_coverages: newSelected }).catch(() => { /* silent */ });
        }
      }
      return { ...f, coverages: newCoverages, selected_coverages: newSelected };
    });
  };
  // 🆕 Riordinamento riga ↑/↓ in "Dettaglio coperture"
  const moveCov = (idx, dir) => {
    const copy = [...form.coverages];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= copy.length) return;
    [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
    setForm({ ...form, coverages: copy });
  };
  // Default visibilità per nuove righe (toggle in alto: "Nascondi al cliente le nuove righe")
  const [newRowsHiddenByDefault, setNewRowsHiddenByDefault] = useState(false);
  const addCovWithVisibility = () => setForm({ ...form, coverages: [...form.coverages, { ...emptyCov, client_visible: !newRowsHiddenByDefault, _added_in_session: true }] });

  const quoteOptions = useMemo(() => {
    // Ordina cronologicamente (più recenti prima)
    const sorted = [...quotes].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dbb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dbb - da;
    });
    return sorted.map((q) => {
      const cl = clientsMap[q.client_id];
      const fullName = cl
        ? `${cl.surname || ""} ${cl.name || ""}`.trim() || cl.email || "Cliente"
        : (q.client_name || "Cliente sconosciuto");
      let dateStr = "";
      if (q.created_at) {
        try {
          const d = new Date(q.created_at);
          const dd = String(d.getDate()).padStart(2, "0");
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const yyyy = d.getFullYear();
          const hh = String(d.getHours()).padStart(2, "0");
          const mn = String(d.getMinutes()).padStart(2, "0");
          dateStr = `${dd}/${mm}/${yyyy} ${hh}:${mn}`;
        } catch { /* noop */ }
      }
      const typeLbl = TYPE_LBL[q.type] || q.type || "—";
      const num = q.quote_number || q.id?.slice(0, 6) || "—";
      const plate = (q.details && (q.details.vehicle_plate || q.details.targa) || "").toString().toUpperCase();
      const vIcon = vehicleIcon(q.type);
      const brand = (q.details?.vehicle_brand || q.details?.marca || "").toString().trim();
      const model = (q.details?.vehicle_model || q.details?.modello || "").toString().trim();
      return {
        v: q.id,
        l: `📅 ${dateStr} · 👤 ${fullName} · ${typeLbl} · #${num}${plate ? ` · ${vIcon} ${plate}` : ""}`,
        base: `📅 ${dateStr} · 👤 ${fullName} · ${typeLbl} · #${num}`,
        brand, model, plate, vIcon,
      };
    });
  }, [quotes, clientsMap]);

  return (
    <div data-testid="quote-offers-page" className="space-y-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">Preventivi</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <Euro className="h-7 w-7 text-emerald-600" /> Offerte finali
          </h1>
          <p className="text-sm text-slate-500 mt-1">Crea offerte con importo definitivo da inviare al cliente per il pagamento e l&apos;emissione polizza.</p>
        </div>
        <Button data-testid="new-offer-btn" onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="h-4 w-4 mr-2" /> Nuova offerta finale
        </Button>
        <Button data-testid="export-offers-btn" variant="outline" onClick={() => setExportOpen(true)}>
          <Download className="h-4 w-4 mr-2" /> Esporta
        </Button>
        {isAdmin && (
          <Button
            data-testid="cleanup-orphan-refs-btn"
            variant="outline"
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            onClick={async () => {
              if (!window.confirm("Esegui sweep dei riferimenti polizza orfani?\n\nVerranno azzerati tutti i linked_policy_id che puntano a polizze cancellate. Operazione sicura e idempotente.")) return;
              const t = toast.loading("Pulizia in corso…");
              try {
                const { data } = await api.post("/admin/cleanup-orphan-policy-refs");
                toast.dismiss(t);
                toast.success(`Sweep completato: ${data.cleaned} riferimenti orfani azzerati (su ${data.scanned} scansionate, ${data.valid} valide)`);
                load();
              } catch (e) {
                toast.dismiss(t);
                toast.error(`Errore: ${formatApiError(e)}`);
              }
            }}
            title="ADMIN: scansiona le offerte e nullifica i linked_policy_id che puntano a polizze cancellate"
          >
            🧹 Pulisci orfani
          </Button>
        )}
        {isAdmin && (
          <Button
            data-testid="fix-installment-batch-btn"
            variant="outline"
            className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
            onClick={async () => {
              const tDry = toast.loading("Scansione offerte con importi dimezzati…");
              try {
                const { data: dry } = await api.post("/admin/offers/fix-installment-amount-batch?dry_run=true");
                toast.dismiss(tDry);
                if (!dry.total_details) {
                  toast.success("Tutte le offerte hanno già importi coerenti ✓");
                  return;
                }
                const sample = (dry.details || []).slice(0, 5)
                  .map((o) => `  • ${o.offer_number} → ${formatCurrency(o.offer_amount_new)}`)
                  .join("\n");
                const extra = dry.total_details > 5 ? `\n  …e altre ${dry.total_details - 5}` : "";
                const uv = dry.updated_variants || 0;
                const uo = dry.updated_offer_amount || 0;
                if (!window.confirm(
                  `Trovate ${dry.total_details} offerte da correggere:\n` +
                  `  • ${uv} con variant.amount dimezzato\n` +
                  `  • ${uo} con offer.amount disallineato dal winner\n\n` +
                  `Esempi:\n${sample}${extra}\n\nProcedo con il fix?`
                )) return;
                const tRun = toast.loading("Applico il fix…");
                const { data } = await api.post("/admin/offers/fix-installment-amount-batch");
                toast.dismiss(tRun);
                toast.success(
                  `Fix completato: ${data.updated_variants} variants corrette, ${data.updated_offer_amount} offer.amount riallineati ` +
                  `(${data.inspected} ispezionate, ${data.skipped} già OK)`
                );
                load();
              } catch (e) {
                toast.dismiss(tDry);
                toast.error(`Errore: ${formatApiError(e)}`);
              }
            }}
            title="ADMIN: ricalcola variant.amount = Σgaranzie × rate per le offerte partner salvate con amount dimezzato (bug pre-Feb 2026). Idempotente."
          >
            🧮 Fix importi dimezzati
          </Button>
        )}
      </header>

      {/* Dashboard statistiche offerte (visibile solo nel tab Attive) */}
      {view === "active" && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="offers-stats">
          <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="stat-total">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <FileText className="h-3.5 w-3.5" /> Totali
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{stats.total}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
              {stats.by_status.inviata} inviate · {stats.by_status.pagata} pagate
            </div>
          </div>
          <div className="bg-white border border-emerald-200 rounded-lg p-3" data-testid="stat-conversion">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
              <TrendingUp className="h-3.5 w-3.5" /> Conversione
            </div>
            <div className="text-2xl font-bold text-emerald-700 mt-1 tabular-nums">{stats.conversion_rate}%</div>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
              Pagate vs chiuse
            </div>
          </div>
          <div className="bg-white border border-blue-200 rounded-lg p-3" data-testid="stat-paid-amount">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-blue-700 font-semibold">
              <Banknote className="h-3.5 w-3.5" /> Incassato
            </div>
            <div className="text-2xl font-bold text-blue-700 mt-1 tabular-nums">{formatCurrency(stats.paid_amount_total)}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
              In attesa: {formatCurrency(stats.open_amount_total)}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="stat-avg-days">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <Clock className="h-3.5 w-3.5" /> Tempo medio
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
              {stats.avg_payment_days != null ? `${stats.avg_payment_days}g` : "—"}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
              dall&apos;invio al pagamento
            </div>
          </div>
          <div className={`bg-white border rounded-lg p-3 ${stats.expiring_soon_count > 0 ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`} data-testid="stat-expiring-soon">
            <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold ${stats.expiring_soon_count > 0 ? "text-amber-700" : "text-slate-500"}`}>
              <AlertTriangle className="h-3.5 w-3.5" /> Scadenza &lt; 48h
            </div>
            <div className={`text-2xl font-bold mt-1 tabular-nums ${stats.expiring_soon_count > 0 ? "text-amber-700" : "text-slate-900"}`}>
              {stats.expiring_soon_count}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
              offerte da sollecitare
            </div>
          </div>
        </div>
      )}

      {/* Grafico trend mensile ultimi 6 mesi (collassabile) */}
      {view === "active" && stats && Array.isArray(stats.monthly_trend) && stats.monthly_trend.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="offers-trend-chart">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-slate-600" />
            <h3 className="font-semibold text-sm text-slate-800">Andamento offerte · ultimi 6 mesi</h3>
            <button
              type="button"
              data-testid="toggle-trend-chart"
              onClick={() => {
                setShowTrendChart((v) => {
                  const next = !v;
                  try { localStorage.setItem("offers_trend_visible", next ? "1" : "0"); } catch { /* noop */ }
                  return next;
                });
              }}
              className="ml-auto text-[11px] px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1"
              title={showTrendChart ? "Nascondi grafico" : "Mostra grafico"}
            >
              {showTrendChart ? "🙈 Nascondi" : "👁 Mostra"}
            </button>
            {showTrendChart && <span className="text-[11px] text-slate-500">Create vs. pagate</span>}
          </div>
          {showTrendChart && (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.monthly_trend} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }}
                  formatter={(value, name) => {
                    if (name === "Incassato") return [formatCurrency(value), name];
                    return [value, name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="created" name="Create" fill="#64748B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="paid" name="Pagate" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>
      )}

      {/* Tab switch Attive / Cestino */}
      <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-0.5 items-center flex-wrap">
        <button
          data-testid="tab-active"
          onClick={() => setView("active")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "active" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
        >
          Attive
        </button>
        <button
          data-testid="tab-trash"
          onClick={() => setView("trash")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${view === "trash" ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
        >
          <Trash className="h-3.5 w-3.5" /> Cestino
          {Number(stats?.trash_count || 0) > 0 && (
            <span
              data-testid="offers-trash-count"
              className="ml-1 inline-flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px]"
            >
              {stats.trash_count > 99 ? "99+" : stats.trash_count}
            </span>
          )}
        </button>
        {/* 🆕 Toggle: mostra/nascondi offerte già pagate (default: nascoste) */}
        {view === "active" && (
          <label
            className="ml-2 inline-flex items-center gap-2 px-3 py-1 rounded-md cursor-pointer text-xs font-medium text-slate-600 hover:bg-slate-200"
            data-testid="toggle-show-paid"
            title="Le offerte pagate sono visibili nella scheda del preventivo collegato"
          >
            <input
              type="checkbox"
              checked={showPaid}
              onChange={(e) => setShowPaid(e.target.checked)}
              className="h-3.5 w-3.5 accent-emerald-600"
            />
            Mostra offerte pagate
          </label>
        )}
        {/* 🆕 16/07/2026 — Filtro "Solo offerte generate da polizze" */}
        {view === "active" && (
          <label
            className="ml-2 inline-flex items-center gap-2 px-3 py-1 rounded-md cursor-pointer text-xs font-medium text-slate-600 hover:bg-slate-200"
            data-testid="toggle-only-from-policy"
            title="Mostra solo le offerte generate da una polizza esistente (via 'Crea Offerta da Polizza')"
          >
            <input
              type="checkbox"
              checked={showOnlyFromPolicy}
              onChange={(e) => setShowOnlyFromPolicy(e.target.checked)}
              className="h-3.5 w-3.5 accent-purple-600"
            />
            📄 Solo da polizze
          </label>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Input
            type="text"
            data-testid="offers-search-input"
            placeholder="🔎 Cerca targa, cliente, n° offerta…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="h-8 w-64 text-sm"
          />
          {searchText && (
            <button
              data-testid="offers-search-clear"
              onClick={() => setSearchText("")}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
              title="Pulisci ricerca"
            >
              pulisci
            </button>
          )}
          <label className="text-xs text-slate-700 cursor-pointer select-none inline-flex items-center gap-1.5" title="Raggruppa per cliente con la più conveniente in cima">
            <input
              type="checkbox"
              data-testid="group-by-client-toggle"
              checked={groupByClient}
              onChange={(e) => setGroupByClient(e.target.checked)}
              className="h-3.5 w-3.5 accent-emerald-600"
            />
            🏆 Raggruppa per cliente · migliore in cima
          </label>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Barra bulk: appare quando ci sono offerte selezionate */}
        {selectedIds.size > 0 && (
          <div
            data-testid="offers-bulk-bar"
            className="flex items-center justify-between gap-3 px-4 py-2.5 bg-rose-50 border-b border-rose-200 text-sm"
          >
            <div className="text-rose-800">
              <strong data-testid="offers-bulk-count">{selectedIds.size}</strong> offert{selectedIds.size === 1 ? "a" : "e"} selezionat{selectedIds.size === 1 ? "a" : "e"}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                data-testid="offers-bulk-clear"
                onClick={() => setSelectedIds(new Set())}
                disabled={bulkBusy}
              >
                <XIcon className="h-4 w-4 mr-1.5" /> Annulla
              </Button>
              <Button
                size="sm"
                data-testid="offers-bulk-delete-btn"
                onClick={() => setBulkConfirmOpen(true)}
                disabled={bulkBusy}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                {view === "trash" ? "Elimina definitivamente" : "Sposta nel cestino"}
              </Button>
            </div>
          </div>
        )}
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="offers-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-3 text-left w-10">
                <input
                  type="checkbox"
                  data-testid="offers-select-all"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={selectableOffers.length === 0}
                  className="h-4 w-4 cursor-pointer accent-rose-600"
                  title={allSelected ? "Deseleziona tutte" : "Seleziona tutte"}
                />
              </th>
              <SortableTh label="N°" sortKey="offer_number" sortConfig={sortConfig} onSort={requestSort} align="left" disabled={groupByClient} />
              <SortableTh label="Cliente" sortKey="client_name" sortConfig={sortConfig} onSort={requestSort} align="left" disabled={groupByClient} />
              <SortableTh label="Preventivo" sortKey="quote_number" sortConfig={sortConfig} onSort={requestSort} align="left" disabled={groupByClient} />
              <SortableTh label="Importo" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} align="right" disabled={groupByClient} />
              <SortableTh label="Scadenza" sortKey="end_date" sortConfig={sortConfig} onSort={requestSort} align="left" disabled={groupByClient} />
              <SortableTh label="Stato" sortKey="status" sortConfig={sortConfig} onSort={requestSort} align="left" disabled={groupByClient} />
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Caricamento…</td></tr>
            ) : filteredOffers.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                {offers.length === 0 ? "Nessuna offerta. Crea la prima." : `Nessuna offerta corrisponde a "${searchText}".`}
              </td></tr>
            ) : (() => {
              // Costruisci la lista da renderizzare: se groupByClient, raggruppa e ordina per amount ASC per cliente
              let renderList;
              if (groupByClient) {
                const byClient = {};
                for (const o of filteredOffers) {
                  const k = o.client_id || o.client_email || o.id;
                  if (!byClient[k]) byClient[k] = [];
                  byClient[k].push(o);
                }
                renderList = [];
                // Ordina i gruppi per nome cliente
                const groups = Object.entries(byClient).sort((a, b) => {
                  const na = (a[1][0].client_name || "").toLowerCase();
                  const nb = (b[1][0].client_name || "").toLowerCase();
                  return na.localeCompare(nb);
                });
                groups.forEach(([, arr]) => {
                  arr.sort((a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0));
                  arr.forEach((o, i) => renderList.push({ ...o, _isBest: i === 0 && arr.length > 1, _isGroupStart: i === 0, _groupSize: arr.length }));
                });
              } else {
                renderList = filteredOffers;
              }
              return renderList.map((o) => {
              const st = STATUS_LABEL[o.status] || STATUS_LABEL.draft;
              const _isFinalizedRow = !!o.winner_variant_id;
              const rowClass = _isFinalizedRow
                ? "border-t-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100/70"
                : o._isBest
                  ? "border-t-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-amber-50 hover:bg-emerald-50/80"
                  : "border-t border-slate-100 hover:bg-slate-50/50";
              return (
                <React.Fragment key={o.id}>
                  {groupByClient && o._isGroupStart && o._groupSize > 1 && (
                    <tr className="bg-slate-100/80 border-t-2 border-slate-300">
                      <td colSpan={8} className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-700">
                        👤 <span className="uppercase">{o.client_name || o.client_email || "Cliente"}</span> · {o._groupSize} offerte
                      </td>
                    </tr>
                  )}
                <tr data-testid={`offer-row-${o.id}`} className={`${rowClass} ${selectedIds.has(o.id) ? "bg-rose-50/50" : ""} align-top`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      data-testid={`offer-select-${o.id}`}
                      checked={selectedIds.has(o.id)}
                      onChange={() => toggleSelect(o.id)}
                      className="h-4 w-4 cursor-pointer accent-rose-600"
                      aria-label={`Seleziona offerta ${o.offer_number}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono font-medium">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{o.offer_number}</span>
                      {Array.isArray(o.variants) && o.variants.length > 0 && (
                        <span
                          data-testid={`offer-variants-count-${o.id}`}
                          className="text-[10px] font-sans font-semibold uppercase tracking-tight bg-indigo-100 text-indigo-700 border border-indigo-300 rounded-full px-1.5 py-0.5"
                          title={`Contiene ${o.variants.length} variant${o.variants.length === 1 ? "e" : "i"} compagnia${o.winner_variant_id ? " · 1 finalizzata" : ""}`}
                        >
                          🏆 {o.variants.length} variant{o.variants.length === 1 ? "e" : "i"}
                          {o.winner_variant_id && <span className="ml-1 text-emerald-700">✓</span>}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className="font-medium cursor-pointer hover:text-blue-700 select-none uppercase"
                      onDoubleClick={() => openEdit(o)}
                      data-testid={`offer-client-name-${o.id}`}
                      title="Doppio click per modificare l'offerta"
                    >
                      {o.client_name || "—"}
                    </div>
                    <div className="text-xs text-slate-500">{o.client_email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className="font-mono cursor-pointer hover:text-blue-700 hover:underline select-none"
                      onDoubleClick={() => o.quote_id && navigate(`/quotes?focus=${o.quote_id}`)}
                      data-testid={`offer-quote-number-${o.id}`}
                      title="Doppio click per aprire il preventivo collegato"
                    >
                      {o.quote_number || "—"}
                    </span>
                    <span className="text-slate-400 ml-1">{o.quote_type || ""}</span>
                    {renderVehicleChip(o, `offer-vehicle-chip-${o.id}`) && (
                      <span className="ml-1.5">{renderVehicleChip(o, `offer-vehicle-chip-${o.id}`)}</span>
                    )}
                    {o.quote_id && (
                      <button
                        type="button"
                        onClick={() => navigate(`/quotes?focus=${o.quote_id}`)}
                        data-testid={`open-linked-quote-${o.id}`}
                        title="Apri il preventivo collegato"
                        className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      >
                        <FileText className="h-3 w-3" /> Apri preventivo
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums whitespace-nowrap">
                    {o._isBest && (
                      <span className="inline-block mr-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-600 text-white align-middle" title="Più conveniente per questo cliente">🏆 MIGLIORE</span>
                    )}
                    {(() => {
                      // 🆕 Se la variante finalizzata (o la più economica) ha frazionamento ≠ annuale e installments_count > 1,
                      // mostra "€ per_rata × N rate" invece del solo totale.
                      const _vars = Array.isArray(o.variants) ? o.variants : [];
                      // 🆕 16/07/2026 — Se non c'è variante finalizzata e o.amount non è valido,
                      // mostra come importo il PIÙ BASSO tra le varianti (indicato con badge "min").
                      const _amtFinal = parseFloat(o.amount);
                      const _hasFinal = Number.isFinite(_amtFinal) && _amtFinal > 0;
                      const _hasWinner = !!o.winner_variant_id;
                      // 🆕 16/07/2026 — "Finalizzato" = variante vincente scelta esplicitamente
                      const _isFinalized = _hasFinal && _hasWinner;
                      const _sortedByAmt = _vars.slice().sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
                      const _win = _vars.find((vv) => vv.id === o.winner_variant_id) || _sortedByAmt[0];
                      const _cheapest = _sortedByAmt.find((vv) => Number(vv.amount || 0) > 0);
                      const _displayAmount = _hasFinal ? _amtFinal : (Number(_cheapest?.amount) || 0);
                      const _isMinFromVariant = !_hasFinal && _displayAmount > 0;
                      const _raten = _win ? parseInt(_win.installments_count, 10) : NaN;
                      const _fraz = (_win?.frazionamento || o.frazionamento || "").toLowerCase();
                      const _tot = _displayAmount;
                      if (_tot <= 0) {
                        return <span data-testid={`offer-amount-list-${o.id}`} className="text-slate-400">—</span>;
                      }
                      const minBadge = _isMinFromVariant ? (
                        <span
                          className="ml-1 inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-300 align-middle"
                          title={`Importo minimo tra le ${_vars.length} variant${_vars.length === 1 ? "e" : "i"} (offerta non ancora finalizzata)`}
                          data-testid={`offer-amount-min-badge-${o.id}`}
                        >min</span>
                      ) : null;
                      // 🆕 16/07/2026 — Icona ✅ per importo finalizzato (variante vincente scelta)
                      const finalizedIcon = _isFinalized ? (
                        <CheckCircle2
                          className="inline-block h-3.5 w-3.5 text-emerald-600 ml-1 align-middle"
                          title="Importo finalizzato · variante vincente scelta"
                          data-testid={`offer-amount-finalized-icon-${o.id}`}
                        />
                      ) : null;
                      if (_fraz && _fraz !== "annuale" && Number.isFinite(_raten) && _raten > 1 && Number.isFinite(_tot) && _tot > 0) {
                        const _per = Math.round((_tot / _raten) * 100) / 100;
                        return (
                          <span title={`${formatCurrency(_per)} per rata × ${_raten} rate = ${formatCurrency(_tot)} totale${_isMinFromVariant ? " (min variante)" : (_isFinalized ? " (finalizzato)" : "")}`} data-testid={`offer-amount-list-${o.id}`}>
                            {formatCurrency(_per)} <span className="text-[10px] font-normal text-slate-500">× {_raten}</span>
                            {finalizedIcon}
                            {minBadge}
                          </span>
                        );
                      }
                      return (
                        <span data-testid={`offer-amount-list-${o.id}`}>
                          {formatCurrency(_tot)}
                          {finalizedIcon}
                          {minBadge}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap tabular-nums">{formatDate(o.valid_until)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${st.cls}`}>{st.label}</span>
                    {/* 🆕 Feb 2026 — Badge FIRMATO se l'offerta è stata firmata elettronicamente dal cliente */}
                    {o.signature_status === "signed" && (
                      <span
                        className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300"
                        title={o.signed_at ? `Firmata elettronicamente il ${formatDateTime(o.signed_at)}` : "Firmata elettronicamente"}
                        data-testid={`offer-signed-badge-${o.id}`}
                      >
                        ✓ FIRMATA
                      </span>
                    )}
                    {o.signature_status === "signed" && o.signed_at && (
                      <span
                        className="ml-1.5 text-[10px] text-emerald-700 font-medium tabular-nums"
                        data-testid={`offer-signed-at-${o.id}`}
                      >
                        ✍️ {formatDateTime(o.signed_at)}
                      </span>
                    )}
                    {o.status === "inviata" && Number(o.resend_count || 0) > 0 && (
                      <span
                        className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800 border border-amber-300"
                        title={`Reinviata ${o.resend_count} volt${Number(o.resend_count) === 1 ? "a" : "e"}${o.last_sent_at ? ` · ultimo invio: ${formatDateTime(o.last_sent_at)}` : ""}`}
                        data-testid={`offer-resend-badge-${o.id}`}
                      >
                        🔁 ×{o.resend_count}
                      </span>
                    )}
                    {o.status === "inviata" && o.sent_at && (
                      <span
                        className="ml-1.5 text-[10px] text-blue-700 font-medium tabular-nums"
                        title={`${Number(o.resend_count || 0) > 0 ? "Ultimo invio" : "Inviata al cliente"} il ${formatDateTime(o.sent_at)}${o.first_sent_at && o.first_sent_at !== o.sent_at ? ` (1° invio: ${formatDateTime(o.first_sent_at)})` : ""}`}
                        data-testid={`offer-sent-at-${o.id}`}
                      >
                        📤 {formatDateTime(o.sent_at)}
                      </span>
                    )}
                    {o.payment_method && <span className="text-[10px] text-slate-500 block mt-1">via {o.payment_method}</span>}
                    {/* 🆕 Banner CTA su offerta pagata: ricorda di allegare copia polizza per estrarre i dati */}
                    {o.status === "pagata" && (
                      <div
                        data-testid={`offer-complete-policy-cta-${o.id}`}
                        className="mt-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900"
                      >
                        <div className="flex items-start gap-1 mb-1">
                          <span>📋</span>
                          <span><strong>Completa la polizza</strong>: allega la copia per estrarre i dati o compila a mano.</span>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <button
                            data-testid={`offer-attach-policy-${o.id}`}
                            onClick={async () => {
                              const t = toast.loading("Preparo la polizza…");
                              try {
                                const { data } = await api.post(`/quote-offers/${o.id}/ensure-policy`);
                                toast.dismiss(t);
                                if (data.created) toast.success("Polizza draft creata, apro per estrazione AI…");
                                navigate(`/policies?focus=${data.policy_id}&return_to=${encodeURIComponent("/quotes/offers")}&action=extract`);
                              } catch (err) {
                                toast.dismiss(t);
                                toast.error(`Impossibile aprire la polizza: ${formatApiError(err)}`);
                              }
                            }}
                            className="px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold inline-flex items-center gap-1"
                            title="Crea la polizza (se non esiste) e aprila in modifica con il file-picker pronto per estrarre i dati"
                          >
                            📎 Allega & estrai
                          </button>
                          <button
                            data-testid={`offer-fill-manual-${o.id}`}
                            onClick={async () => {
                              const t = toast.loading("Preparo la polizza…");
                              try {
                                const { data } = await api.post(`/quote-offers/${o.id}/ensure-policy`);
                                toast.dismiss(t);
                                if (data.created) toast.success("Polizza draft creata, apro per compilazione…");
                                navigate(`/policies?focus=${data.policy_id}&return_to=${encodeURIComponent("/quotes/offers")}`);
                              } catch (err) {
                                toast.dismiss(t);
                                toast.error(`Impossibile aprire la polizza: ${formatApiError(err)}`);
                              }
                            }}
                            className="px-2 py-0.5 rounded bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold inline-flex items-center gap-1"
                            title="Crea la polizza (se non esiste) e aprila in modifica per compilare i dati manualmente"
                          >
                            ✍️ A mano
                          </button>
                        </div>
                      </div>
                    )}
                    {view === "trash" && o.deleted_at && (
                      <div className="text-[10px] text-slate-500 mt-1 leading-tight">
                        <div>🗑️ {formatDateTime(o.deleted_at)}</div>
                        {o.deleted_by_name && <div className="text-slate-400">da <strong>{o.deleted_by_name}</strong></div>}
                      </div>
                    )}
                    {view !== "trash" && o.status === "annullata" && o.cancelled_by_name && (
                      <div className="text-[10px] text-slate-500 mt-1 leading-tight">
                        ⊘ da <strong>{o.cancelled_by_name}</strong>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1 items-center flex-wrap justify-end">
                      {/* 🆕 MODIFICA: bottone primario con etichetta, prima di tutti gli altri */}
                      {(o.status === "draft" || o.status === "inviata") && (
                        <button
                          data-testid={`edit-offer-${o.id}`}
                          onClick={() => openEdit(o)}
                          className="order-1 px-2.5 py-1 rounded-md border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold inline-flex items-center gap-1.5"
                          title="Modifica offerta"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Modifica
                        </button>
                      )}
                      {/* 🆕 Bottone "Offerta accettata" — visibile per inviata/accettata/pagata,
                          arancione tenue+disabilitato se non pagata, verde+cliccabile se pagata. */}
                      {(o.status === "inviata" || o.status === "accettata" || o.status === "pagata") && (
                        (() => {
                          const isPaid = o.status === "pagata";
                          const refTs = o.sent_at || o.created_at;
                          let daysWaiting = 0;
                          if (!isPaid && refTs) {
                            const ms = Date.now() - new Date(refTs).getTime();
                            if (Number.isFinite(ms) && ms > 0) daysWaiting = Math.floor(ms / 86400000);
                          }
                          const isStale = !isPaid && daysWaiting > 7;
                          return (
                            <span className="order-3 basis-full inline-flex items-center gap-1.5 justify-end mt-0.5">
                              {!isPaid && (
                                <button
                                  type="button"
                                  onClick={() => { setMarkPaidOpen(o); setPaidMethod("bonifico"); }}
                                  className={
                                    isStale
                                      ? "px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-300 text-[10px] font-semibold uppercase tracking-wide hover:bg-rose-200 transition-colors animate-pulse"
                                      : "px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold uppercase tracking-wide hover:bg-amber-100 hover:border-amber-300 transition-colors"
                                  }
                                  data-testid={`awaiting-payment-${o.id}`}
                                  title={isStale
                                    ? `In attesa di pagamento da ${daysWaiting} giorni — clicca per registrare il pagamento`
                                    : "Clicca per registrare il pagamento"}
                                >
                                  {isStale ? `⚠️ Ferma da ${daysWaiting}gg` : "⏳ Attendo pagamento"}
                                </button>
                              )}
                              {isPaid && (
                                <button
                                  data-testid={`accept-offer-${o.id}`}
                                  onClick={() => { setAcceptedOpen(o); setAcceptedFlags({ accepted: false, paid: true }); setPaidMethod(o.payment_method || "bonifico"); }}
                                  className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold inline-flex items-center gap-1"
                                  title="Conferma accettazione e apri scheda polizza"
                                >
                                  ✅ Offerta accettata
                                </button>
                              )}
                            </span>
                          );
                        })()
                      )}
                      {/* 🆕 16/07/2026 — Menu ⋮ per azioni secondarie */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`offer-actions-menu-${o.id}`}
                            className="order-2 p-1.5 rounded hover:bg-slate-100 border border-slate-200 text-slate-600"
                            title="Altre azioni"
                            aria-label="Altre azioni"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56" data-testid={`offer-actions-menu-content-${o.id}`}>
                          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                            Azioni offerta
                          </DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => setViewOpen(o)} data-testid={`menu-view-offer-${o.id}`}>
                            <Eye className="mr-2 h-4 w-4 text-slate-600" /> Visualizza riepilogo
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quote-offers/${o.id}/print-pdf`, "_blank", "noopener,noreferrer")}
                            data-testid={`menu-print-offer-${o.id}`}
                          >
                            <Printer className="mr-2 h-4 w-4 text-slate-600" /> Stampa / Scarica PDF
                          </DropdownMenuItem>
                          {view !== "trash" && (
                            <DropdownMenuItem onClick={() => duplicateOffer(o)} data-testid={`menu-duplicate-offer-${o.id}`}>
                              <Files className="mr-2 h-4 w-4 text-indigo-600" /> Duplica come nuova bozza
                            </DropdownMenuItem>
                          )}
                          {o.status === "draft" && (
                            <DropdownMenuItem onClick={() => sendOffer(o)} data-testid={`menu-send-offer-${o.id}`}>
                              <Send className="mr-2 h-4 w-4 text-blue-600" /> Invia al cliente
                            </DropdownMenuItem>
                          )}
                          {(o.status === "inviata" || o.status === "accettata") && (
                            <>
                              <DropdownMenuItem onClick={() => copyLink(o)} data-testid={`menu-copy-link-${o.id}`}>
                                <Copy className="mr-2 h-4 w-4 text-slate-600" /> Copia link pagamento
                              </DropdownMenuItem>
                              {o.status === "inviata" && (
                                <DropdownMenuItem onClick={() => resendOffer(o)} data-testid={`menu-resend-offer-${o.id}`}>
                                  <Send className="mr-2 h-4 w-4 text-blue-700" /> Reinvia (con PDF)
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => remindOffer(o)} data-testid={`menu-remind-${o.id}`}>
                                <Bell className="mr-2 h-4 w-4 text-amber-700" /> Sollecita pagamento
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setMarkPaidOpen(o); setPaidMethod("bonifico"); }} data-testid={`menu-mark-paid-${o.id}`}>
                                <Euro className="mr-2 h-4 w-4 text-green-700" /> Segna come pagata
                              </DropdownMenuItem>
                            </>
                          )}
                          {view !== "trash" && o.status !== "pagata" && o.status !== "annullata" && o.status !== "eliminata" && (
                            <DropdownMenuItem
                              onClick={() => { setForcePaidOpen(o); setForcePaidMethod("bonifico"); }}
                              data-testid={`menu-force-paid-${o.id}`}
                            >
                              <Zap className="mr-2 h-4 w-4 text-fuchsia-700" /> Forza pagamento
                            </DropdownMenuItem>
                          )}
                          {o.quote_id && o.status !== "eliminata" && (
                            <DropdownMenuItem
                              data-testid={`menu-new-offer-from-row-${o.id}`}
                              onClick={async () => {
                                try {
                                  const t = toast.loading("Creo una nuova offerta per lo stesso preventivo…");
                                  const initialAmount = Math.max(1, Number(o.amount) || 1);
                                  const { data } = await api.post("/quote-offers", { quote_id: o.quote_id, amount: initialAmount, coverages: [] });
                                  const srcVariants = Array.isArray(o.variants) ? o.variants : [];
                                  if (srcVariants.length > 0) {
                                    for (const v of srcVariants) {
                                      try {
                                        await api.post(`/quote-offers/${data.id}/variants`, {
                                          company: v.company,
                                          amount: Number(v.amount) || 0,
                                          coverages: v.coverages || [],
                                          notes: v.notes || null,
                                          source_filename: v.source_filename || null,
                                        });
                                      } catch { /* skip variante errata */ }
                                    }
                                  }
                                  toast.dismiss(t);
                                  toast.success(srcVariants.length > 0
                                    ? `Offerta ${data.offer_number} creata con ${srcVariants.length} varianti clonate`
                                    : `Offerta ${data.offer_number} creata`);
                                  await load();
                                  const fresh = (await api.get("/quote-offers")).data?.find((x) => x.id === data.id);
                                  if (fresh) openEdit(fresh, true);
                                } catch (e) { toast.error(formatApiError(e)); }
                              }}
                            >
                              <Plus className="mr-2 h-4 w-4 text-emerald-700" /> Nuova offerta stesso preventivo
                            </DropdownMenuItem>
                          )}
                          {(o.status !== "pagata" && o.status !== "annullata" && o.status !== "eliminata") && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => cancelOffer(o)}
                                data-testid={`menu-cancel-offer-${o.id}`}
                                className="text-rose-600 focus:text-rose-700"
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Annulla
                              </DropdownMenuItem>
                            </>
                          )}
                          {(o.status === "annullata" || o.status === "pagata") && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => deleteOffer(o)}
                                data-testid={`menu-delete-offer-${o.id}`}
                                className={o.status === "pagata" ? "text-amber-700 focus:text-amber-800" : "text-rose-700 focus:text-rose-800"}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> {o.status === "pagata" ? "Sposta nel cestino (pagata)" : "Sposta nel cestino"}
                              </DropdownMenuItem>
                            </>
                          )}
                          {o.status === "eliminata" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => restoreOffer(o)} data-testid={`menu-restore-offer-${o.id}`} className="text-emerald-700 focus:text-emerald-800">
                                <RotateCcw className="mr-2 h-4 w-4" /> Ripristina
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => permanentDelete(o)} data-testid={`menu-permanent-delete-${o.id}`} className="text-rose-700 focus:text-rose-800">
                                <Trash className="mr-2 h-4 w-4" /> Elimina definitivamente
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              );
              });
            })()}
          </tbody>
        </table>
        </div>

        {/* 🆕 16/07/2026 — Vista mobile a card (<768 px) */}
        <div className="md:hidden divide-y divide-slate-200" data-testid="offers-mobile-list">
          {loading ? (
            <div className="px-4 py-8 text-center text-slate-400">Caricamento…</div>
          ) : filteredOffers.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-400 text-sm">
              {offers.length === 0 ? "Nessuna offerta. Crea la prima." : `Nessuna offerta corrisponde a "${searchText}".`}
            </div>
          ) : (() => {
            // Ricostruisco la stessa renderList del desktop (grouping)
            let renderList;
            if (groupByClient) {
              const byClient = {};
              for (const o of filteredOffers) {
                const k = o.client_id || o.client_email || o.id;
                if (!byClient[k]) byClient[k] = [];
                byClient[k].push(o);
              }
              renderList = [];
              const groups = Object.entries(byClient).sort((a, b) => (a[1][0].client_name || "").toLowerCase().localeCompare((b[1][0].client_name || "").toLowerCase()));
              groups.forEach(([, arr]) => {
                arr.sort((a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0));
                arr.forEach((o, i) => renderList.push({ ...o, _isBest: i === 0 && arr.length > 1, _isGroupStart: i === 0, _groupSize: arr.length }));
              });
            } else {
              renderList = filteredOffers;
            }
            return renderList.map((o) => {
              const st = STATUS_LABEL[o.status] || STATUS_LABEL.draft;
              const _vars = Array.isArray(o.variants) ? o.variants : [];
              const _amtFinal = parseFloat(o.amount);
              const _hasFinal = Number.isFinite(_amtFinal) && _amtFinal > 0;
              const _hasWinner = !!o.winner_variant_id;
              const _isFinalized = _hasFinal && _hasWinner;
              const _sortedByAmt = _vars.slice().sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
              const _cheapest = _sortedByAmt.find((vv) => Number(vv.amount || 0) > 0);
              const _displayAmount = _hasFinal ? _amtFinal : (Number(_cheapest?.amount) || 0);
              const _isMinFromVariant = !_hasFinal && _displayAmount > 0;
              return (
                <React.Fragment key={`m-${o.id}`}>
                  {groupByClient && o._isGroupStart && o._groupSize > 1 && (
                    <div className="bg-slate-100/80 border-t-2 border-slate-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-700" data-testid={`mobile-group-${o.client_id || o.client_email}`}>
                      👤 <span className="uppercase">{o.client_name || o.client_email || "Cliente"}</span> · {o._groupSize} offerte
                    </div>
                  )}
                  <div
                    className={`px-3 py-3 space-y-2 ${_isFinalized ? "bg-emerald-50 border-l-4 border-emerald-400" : o._isBest ? "bg-gradient-to-r from-emerald-50 to-amber-50 border-l-4 border-emerald-400" : "bg-white"} ${selectedIds.has(o.id) ? "bg-rose-50/50" : ""}`}
                    data-testid={`offer-card-${o.id}`}
                  >
                    {/* Riga 1: N° + status + menu */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono font-semibold text-sm">{o.offer_number}</span>
                          {o._isBest && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-600 text-white" title="Più conveniente per questo cliente">🏆 MIGLIORE</span>
                          )}
                          {_vars.length > 0 && (
                            <span className="text-[10px] font-semibold uppercase tracking-tight bg-indigo-100 text-indigo-700 border border-indigo-300 rounded-full px-1.5 py-0.5">
                              🏆 {_vars.length} variant{_vars.length === 1 ? "e" : "i"}
                              {o.winner_variant_id && <span className="ml-1 text-emerald-700">✓</span>}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded border ${st.cls}`}>{st.label}</span>
                          {o.status === "inviata" && o.sent_at && (
                            <span className="text-[10px] text-blue-700 font-medium tabular-nums">📤 {formatDateTime(o.sent_at)}</span>
                          )}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(o.id)}
                        onChange={() => toggleSelect(o.id)}
                        className="h-4 w-4 mt-1 cursor-pointer accent-rose-600"
                        aria-label={`Seleziona offerta ${o.offer_number}`}
                      />
                    </div>

                    {/* Riga 2: Cliente */}
                    <div>
                      <div
                        className="font-medium text-sm uppercase cursor-pointer hover:text-blue-700"
                        onDoubleClick={() => openEdit(o)}
                        data-testid={`offer-card-client-name-${o.id}`}
                      >
                        {o.client_name || "—"}
                      </div>
                      {o.client_email && <div className="text-[11px] text-slate-500 truncate">{o.client_email}</div>}
                    </div>

                    {/* Riga 3: Preventivo + Targa */}
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="font-mono text-slate-600">{o.quote_number || "—"}</span>
                      <span className="text-slate-400">{o.quote_type || ""}</span>
                      {renderVehicleChip(o, `offer-card-vehicle-chip-${o.id}`)}
                    </div>

                    {/* Riga 4: Importo + Scadenza */}
                    <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
                      <div className="text-lg font-semibold tabular-nums">
                        {_displayAmount > 0 ? formatCurrency(_displayAmount) : <span className="text-slate-400">—</span>}
                        {_isFinalized && <CheckCircle2 className="inline-block h-4 w-4 text-emerald-600 ml-1 align-middle" title="Finalizzato" />}
                        {_isMinFromVariant && (
                          <span className="ml-1 inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-300 align-middle" title="Importo minimo tra le varianti">min</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 tabular-nums">
                        scad. <span className="font-medium text-slate-700">{formatDate(o.valid_until)}</span>
                      </div>
                    </div>

                    {/* Riga 5: azioni */}
                    <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                      {(o.status === "draft" || o.status === "inviata") && (
                        <button
                          onClick={() => openEdit(o)}
                          className="flex-1 min-w-[90px] px-2 py-1 rounded-md border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                          data-testid={`offer-card-edit-${o.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Modifica
                        </button>
                      )}
                      {(o.status === "inviata" || o.status === "accettata" || o.status === "pagata") && (() => {
                        const isPaid = o.status === "pagata";
                        return (
                          <>
                            {!isPaid && (
                              <button
                                onClick={() => { setMarkPaidOpen(o); setPaidMethod("bonifico"); }}
                                className="flex-1 min-w-[90px] px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold uppercase tracking-wide hover:bg-amber-100"
                                data-testid={`offer-card-awaiting-${o.id}`}
                              >
                                ⏳ Attendo pagamento
                              </button>
                            )}
                            {isPaid && (
                              <button
                                onClick={() => { setAcceptedOpen(o); setAcceptedFlags({ accepted: false, paid: true }); setPaidMethod(o.payment_method || "bonifico"); }}
                                className="flex-1 min-w-[90px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1"
                                data-testid={`offer-card-accept-${o.id}`}
                              >
                                ✅ Offerta accettata
                              </button>
                            )}
                          </>
                        );
                      })()}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-100"
                            aria-label="Altre azioni"
                            data-testid={`offer-card-menu-${o.id}`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">Azioni offerta</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => setViewOpen(o)}>
                            <Eye className="mr-2 h-4 w-4 text-slate-600" /> Visualizza riepilogo
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quote-offers/${o.id}/print-pdf`, "_blank", "noopener,noreferrer")}>
                            <Printer className="mr-2 h-4 w-4 text-slate-600" /> Stampa / Scarica PDF
                          </DropdownMenuItem>
                          {view !== "trash" && (
                            <DropdownMenuItem onClick={() => duplicateOffer(o)}>
                              <Files className="mr-2 h-4 w-4 text-indigo-600" /> Duplica come bozza
                            </DropdownMenuItem>
                          )}
                          {o.status === "draft" && (
                            <DropdownMenuItem onClick={() => sendOffer(o)}>
                              <Send className="mr-2 h-4 w-4 text-blue-600" /> Invia al cliente
                            </DropdownMenuItem>
                          )}
                          {(o.status === "inviata" || o.status === "accettata") && (
                            <>
                              <DropdownMenuItem onClick={() => copyLink(o)}>
                                <Copy className="mr-2 h-4 w-4 text-slate-600" /> Copia link pagamento
                              </DropdownMenuItem>
                              {o.status === "inviata" && (
                                <DropdownMenuItem onClick={() => resendOffer(o)}>
                                  <Send className="mr-2 h-4 w-4 text-blue-700" /> Reinvia
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => remindOffer(o)}>
                                <Bell className="mr-2 h-4 w-4 text-amber-700" /> Sollecita
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setMarkPaidOpen(o); setPaidMethod("bonifico"); }}>
                                <Euro className="mr-2 h-4 w-4 text-green-700" /> Segna come pagata
                              </DropdownMenuItem>
                            </>
                          )}
                          {view !== "trash" && o.status !== "pagata" && o.status !== "annullata" && o.status !== "eliminata" && (
                            <DropdownMenuItem onClick={() => { setForcePaidOpen(o); setForcePaidMethod("bonifico"); }}>
                              <Zap className="mr-2 h-4 w-4 text-fuchsia-700" /> Forza pagamento
                            </DropdownMenuItem>
                          )}
                          {(o.status !== "pagata" && o.status !== "annullata" && o.status !== "eliminata") && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => cancelOffer(o)} className="text-rose-600 focus:text-rose-700">
                                <Trash2 className="mr-2 h-4 w-4" /> Annulla
                              </DropdownMenuItem>
                            </>
                          )}
                          {(o.status === "annullata" || o.status === "pagata") && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => deleteOffer(o)} className={o.status === "pagata" ? "text-amber-700 focus:text-amber-800" : "text-rose-700 focus:text-rose-800"}>
                                <Trash2 className="mr-2 h-4 w-4" /> Sposta nel cestino
                              </DropdownMenuItem>
                            </>
                          )}
                          {o.status === "eliminata" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => restoreOffer(o)} className="text-emerald-700 focus:text-emerald-800">
                                <RotateCcw className="mr-2 h-4 w-4" /> Ripristina
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => permanentDelete(o)} className="text-rose-700 focus:text-rose-800">
                                <Trash className="mr-2 h-4 w-4" /> Elimina definitivamente
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </React.Fragment>
              );
            });
          })()}
        </div>
      </div>

      {/* Dialog conferma eliminazione bulk offerte */}
      <Dialog open={bulkConfirmOpen} onOpenChange={(o) => { if (!o && !bulkBusy) setBulkConfirmOpen(false); }}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="bulk-delete-offers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-rose-600" />
              {view === "trash" ? "Eliminazione definitiva" : "Sposta nel cestino"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {view === "trash" ? (
              <>
                <p className="text-slate-700">
                  Stai per eliminare <strong className="text-rose-700">DEFINITIVAMENTE</strong> {selectedIds.size} offert{selectedIds.size === 1 ? "a" : "e"}.
                </p>
                <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
                  ⚠️ Questa operazione <strong>non è reversibile</strong>: le offerte non saranno più recuperabili dal cestino.
                </div>
              </>
            ) : (
              <>
                <p className="text-slate-700">
                  Stai per spostare nel cestino <strong>{selectedIds.size}</strong> offert{selectedIds.size === 1 ? "a" : "e"} selezionat{selectedIds.size === 1 ? "a" : "e"}.
                </p>
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                  ℹ️ Le offerte saranno spostate nel cestino e potrai ripristinarle in seguito dalla vista 🗑️ Cestino.
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="bulk-delete-cancel"
              onClick={() => setBulkConfirmOpen(false)}
              disabled={bulkBusy}
            >
              Annulla
            </Button>
            <Button
              data-testid="bulk-delete-confirm"
              onClick={doBulkDelete}
              disabled={bulkBusy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {bulkBusy
                ? "Elaborazione…"
                : view === "trash"
                  ? `Elimina ${selectedIds.size} offert${selectedIds.size === 1 ? "a" : "e"}`
                  : `Sposta ${selectedIds.size} nel cestino`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Crea/Modifica offerta */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) closeOfferDialog(); else setOpen(true); }}>
        <DialogContent className="w-[97vw] max-w-3xl lg:max-w-5xl xl:max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5 text-emerald-600" /> {editing ? "Modifica offerta finale" : "Nuova offerta finale"}
            </DialogTitle>
          </DialogHeader>

          {/* Drag&drop importa preventivo da file (AI extract) */}
          {!editing && (() => {
            // Helper: mappa i dati estratti dall'AI nel form (importo, compagnia, garanzie, auto-match preventivo)
            const applyExtraction = async (data, fileName) => {
              // 🆕 Feb 2026 — Carica il listino garanzie della compagnia dell'estrazione per l'auto-agganciamento
              let masterGuarantees = [];
              let matchedCompanyId = null;
              if (data?.company && Array.isArray(companies)) {
                const cn = String(data.company).toLowerCase().trim();
                const c = companies.find((cc) => (cc.name || "").toLowerCase().trim() === cn)
                       || companies.find((cc) => (cc.name || "").toLowerCase().includes(cn) || cn.includes((cc.name || "").toLowerCase()));
                if (c?.id) {
                  matchedCompanyId = c.id;
                  try {
                    const { data: mg } = await api.get(`/companies/${c.id}/guarantees`);
                    masterGuarantees = Array.isArray(mg) ? mg : [];
                  } catch { /* master può non esistere */ }
                }
              }
              setForm((f) => {
                // 1) Mappa garanzie/voci estratte → form.coverages
                let newCoverages = (f.coverages || []).filter((c) => c.name?.trim() || c.premium || c.sum_insured);
                const guarantees = Array.isArray(data?.guarantees) ? data.guarantees : [];
                if (guarantees.length > 0) {
                  // Sostituisce le coperture vuote con quelle estratte (preserva eventuali righe già compilate manualmente)
                  const extractedRows = guarantees.map((g) => {
                    const extractedName = g?.name || "";
                    const master = resolveGuarantee(extractedName, masterGuarantees);
                    return {
                      name: master?.name || extractedName,
                      extracted_name: master ? extractedName : null,   // etichetta originale se rinominata
                      _unmatched: !master && masterGuarantees.length > 0,
                      code: buildGuaranteeCode(master?.name || extractedName),
                      company_guarantee_id: master?.id || null,
                      imposte_percent: master?.imposte_percent != null ? Number(master.imposte_percent) : null,
                      ssn_percent: master?.ssn_percent != null ? Number(master.ssn_percent) : null,
                      sum_insured: g?.sum_insured != null ? String(g.sum_insured) : "",
                      premium: g?.premium != null ? String(g.premium) : "",
                      deductible: g?.deductible != null ? String(g.deductible) : "",
                      description: g?.description || "",
                      kind: "line_item",
                      _ai_extracted: true,
                    };
                  });
                  // Rimuove righe AI di estrazioni precedenti (rigenera dal nuovo file)
                  newCoverages = newCoverages.filter((c) => !c._ai_extracted);
                  newCoverages = [...extractedRows, ...newCoverages];
                  if (newCoverages.length === 0) newCoverages = [{ ...emptyCov }];
                }
                // 2) Auto-match del preventivo collegato in base alla targa estratta
                let matchedQuoteId = f.quote_id;
                if (!matchedQuoteId && data?.vehicle_plate) {
                  const plateNorm = String(data.vehicle_plate).toUpperCase().replace(/\s+/g, "");
                  const candidate = (quotes || []).find((qq) => {
                    const p = (qq?.details && (qq.details.vehicle_plate || qq.details.targa)) || qq?.vehicle_plate || "";
                    return String(p).toUpperCase().replace(/\s+/g, "") === plateNorm;
                  });
                  if (candidate) matchedQuoteId = candidate.id;
                }
                return {
                  ...f,
                  quote_id: matchedQuoteId,
                  amount: data?.premium != null ? String(data.premium) : f.amount,
                  notes: [
                    f.notes,
                    data?.company ? `Compagnia: ${data.company}` : "",
                    data?.policy_number ? `N° preventivo: ${data.policy_number}` : "",
                  ].filter(Boolean).join(" · "),
                  coverages: newCoverages.length ? newCoverages : [{ ...emptyCov }],
                };
              });
              const matched = data?.vehicle_plate ? ` · targa ${data.vehicle_plate}` : "";
              const nGar = Array.isArray(data?.guarantees) ? data.guarantees.length : 0;
              toast.success(
                `Estratti da ${fileName}${data?.company ? ` (${data.company})` : ""}${matched}` +
                (nGar > 0 ? ` · ${nGar} garanzie/voci` : "")
              );
            };
            const handleExtract = async (file) => {
              if (!file || extractBusy) return;
              try {
                setExtractBusy(true);
                // 🆕 Feb 2026 — Progress bar per estrazione AI (0→90% simulato, 100% al termine)
                const data = await runExtractionWithProgress({
                  label: `Estrazione preventivo · ${file.name}`,
                  run: () => {
                    const fd = new FormData();
                    fd.append("file", file);
                    return api.post("/utils/extract-pdf?kind=polizza", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
                  },
                });
                await applyExtraction(data || {}, file.name);
              } catch (_err) {
                // toast d'errore già mostrato da runExtractionWithProgress
              }
              finally { setExtractBusy(false); }
            };
            return (
            <div
              data-testid="offer-dropzone"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation();
                handleExtract(e.dataTransfer.files?.[0]);
              }}
              className="border-2 border-dashed border-violet-300 rounded-lg p-4 bg-gradient-to-br from-violet-50 to-blue-50 text-center cursor-pointer hover:border-violet-500 transition"
            >
              <p className="text-sm font-medium text-violet-800">
                {extractBusy ? "⏳ Estrazione AI in corso…" : "📥 Trascina qui un preventivo (PDF/JPG/PNG/HEIC)"}
              </p>
              <p className="text-[11px] text-slate-600 mt-1">
                L&apos;AI estrarrà importo, compagnia, <strong>garanzie attive</strong> e <strong>voci di costo</strong>. Il preventivo collegato verrà auto-selezionato dalla targa, se trovata.
              </p>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                className="mt-2 text-xs"
                data-testid="offer-file-input"
                onChange={(e) => {
                  handleExtract(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </div>
            );
          })()}

          <div className="space-y-4">
            <div>
              <Label>Preventivo collegato <span className="text-red-500">*</span></Label>
              <Select value={form.quote_id} onValueChange={(v) => setForm({ ...form, quote_id: v })} disabled={!!editing}>
                <SelectTrigger data-testid="offer-quote-select"><SelectValue placeholder="Dato obbligatorio · seleziona un preventivo" /></SelectTrigger>
                <SelectContent>
                  {quoteOptions.map((q) => (
                    <SelectItem key={q.v} value={q.v} data-testid={`offer-quote-option-${q.v}`}>
                      <span className="inline-flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500">{q.base}</span>
                        {(q.brand || q.model || q.plate) && (
                          <span className="inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-sky-50 px-1.5 py-0.5" data-testid={`offer-quote-vehicle-${q.v}`}>
                            <span>{q.vIcon}</span>
                            {(q.brand || q.model) && (
                              <span className="text-[13px] font-semibold text-sky-900">{[q.brand, q.model].filter(Boolean).join(" ")}</span>
                            )}
                            {q.plate && (
                              <span className="font-mono text-[12px] font-bold tracking-wider text-slate-900 bg-white border border-slate-400 rounded px-1.5">{q.plate}</span>
                            )}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Tutti i dati personali (nome, CF, indirizzo, email) sono presi dal contatto del preventivo selezionato.</p>
              {/* 🆕 Feb 2026 — Copia rapida dati (veicolo + cliente/azienda) per compilare portali compagnie */}
              {(() => {
                const q = linkedQuote;
                if (!q) return null;
                const cl = clientsMap[q.client_id] || {};
                const vd = (q.details && typeof q.details === 'object') ? q.details : {};
                const isCompany = cl.subject_type === 'company';
                const copyValue = async (label, value) => {
                  const v = (value ?? '').toString().trim();
                  if (!v) { toast.error(`${label}: nessun valore`); return; }
                  try {
                    await navigator.clipboard.writeText(v);
                    toast.success(`Copiato: ${label}`);
                  } catch (_e) {
                    toast.error("Copia negli appunti non riuscita");
                  }
                };
                const renderCopyField = (label, value) => {
                  const has = value !== undefined && value !== null && String(value).trim() !== '';
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => has && copyValue(label, value)}
                      disabled={!has}
                      title={has ? `Copia ${label}` : `${label} non disponibile`}
                      className={`group flex items-center gap-2 rounded-md border px-2 py-1.5 text-left transition-colors ${has ? 'bg-white border-slate-200 hover:bg-emerald-50 hover:border-emerald-300 cursor-pointer' : 'bg-slate-50 border-slate-200 cursor-not-allowed opacity-60'}`}
                      data-testid={`copy-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
                        <div className="text-[13px] text-slate-800 font-mono truncate">{has ? String(value) : '—'}</div>
                      </div>
                      {has && <Copy className="h-3.5 w-3.5 text-slate-400 group-hover:text-emerald-600 shrink-0" />}
                    </button>
                  );
                };
                const fmtDate = (d) => {
                  if (!d) return '';
                  try {
                    const dt = new Date(d);
                    if (isNaN(dt.getTime())) return String(d);
                    return dt.toLocaleDateString('it-IT');
                  } catch (_e) { return String(d); }
                };
                const sedeLegale = [cl.address, cl.house_number].filter(Boolean).join(' ');
                const cittaLine = [cl.cap, cl.city, cl.province ? `(${cl.province})` : ''].filter(Boolean).join(' ');
                return (
                  <details className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/40 group/copy" data-testid="quick-copy-section">
                    <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-emerald-900 flex items-center gap-2 hover:bg-emerald-100/50 rounded-t-lg list-none">
                      <span className="inline-block transition-transform group-open/copy:rotate-90 text-emerald-700">▶</span>
                      📋 Copia rapida dati <span className="text-[11px] font-normal text-emerald-700">(clicca su un campo per copiarlo negli appunti — utile per portali compagnie)</span>
                    </summary>
                    <div className="p-3 space-y-3">
                      {/* Veicolo */}
                      <div>
                        <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">🚗 Veicolo</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                          {renderCopyField('Targa', vd.vehicle_plate)}
                          {renderCopyField('Marca', vd.vehicle_brand)}
                          {renderCopyField('Modello', vd.vehicle_model)}
                          {renderCopyField('Versione', vd.vehicle_version)}
                          {renderCopyField('Alimentazione', vd.vehicle_fuel)}
                          {renderCopyField('Cilindrata', vd.vehicle_displacement)}
                          {renderCopyField('Potenza kW', vd.vehicle_kw)}
                          {renderCopyField('CV Fiscali', vd.vehicle_fiscal_power)}
                          {renderCopyField('Immatricolazione', fmtDate(vd.vehicle_first_registration))}
                          {renderCopyField('Data acquisto', fmtDate(vd.vehicle_purchase_date))}
                          {renderCopyField('Telaio', vd.vehicle_chassis)}
                          {renderCopyField('Uso', vd.vehicle_use)}
                        </div>
                      </div>
                      {/* 🆕 30/07/2026 — Natante (mostrata solo se ci sono dati imbarcazione/motore) */}
                      {(vd.boat_maker_model || vd.boat_engine_maker || vd.boat_registration_number || vd.boat_engine_model || vd.boat_engine_kw) && (
                        <div>
                          <div className="text-[11px] font-bold text-cyan-800 uppercase tracking-wide mb-1.5">⛵ Natante</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                            {renderCopyField('Marca/Modello', vd.boat_maker_model)}
                            {renderCopyField('Materiale scafo', vd.boat_hull_material)}
                            {renderCopyField('Lunghezza (m)', vd.boat_length_m)}
                            {renderCopyField('Larghezza (m)', vd.boat_beam_m)}
                            {renderCopyField('Anno costruzione', vd.boat_build_year)}
                            {renderCopyField('Matricola imbarcazione', vd.boat_registration_number)}
                            {renderCopyField('Costruttore motore', vd.boat_engine_maker)}
                            {renderCopyField('Modello motore', vd.boat_engine_model)}
                            {renderCopyField('Matricola motore', vd.boat_engine_serial)}
                            {renderCopyField('Installazione', vd.boat_engine_install)}
                            {renderCopyField('Combustibile', vd.boat_engine_fuel)}
                            {renderCopyField('Potenza (Kw)', vd.boat_engine_kw)}
                            {renderCopyField('Massa motore (kg)', vd.boat_engine_mass_kg)}
                            {renderCopyField('N° dichiarazione', vd.boat_declaration_number)}
                            {renderCopyField('Data dichiarazione', fmtDate(vd.boat_declaration_date))}
                          </div>
                        </div>
                      )}
                      {/* Azienda (se applicabile) */}
                      {isCompany && (
                        <div>
                          <div className="text-[11px] font-bold text-indigo-800 uppercase tracking-wide mb-1.5">🏢 Dati Azienda</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                            {renderCopyField('Ragione Sociale', cl.ragione_sociale)}
                            {renderCopyField('P.IVA', cl.partita_iva)}
                            {renderCopyField('CF Societario', cl.codice_fiscale_societario)}
                            {renderCopyField('Codice SDI', cl.sdi_code)}
                            {renderCopyField('PEC', cl.pec_email)}
                            {renderCopyField('Email', cl.email)}
                            {renderCopyField('Telefono', cl.phone || cl.mobile)}
                            {renderCopyField('Sede - Via', sedeLegale)}
                            {renderCopyField('Sede - CAP/Città', cittaLine)}
                          </div>
                          <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mt-2 mb-1.5">👔 Legale Rappresentante</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                            {renderCopyField('LR Cognome', cl.legale_rappresentante_surname)}
                            {renderCopyField('LR Nome', cl.legale_rappresentante_name)}
                            {renderCopyField('LR CF', cl.legale_rappresentante_fiscal_code)}
                            {renderCopyField('LR Nascita', fmtDate(cl.legale_rappresentante_birth_date))}
                            {renderCopyField('LR Luogo Nasc.', cl.legale_rappresentante_birth_place)}
                            {renderCopyField('LR Prov. Nasc.', cl.legale_rappresentante_birth_province)}
                            {renderCopyField('LR Sesso', cl.legale_rappresentante_gender)}
                            {renderCopyField('LR Carica', cl.legale_rappresentante_role)}
                            {renderCopyField('LR Nazionalità', cl.legale_rappresentante_nationality)}
                          </div>
                        </div>
                      )}
                      {/* Privato */}
                      {!isCompany && (
                        <div>
                          <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-1.5">👤 Anagrafica Contraente</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                            {renderCopyField('Cognome', cl.surname)}
                            {renderCopyField('Nome', cl.name)}
                            {renderCopyField('Codice Fiscale', cl.fiscal_code)}
                            {renderCopyField('Data Nascita', fmtDate(cl.birth_date))}
                            {renderCopyField('Luogo Nascita', cl.birth_place)}
                            {renderCopyField('Sesso', cl.gender)}
                            {renderCopyField('Email', cl.email)}
                            {renderCopyField('Cellulare', cl.mobile)}
                            {renderCopyField('Telefono', cl.phone)}
                            {renderCopyField('Indirizzo', sedeLegale)}
                            {renderCopyField('CAP/Città', cittaLine)}
                            {renderCopyField('Professione', cl.profession)}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>
                );
              })()}
              {/* 🆕 Dropzone PDF preventivo compagnia subito sotto Preventivo collegato (richiesta utente: posizione in alto) */}
              {editing && (
                <div className="rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/40 p-3 mt-3" data-testid="variant-extract-dropzone-wrap">
                  <DropZone
                    testid="variant-extract-dropzone"
                    onFiles={(files) => { if (files[0]) addVariantFromFile(files[0]); }}
                    accept={["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/heic"]}
                    className="text-center"
                  >
                    <div className="py-3">
                      <div className="text-base mb-1">{variantExtracting ? "⏳ Estrazione AI in corso…" : "📥 Trascina qui un PDF preventivo compagnia"}</div>
                      <div className="text-[11px] text-slate-600">
                        L&apos;AI estrae automaticamente <strong>compagnia</strong>, <strong>premio</strong> e <strong>garanzie</strong> e crea una nuova variante.
                        <label className="ml-1 text-blue-700 hover:underline cursor-pointer font-medium">
                          oppure scegli file
                          <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                            className="hidden"
                            data-testid="variant-extract-input"
                            disabled={variantExtracting}
                            onChange={(e) => { if (e.target.files?.[0]) addVariantFromFile(e.target.files[0]); e.target.value = ""; }}
                          />
                        </label>
                      </div>
                    </div>
                  </DropZone>
                </div>
              )}
              {/* 🆕 Allegati offerta: PDF preventivi compagnia estratti + upload manuali (visibili solo in modalità Modifica) */}
              {editing && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="offer-attachments-section">
                  <button
                    type="button"
                    data-testid="offer-attach-toggle"
                    onClick={() => setAttSectionOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-2 hover:bg-slate-100/50 rounded px-1 -mx-1 py-0.5 transition-colors"
                    title={attSectionOpen ? "Nascondi allegati offerta" : "Mostra allegati offerta"}
                  >
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide inline-flex items-center gap-1.5">
                      <span className={`inline-block transition-transform ${attSectionOpen ? "rotate-90" : ""}`}>▶</span>
                      📎 Allegati offerta ({offerAttachments.length})
                    </span>
                    {editing.quote_id && attSectionOpen && (
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid="sync-quote-attachments"
                        onClick={async (e) => {
                          e.stopPropagation();
                          setAttUploadBusy(true);
                          try {
                            const { data } = await api.post(`/quote-offers/${editing.id}/sync-quote-attachments`);
                            if (data.added > 0) {
                              toast.success(`${data.added} allegat${data.added === 1 ? "o sincronizzato" : "i sincronizzati"} dal preventivo`);
                              await loadOfferAttachments();
                            } else {
                              toast.info(`Nessun nuovo allegato da sincronizzare (${data.already_present} già presenti su ${data.quote_total})`);
                            }
                          } catch (err) { toast.error(formatApiError(err)); }
                          finally { setAttUploadBusy(false); }
                        }}
                        aria-disabled={attUploadBusy}
                        className={`text-[11px] font-medium inline-flex items-center gap-1 ${attUploadBusy ? "text-slate-400 pointer-events-none" : "text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"}`}
                        title="Riporta gli allegati caricati sul preventivo padre"
                      >
                        🔄 Sincronizza dal preventivo
                      </span>
                    )}
                  </button>
                  {attSectionOpen && (
                  <div className="mt-2">
                  {/* 🆕 10/07/2026 — Upload dashboard con Drop Zone + Scanner + Camera (allineato a Polizze) */}
                  <DropZone
                    testid="offer-attach-dropzone"
                    onFiles={(files) => uploadOfferAttachmentsBatch(files)}
                    accept={["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp", "image/heic", "image/heif"]}
                    multiple
                    disabled={attUploadBusy}
                    className="space-y-2"
                  >
                    <div className="bg-blue-50/50 border border-blue-100 rounded-md p-3 space-y-2">
                      <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">Carica nuovo allegato</div>
                      <p className="text-[10px] text-blue-700/70">💡 Trascina i file ovunque in questa finestra per caricarli rapidamente.</p>
                      <Input
                        data-testid="offer-attach-description"
                        value={attachDesc}
                        onChange={(e) => setAttachDesc(e.target.value)}
                        placeholder="Descrizione (opzionale): es. Preventivo Allianz, foto CID…"
                        disabled={attUploadBusy}
                        className="h-8 text-xs"
                      />
                      <label className="block">
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                          className="hidden"
                          disabled={attUploadBusy}
                          data-testid="offer-attachment-upload"
                          onChange={(e) => { if (e.target.files?.length) uploadOfferAttachmentsBatch(e.target.files); e.target.value = ""; }}
                        />
                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed ${attUploadBusy ? "border-slate-300 text-slate-400" : "border-blue-400 hover:bg-blue-100 cursor-pointer text-blue-700"} text-xs`}>
                          <Plus className="h-3.5 w-3.5" /> {attUploadBusy ? "Caricamento…" : "Scegli file (PDF / JPG / PNG / WEBP, max 25 MB)"}
                        </span>
                      </label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-slate-500">oppure</span>
                        <ScanButton
                          testid="scan-offer-attachment"
                          label="Importa da scanner"
                          variant="outline"
                          disabled={attUploadBusy}
                          onFile={(f) => uploadOfferAttachmentsBatch([f])}
                        />
                        <CameraButton
                          testid="cam-offer-attachment"
                          label="📷 Foto da fotocamera"
                          variant="outline"
                          disabled={attUploadBusy}
                          onFile={(f) => uploadOfferAttachmentsBatch([f])}
                        />
                      </div>
                    </div>
                  </DropZone>
                  {offerAttachments.length === 0 ? (
                    <p className="text-[11px] text-slate-500 italic">Nessun allegato. I PDF estratti dalla dropzone verranno salvati qui automaticamente.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {offerAttachments.map((a) => (
                        <li key={a.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded px-3 py-2 text-sm" data-testid={`offer-attachment-${a.id}`}>
                          <span className="text-2xl leading-none">{(a.content_type || "").includes("pdf") ? "📄" : "🖼️"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium text-slate-800">{a.filename}</div>
                            {a.description && <div className="text-[10px] text-emerald-700 truncate">{a.description}</div>}
                          </div>
                          <span className="text-[10px] text-slate-400 flex-shrink-0">{Math.round((a.size_bytes || 0) / 1024)} KB</span>
                          <button
                            type="button"
                            onClick={() => renameOfferAttachment(a)}
                            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded p-1.5"
                            title="Rinomina"
                            data-testid={`offer-attachment-rename-${a.id}`}
                          >
                            <Pencil className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => previewOfferAttachment(a)}
                            className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 rounded p-1.5"
                            title="Anteprima"
                            data-testid={`offer-attachment-preview-${a.id}`}
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadOfferAttachment(a)}
                            className="text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded p-1.5"
                            title="Scarica"
                            data-testid={`offer-attachment-download-${a.id}`}
                          >
                            <Download className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteOfferAttachment(a.id)}
                            className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded p-1.5"
                            title="Elimina"
                            data-testid={`offer-attachment-delete-${a.id}`}
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>Importo da pagare (€) <span className="text-[10px] font-normal text-slate-500">— ereditato da offerta finalizzata (opzionale)</span></Label>
              <MoneyInput
                data-testid="offer-amount"
                value={form.amount}
                onChange={(v) => setForm({ ...form, amount: v })}
                placeholder={winnerVariantId ? "es. 850,00" : "Vuoto se nessuna variante finalizzata"}
                disabled={!!winnerVariantId}
                title={winnerVariantId ? "Importo ereditato dalla variante finalizzata. Per modificarlo, cambia o annulla la finalizzazione." : undefined}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {winnerVariantId
                  ? "🔒 Importo auto-calcolato dalle garanzie della variante finalizzata."
                  : "Se non c'è ancora una variante finalizzata puoi lasciare il campo vuoto: l'offerta resta interlocutoria."}
              </p>
              {/* 🆕 Avviso quando l'agente ha digitato un importo manuale senza variante finalizzata */}
              {!winnerVariantId && form.amount !== "" && form.amount != null && Number(form.amount) > 0 && (
                <div
                  className="mt-2 rounded-md px-3 py-2 text-xs border bg-amber-50 border-amber-300 text-amber-800 flex items-start gap-2"
                  data-testid="manual-amount-warning"
                >
                  <span className="text-base leading-none">⚠</span>
                  <div>
                    <strong>Importo manuale</strong> — nessuna variante è finalizzata.
                    Ricorda di finalizzare una variante compagnia per allineare i dati dell&apos;offerta (compagnia, garanzie, rate) con la scelta definitiva prima di inviarla al cliente.
                  </div>
                </div>
              )}
              {(() => {
                // 🆕 Se c'è una variante finalizzata, mostra il dettaglio del calcolo
                // basato esclusivamente sulle garanzie della variante (non più dal Dettaglio coperture).
                if (winnerVariantId) {
                  const winner = (variants || []).find((v) => v.id === winnerVariantId);
                  const wcovs = (winner?.coverages || []).filter((c) => (c.kind || "coverage") === "coverage");
                  const wlines = (winner?.coverages || []).filter((c) => c.kind === "line_item");
                  const covSum = wcovs.map((c) => Number(c.premium) || 0).reduce((a, b) => a + b, 0);
                  const liSum = wlines.map((c) => Number(c.premium) || 0).reduce((a, b) => a + b, 0);
                  return (
                    <div data-testid="amount-coverage-check" className="mt-2 rounded-md px-3 py-2 text-xs border bg-emerald-50 border-emerald-200 text-emerald-800 flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">Calcolo importo (dalla variante finalizzata{winner?.company ? ` "${winner.company}"` : ""}):</div>
                        <div className="text-[11px] mt-0.5 space-y-0.5">
                          <div>• Somma garanzie ({wcovs.length}): <span className="tabular-nums font-semibold">{formatCurrency(covSum)}</span></div>
                          {wlines.length > 0 && (
                            <div>• Voci accessorie ({wlines.length}): <span className="tabular-nums font-semibold">+{formatCurrency(liSum)}</span></div>
                          )}
                          <div className="border-t border-emerald-200 mt-1 pt-1">= Totale: <strong className="tabular-nums">{formatCurrency(covSum + liSum)}</strong></div>
                        </div>
                      </div>
                    </div>
                  );
                }
                // Workflow legacy senza variante finalizzata: validazione strict
                const premiums = (form.coverages || [])
                  .map((c) => Number(c.premium) || 0)
                  .filter((n) => n > 0);
                if (premiums.length === 0) return null;
                const sum = premiums.reduce((a, b) => a + b, 0);
                const amount = Number(form.amount) || 0;
                const diff = +(amount - sum).toFixed(2);
                const ok = Math.abs(diff) <= 1;
                return (
                  <div
                    data-testid="amount-coverage-check"
                    className={`mt-2 rounded-md px-3 py-2 text-xs border flex items-start gap-2 ${
                      ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"
                    }`}
                  >
                    {ok ? <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">
                        Somma premi coperture: <span className="tabular-nums">{formatCurrency(sum)}</span>
                      </div>
                      <div className="text-[11px] mt-0.5">
                        {ok ? (
                          "✓ Coerente con l'importo da pagare (tolleranza € 1)."
                        ) : (
                          <>Differenza: <strong className="tabular-nums">{diff > 0 ? "+" : ""}{formatCurrency(diff)}</strong> — puoi salvare comunque, la somma è solo indicativa.</>
                        )}
                      </div>
                    </div>
                    {!ok && (
                      <button
                        type="button"
                        data-testid="align-amount-btn"
                        onClick={() => setForm({ ...form, amount: String(sum) })}
                        className="shrink-0 text-[11px] font-semibold underline hover:no-underline whitespace-nowrap"
                      >
                        Allinea a {formatCurrency(sum)}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>

            <div>
              <Label>Frazionamento <span className="text-[10px] font-normal text-slate-500">(sincronizzato con il preventivo)</span></Label>
              <Select
                value={form.frazionamento || "annuale"}
                onValueChange={(v) => setForm({ ...form, frazionamento: v })}
              >
                <SelectTrigger data-testid="offer-frazionamento"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="annuale">Annuale</SelectItem>
                  <SelectItem value="semestrale">Semestrale</SelectItem>
                  <SelectItem value="quadrimestrale">Quadrimestrale</SelectItem>
                  <SelectItem value="trimestrale">Trimestrale</SelectItem>
                  <SelectItem value="mensile">Mensile</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-0.5">Modificandolo qui, viene aggiornato anche sul preventivo collegato (e viceversa).</p>
            </div>

            {/* 🆕 Feb 2026 — Date decorrenza/scadenza EREDITATE dal preventivo collegato.
                Mostrate qui in modo prominente per essere replicate automaticamente su ogni nuova variante. */}
            {(linkedQuoteStart || linkedQuoteEnd) && (
              <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3" data-testid="offer-linked-quote-dates">
                <div className="text-[10px] uppercase tracking-wider text-blue-800 font-semibold mb-1.5 flex items-center gap-1">
                  📅 Decorrenza e scadenza (dal preventivo{linkedQuote?.quote_number ? ` ${linkedQuote.quote_number}` : ""})
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[10px] text-slate-500 block">Decorrenza</span>
                    <span className="font-semibold text-slate-800" data-testid="offer-linked-start">
                      {linkedQuoteStart ? new Date(linkedQuoteStart).toLocaleDateString("it-IT") : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 block">Scadenza</span>
                    <span className="font-semibold text-slate-800" data-testid="offer-linked-end">
                      {linkedQuoteEnd ? new Date(linkedQuoteEnd).toLocaleDateString("it-IT") : "—"}
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">
                  Queste date vengono pre-compilate automaticamente su ogni nuova variante. Per cambiarle, apri il preventivo.
                </p>
              </div>
            )}

            {/* 🆕 Feb 2026 — Contatore "Il cliente vedrà: N garanzie + M voci accessorie".
                Replica la stessa logica del backend `public_get_offer` per anteprima real-time. */}
            <ClientVisibilityCounter form={form} variants={variants} />

            <div>
              <Label>Tipo di guida <span className="text-[10px] font-normal text-slate-500">(sincronizzato con il preventivo)</span></Label>
              <Select
                value={form.driving_type || ""}
                onValueChange={(v) => setForm({ ...form, driving_type: v })}
              >
                <SelectTrigger data-testid="offer-driving-type"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="esclusiva">Esclusiva</SelectItem>
                  <SelectItem value="esperta">Esperta</SelectItem>
                  <SelectItem value="libera">Libera</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-0.5">Modificandolo qui, viene aggiornato anche sul preventivo collegato (e viceversa). Mostrato nell&apos;email al cliente.</p>
              {/* 🆕 09/07/2026 — Warning se né offerta né preventivo hanno un tipo di guida */}
              {!form.driving_type && !(linkedQuote?.details?.driving_type) && form.quote_id && (
                <div
                  className="mt-2 rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-[11px] flex items-start gap-2"
                  data-testid="driving-type-missing-warning"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                  <span>
                    <strong>Attenzione:</strong> il preventivo collegato non ha ancora un tipo di guida. Imposta manualmente la selezione qui sopra per evitare che la sezione &quot;Tipo di Guida&quot; sia nascosta nel PDF finale.
                  </span>
                </div>
              )}
            </div>

            {/* 🆕 Scadenza offerta (valid_until) — modificabile */}
            <div>
              <Label>Scadenza offerta <span className="text-[10px] font-normal text-slate-500">(data entro cui il cliente deve pagare)</span></Label>
              <DatePicker
                value={form.valid_until || ""}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                data-testid="offer-valid-until"
              />
              <p className="text-[10px] text-slate-500 mt-0.5">Se non specificata, viene impostata a 5 giorni dalla creazione. Modificabile in qualsiasi momento (finché l&apos;offerta non è pagata).</p>
            </div>

            {/* 🆕 Varianti compagnia — multiple offerte annidate, ordinate per prezzo */}
            {editing && (
              <div id="offer-variants-section" className="border-t border-slate-200 pt-4" data-testid="offer-variants-section">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <Label className="text-sm font-semibold inline-flex items-center gap-2">
                    🏆 Varianti compagnia
                    {sortedVariants.length > 0 && <span className="text-[10px] font-normal text-slate-500">({sortedVariants.length} · ordinate dal più economico)</span>}
                    {/* 🆕 Audit log espandibile: chi/quando ha finalizzato/rimosso finalizzazione */}
                    {(editing?.finalization_history || []).length > 0 && (
                      <details className="inline-block ml-2">
                        <summary className="cursor-pointer text-[10px] font-normal text-slate-500 hover:text-slate-700 select-none" data-testid="finalization-history-toggle">
                          📜 Storico ({editing.finalization_history.length})
                        </summary>
                        <div className="mt-1 bg-slate-50 border border-slate-200 rounded p-2 text-[10px] max-h-40 overflow-y-auto min-w-[260px] absolute z-40 shadow-lg" data-testid="finalization-history-list">
                          {[...editing.finalization_history].reverse().map((h, i) => (
                            <div key={i} className="py-1 border-b border-slate-100 last:border-0">
                              <span className={h.action === "finalize" ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>
                                {h.action === "finalize" ? "🏆 Finalizzata" : "✖ Rimossa"}
                              </span>
                              {h.company && <span className="ml-1 text-slate-800">{h.company}</span>}
                              {h.amount != null && <span className="ml-1 text-slate-600">({formatCurrency(h.amount)})</span>}
                              <div className="text-slate-500">👤 {h.user_name || "—"} · 🕓 {formatDateTime(h.at)}</div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </Label>
                  <div className="flex gap-1.5 flex-wrap items-center">
                    {/* 🆕 Template varianti */}
                    <div className="relative">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="variant-templates-menu-btn"
                        onClick={() => setTemplatesMenuOpen((v) => !v)}
                        className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        📥 Template ({variantTemplates.length})
                      </Button>
                      {templatesMenuOpen && (
                        <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-50 min-w-[280px] max-h-[320px] overflow-hidden flex flex-col" data-testid="variant-templates-menu">
                          {variantTemplates.length >= 5 && (
                            <input
                              type="text"
                              data-testid="templates-search"
                              value={templatesSearch}
                              onChange={(e) => setTemplatesSearch(e.target.value)}
                              placeholder="🔎 Cerca template…"
                              className="text-xs px-2 py-1.5 border-b border-slate-200 outline-none focus:bg-purple-50"
                              autoFocus
                            />
                          )}
                          <div className="overflow-y-auto flex-1">
                          {variantTemplates.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-slate-500 italic">Nessun template salvato</div>
                          ) : (
                            (() => {
                              const q = templatesSearch.trim().toLowerCase();
                              const filtered = !q ? variantTemplates : variantTemplates.filter((t) =>
                                (t.name || "").toLowerCase().includes(q)
                                || (t.variants || []).some((v) => (v.company || "").toLowerCase().includes(q))
                              );
                              if (filtered.length === 0) {
                                return <div className="px-3 py-2 text-xs text-slate-400 italic">Nessun match per &quot;{q}&quot;</div>;
                              }
                              return filtered.map((tpl) => {
                              const totalCovs = (tpl.variants || []).reduce((acc, v) => acc + (v.coverages?.length || 0), 0);
                              return (
                              <div key={tpl.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-purple-50">
                                <button
                                  type="button"
                                  data-testid={`apply-template-${tpl.id}`}
                                  onClick={() => applyVariantTemplate(tpl.id, tpl.name)}
                                  className="flex-1 text-left text-xs"
                                  title="Applica questo template all'offerta corrente"
                                >
                                  <div className="font-semibold">{tpl.name}</div>
                                  <div className="text-[10px] text-slate-500">
                                    {(tpl.variants || []).length} variant{(tpl.variants || []).length === 1 ? "e" : "i"}
                                    {totalCovs > 0 && <span className="ml-1 text-emerald-700">· {totalCovs} garanzie incluse</span>}
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteVariantTemplate(tpl.id, tpl.name)}
                                  className="text-rose-500 hover:text-rose-700 px-1"
                                  title="Elimina template"
                                >
                                  <XIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              );
                            });
                            })()
                          )}
                          </div>
                        </div>
                      )}
                    </div>
                    {sortedVariants.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="save-variants-as-template"
                        onClick={saveVariantsAsTemplate}
                        className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                        title="Salva queste varianti come template riutilizzabile"
                      >
                        💾 Salva
                      </Button>
                    )}
                    {sortedVariants.length >= 2 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="export-variants-comparison-pdf"
                      onClick={() => {
                        (async () => {
                          try {
                            const r = await api.get(`/quote-offers/${editing.id}/variants-comparison-pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(r.data);
                            const a = document.createElement("a");
                            a.href = url; a.download = `confronto_${editing.offer_number || editing.id}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } catch (e) { toast.error(formatApiError(e)); }
                        })();
                      }}
                      title="Genera un PDF tabellare con tutte le varianti a confronto"
                      className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    >
                      <FileText className="h-3.5 w-3.5 mr-1" /> Esporta confronto PDF
                    </Button>
                  )}
                  </div>
                </div>
                {sortedVariants.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mb-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5" data-testid="variants-selection-bar">
                    <div className="inline-flex rounded-md border border-slate-300 bg-white p-0.5" data-testid="variants-view-toggle">
                      {[["cards", "▤ Schede"], ["table", "▦ Tabella"]].map(([k, lbl]) => (
                        <button key={k} type="button" onClick={() => changeVariantsView(k)} data-testid={`variants-view-${k}`}
                          className={`h-6 px-2 rounded text-xs font-medium transition-colors ${variantsView === k ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                    <span className="text-xs text-slate-600" data-testid="variants-selected-count">
                      {selectedVariants.length} selezionat{selectedVariants.length === 1 ? "a" : "e"}
                    </span>
                    <Button
                      type="button" size="sm" variant="outline"
                      className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                      disabled={selectedVariants.length === 0 || variantBulkBusy}
                      onClick={toggleFeaturedSelected}
                      data-testid="variants-feature-selected-btn"
                    >
                      ⭐ {allSelectedFeatured ? "Togli evidenza" : "Metti in evidenza"}
                    </Button>
                    <Button
                      type="button" size="sm" variant="outline"
                      className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      disabled={selectedVariants.length === 0 || variantBulkBusy}
                      onClick={printSelectedComparison}
                      data-testid="variants-print-selected-btn"
                    >
                      <Printer className="h-3.5 w-3.5 mr-1" /> Stampa confronto selezionate
                    </Button>
                  </div>
                )}
                <PartnerReplyDialog
                  emailId={partnerReply?.emailId}
                  offerId={editing?.id}
                  variantId={partnerReply?.variantId}
                  onOpenChange={closePartnerReply}
                  onSent={(entry) => {
                    const vid = partnerReply?.variantId;
                    setVariants((arr) => arr.map((vv) => vv.id === vid ? { ...vv, partner_emails: [...(vv.partner_emails || []), entry] } : vv));
                  }}
                />
                <VariantPartnerEmailDialog
                  offerId={editing?.id}
                  variant={partnerEmailVariant}
                  open={!!partnerEmailVariant}
                  onOpenChange={(o) => { if (!o) setPartnerEmailVariant(null); }}
                  onSent={(entry) => {
                    const vid = partnerEmailVariant?.id;
                    setVariants((arr) => arr.map((vv) => vv.id === vid ? { ...vv, partner_emails: [...(vv.partner_emails || []), entry] } : vv));
                  }}
                />
                {sortedVariants.length > 0 && variantsView === "table" && (
                  <VariantsCompareTable
                    variants={displayVariants}
                    cheapestId={sortedVariants[0]?.id}
                    winnerId={winnerVariantId}
                    selectedIds={selectedVariantIds}
                    onToggle={toggleVariantSelected}
                    fallbackDrivingType={form.driving_type || linkedQuote?.details?.driving_type}
                  />
                )}
                {/* Lista varianti */}
                {sortedVariants.length > 0 && variantsView === "cards" && (
                  <div className="space-y-2 mb-3" data-testid="offer-variants-list">
                    {displayVariants.map((v) => {
                      const isWinner = v.id === winnerVariantId;
                      const isCheapest = v.id === sortedVariants[0]?.id;
                      const isEditing = editingVariantId === v.id;
                      if (isEditing) {
                        return (
                          <div key={v.id} className="rounded-lg border-2 border-blue-500 bg-blue-50 p-3" data-testid={`offer-variant-edit-${v.id}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                              <div className="sm:col-span-3 min-w-0 flex flex-col gap-1">
                                <select
                                  className="min-w-0 h-9 px-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                  value={editVariantDraft.broker_id || ""}
                                  onChange={(e) => setEditVariantDraft((d) => ({ ...d, broker_id: e.target.value, company_id: "" }))}
                                  data-testid={`edit-variant-broker-${v.id}`}
                                  title="Broker / Agenzia partner che ha fornito la quotazione (opz.)"
                                >
                                  <option value="">Broker / Agenzia (opz.)</option>
                                  {(brokers || []).map((b) => (
                                    <option key={b.id} value={b.id}>
                                      {b.name}{b.type ? ` · ${b.type === "agenzia" ? "Agenzia" : "Broker"}` : ""}
                                    </option>
                                  ))}
                                </select>
                                {editVariantDraft.broker_id && (() => {
                                  const cnt = getLinkedCompaniesCount(editVariantDraft.broker_id);
                                  const onOpen = () => navigate(`/brokers?expand=${editVariantDraft.broker_id}&return_to_offer=${editing?.id || ""}`);
                                  return cnt !== null ? (
                                    <button
                                      type="button"
                                      onClick={onOpen}
                                      className="text-[10.5px] text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1 self-start"
                                      title="Gestisci compagnie collegate a questo partner"
                                      data-testid={`edit-variant-broker-links-count-${v.id}`}
                                    >
                                      🔗 <span className="font-medium">{cnt}</span>&nbsp;{cnt === 1 ? "compagnia collegata" : "compagnie collegate"} · gestisci →
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={onOpen}
                                      className="text-[10.5px] text-amber-700 hover:text-amber-900 hover:underline flex items-center gap-1 self-start"
                                      title="Configura le compagnie collegate a questo partner"
                                      data-testid={`edit-variant-broker-links-count-${v.id}`}
                                    >
                                      ⚠️ nessuna compagnia collegata · configura →
                                    </button>
                                  );
                                })()}
                              </div>
                              {/* 🆕 Compagnia: dropdown compagnie collegate al broker (con override manuale) */}
                              <div className="sm:col-span-3 min-w-0 flex gap-1 items-stretch">
                                {editVariantCompanyMode === "select" ? (
                                  <select
                                    className="flex-1 min-w-0 h-9 px-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                    value={editVariantDraft.company_id || (editVariantDraft.company ? "__custom__" : "")}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === "__other__") {
                                        setEditVariantCompanyMode("manual");
                                        setEditVariantDraft((d) => ({ ...d, company: "", company_id: "" }));
                                        return;
                                      }
                                      const cmp = getCompaniesForBroker(editVariantDraft.broker_id).find((c) => c.id === val);
                                      setEditVariantDraft((d) => ({ ...d, company: cmp?.name || "", company_id: cmp?.id || "" }));
                                    }}
                                    data-testid={`edit-variant-company-${v.id}`}
                                  >
                                    <option value="">Seleziona compagnia…</option>
                                    {/* Se la compagnia corrente non è in lista, mostra come opzione "custom" */}
                                    {editVariantDraft.company && !editVariantDraft.company_id && (
                                      <option value="__custom__">{editVariantDraft.company} (manuale)</option>
                                    )}
                                    {getCompaniesForBroker(editVariantDraft.broker_id).map((c) => (
                                      <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                    <option value="__other__">➕ Altro / inserisci manualmente…</option>
                                  </select>
                                ) : (
                                  <>
                                    <Input
                                      className="flex-1 min-w-0"
                                      placeholder="Compagnia (manuale)"
                                      value={editVariantDraft.company}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const cid = findCompanyIdByName(val, editVariantDraft.broker_id);
                                        setEditVariantDraft((d) => ({ ...d, company: val, company_id: cid }));
                                      }}
                                      data-testid={`edit-variant-company-${v.id}`}
                                    />
                                    <button
                                      type="button"
                                      className="px-2 text-xs text-slate-500 hover:text-slate-800 border border-slate-300 rounded"
                                      onClick={() => {
                                        setEditVariantCompanyMode("select");
                                        setEditVariantDraft((d) => ({ ...d, company: "", company_id: "" }));
                                      }}
                                      title="Torna alla lista compagnie"
                                      data-testid={`edit-variant-company-back-to-select-${v.id}`}
                                    >
                                      ↺
                                    </button>
                                  </>
                                )}
                              </div>
                              {(() => {
                                // 🆕 Se il piano rateale personalizzato è VALIDO (tutte rate > 0, non uniformi),
                                // disabilita "Premio per rata" e mostra il TOTALE calcolato dalla somma.
                                const _fraz = editVariantDraft.frazionamento;
                                const _rateN = parseInt(editVariantDraft.installments_count, 10) || 1;
                                const _amts = Array.isArray(editVariantDraft.installments_amounts)
                                  ? editVariantDraft.installments_amounts.map((x) => Number(x) || 0)
                                  : [];
                                const _customValid =
                                  _fraz && _fraz !== "annuale" && _rateN >= 2 && _amts.length === _rateN &&
                                  _amts.every((a) => a > 0) && !_amts.every((a) => Math.abs(a - _amts[0]) < 0.005);
                                if (_customValid) {
                                  const _sum = _amts.reduce((s, a) => s + a, 0);
                                  return (
                                    <div className="sm:col-span-2 min-w-0" data-testid={`edit-variant-total-locked-${v.id}`}>
                                      <div className="h-9 flex items-center justify-between gap-2 px-2.5 border border-amber-300 bg-amber-50 rounded text-sm">
                                        <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">🔒 Totale piano</span>
                                        <span className="tabular-nums font-bold text-amber-900">{formatCurrency(_sum)}</span>
                                      </div>
                                      <p className="text-[9px] text-amber-700 mt-0.5 leading-tight">Calcolato dalla somma delle rate personalizzate</p>
                                    </div>
                                  );
                                }
                                return (
                                  <div className="sm:col-span-2 min-w-0 flex flex-col gap-0.5" data-testid={`edit-variant-amount-wrap-${v.id}`}>
                                    <MoneyInput
                                      placeholder="Premio PER RATA €"
                                      value={editVariantDraft.amount}
                                      onChange={(val) => setEditVariantDraft((d) => ({ ...d, amount: val }))}
                                      data-testid={`edit-variant-amount-${v.id}`}
                                    />
                                    {(() => {
                                      // 🆕 09/07/2026 — Mostra il TOTALE variante come badge sotto al campo
                                      // "Premio per rata" per evitare confusione: molti utenti pensano
                                      // che il campo mostri il totale, quindi qui esplicitiamo entrambi.
                                      const _perRata = Number(editVariantDraft.amount) || 0;
                                      const _rateN2 = parseInt(editVariantDraft.installments_count, 10) || 1;
                                      const _tot = Math.round(_perRata * _rateN2 * 100) / 100;
                                      if (_tot <= 0) return null;
                                      return (
                                        <div className="text-[10px] text-blue-800 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 leading-tight" data-testid={`edit-variant-total-info-${v.id}`}>
                                          🔢 Totale variante: <strong className="tabular-nums">{formatCurrency(_tot)}</strong>
                                          <span className="text-blue-600 ml-1">({_rateN2} × {formatCurrency(_perRata)})</span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })()}
                              <Input
                                className="sm:col-span-4 min-w-0"
                                placeholder="Note"
                                value={editVariantDraft.notes}
                                onChange={(e) => setEditVariantDraft((d) => ({ ...d, notes: e.target.value }))}
                                data-testid={`edit-variant-notes-${v.id}`}
                              />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Frazionamento</label>
                                <select
                                  className="w-full h-9 px-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                  value={editVariantDraft.frazionamento || ""}
                                  onChange={(e) => {
                                    const nv = e.target.value;
                                    setEditVariantDraft((d) => {
                                      // Se l'utente non ha mai personalizzato le rate (valore vuoto o coincide con un default noto), aggiorna al nuovo default
                                      const cur = String(d.installments_count || "").trim();
                                      const useDefault = cur === "" || ["1", "2", "3", "4", "12"].includes(cur);
                                      const def = FRAZ_RATE_MAP[nv];
                                      return {
                                        ...d,
                                        frazionamento: nv,
                                        installments_count: useDefault && def ? String(def) : d.installments_count,
                                      };
                                    });
                                  }}
                                  data-testid={`edit-variant-frazionamento-${v.id}`}
                                >
                                  <option value="">— eredita dall&apos;offerta —</option>
                                  <option value="annuale">Annuale</option>
                                  <option value="semestrale">Semestrale</option>
                                  <option value="quadrimestrale">Quadrimestrale</option>
                                  <option value="trimestrale">Trimestrale</option>
                                  <option value="mensile">Mensile</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">N° rate</label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={60}
                                  className="w-full h-9 text-center tabular-nums"
                                  placeholder="1"
                                  value={editVariantDraft.installments_count}
                                  onChange={(e) => setEditVariantDraft((d) => ({ ...d, installments_count: e.target.value.replace(/[^0-9]/g, "") }))}
                                  data-testid={`edit-variant-installments-count-${v.id}`}
                                  title="Numero di rate (default dal frazionamento, modificabile)"
                                />
                                {(() => {
                                  const perRate = parseFloat(editVariantDraft.amount);
                                  const raten = parseInt(editVariantDraft.installments_count, 10);
                                  if (!perRate || !Number.isFinite(raten) || raten < 1) return (
                                    // 🆕 Anche senza importo mostro il selettore di modalità (utile prima di iniziare)
                                    <div className="mt-1 flex flex-col gap-0.5" data-testid={`edit-variant-sum-mode-${v.id}`}>
                                      <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Nuove garanzie</span>
                                      <div className="flex flex-wrap gap-1">
                                        <button
                                          type="button"
                                          onClick={() => setEditVariantDraft((d) => ({ ...d, sum_mode: "add" }))}
                                          className={`h-6 px-1.5 text-[9.5px] rounded border whitespace-nowrap transition-colors ${
                                            editVariantEffectiveSumMode === "add"
                                              ? "bg-emerald-100 border-emerald-400 text-emerald-800 font-semibold"
                                              : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                                          }`}
                                          title="I premi delle nuove garanzie vengono SOMMATI al Premio per rata iniziale (default: attivo se le garanzie sono senza importo)"
                                          data-testid={`edit-variant-sum-mode-add-${v.id}`}
                                        >
                                          ➕ Somma
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setEditVariantDraft((d) => ({ ...d, sum_mode: "replace" }))}
                                          className={`h-6 px-1.5 text-[9.5px] rounded border whitespace-nowrap transition-colors ${
                                            editVariantEffectiveSumMode === "replace"
                                              ? "bg-indigo-100 border-indigo-400 text-indigo-800 font-semibold"
                                              : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                                          }`}
                                          title="Azzera il Premio per rata iniziale e ricalcola SOLO come somma delle nuove garanzie (default: attivo se le garanzie hanno già un importo)"
                                          data-testid={`edit-variant-sum-mode-replace-${v.id}`}
                                        >
                                          🔄 Solo aggiunte
                                        </button>
                                      </div>
                                    </div>
                                  );
                                  const total = perRate * raten;
                                  // 🆕 Confronto con la somma dei premi delle garanzie inserite
                                  const sumCovs = (editVariantDraft.coverages || []).reduce((s, c) => {
                                    const p = Number(c.premium);
                                    return s + (Number.isFinite(p) && p > 0 ? p : 0);
                                  }, 0);
                                  const hasCovs = sumCovs > 0;
                                  const diff = Math.round((total - sumCovs) * 100) / 100;
                                  const isMatch = hasCovs && Math.abs(diff) < 0.01;
                                  const isOver = hasCovs && diff > 0;
                                  const isUnder = hasCovs && diff < 0;
                                  return (
                                    <>
                                      {/* 🆕 Nascondi il badge "premio × rate = totale" quando c'è un piano rateale NON uniforme
                                          con tutte le rate > 0 (in quel caso `amount` contiene il totale del piano). */}
                                      {(() => {
                                        const _amts = Array.isArray(editVariantDraft.installments_amounts) ? editVariantDraft.installments_amounts.map((x) => Number(x) || 0) : [];
                                        const _allFilled = _amts.length >= 2 && _amts.every((a) => a > 0);
                                        const hasCustomPlan = _allFilled && !_amts.every((a) => Math.abs(a - _amts[0]) < 0.005);
                                        if (hasCustomPlan) return null;
                                        return (
                                          <div
                                            className="mt-1 text-[10px] text-blue-800 tabular-nums font-medium leading-tight"
                                            data-testid={`edit-variant-installments-calc-${v.id}`}
                                          >
                                            🧮 {formatCurrency(perRate)} × {raten} = <strong>{formatCurrency(total)}</strong>
                                          </div>
                                        );
                                      })()}
                                      {/* 🆕 Selettore modalità: come aggiungere le nuove garanzie al Premio per rata */}
                                      <div className="mt-1 flex flex-col gap-0.5" data-testid={`edit-variant-sum-mode-${v.id}`}>
                                        <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Nuove garanzie</span>
                                        <div className="flex flex-wrap gap-1">
                                          <button
                                            type="button"
                                            onClick={() => setEditVariantDraft((d) => ({ ...d, sum_mode: "add" }))}
                                            className={`h-6 px-1.5 text-[9.5px] rounded border whitespace-nowrap transition-colors ${
                                              editVariantEffectiveSumMode === "add"
                                                ? "bg-emerald-100 border-emerald-400 text-emerald-800 font-semibold"
                                                : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                                            }`}
                                            title="I premi delle nuove garanzie vengono SOMMATI al Premio per rata iniziale (default: attivo se le garanzie sono senza importo)"
                                            data-testid={`edit-variant-sum-mode-add-${v.id}`}
                                          >
                                            ➕ Somma
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              // 🆕 Applica immediatamente: azzera + calcola solo dalle aggiunte
                                              setEditVariantDraft((d) => {
                                                const rate2 = parseInt(d.installments_count, 10);
                                                const rate = Number.isFinite(rate2) && rate2 >= 1 ? rate2 : 1;
                                                const sumAdded = (d.coverages || []).reduce((s, c) => {
                                                  // 🛠 Fix: include tutte le garanzie visibili, non solo quelle aggiunte in sessione.
                                                  // Prima escludeva le coverage caricate da server (senza _added_in_session).
                                                  if (c.client_visible === false) return s;
                                                  const p = Number(c.premium);
                                                  return s + (Number.isFinite(p) && p > 0 ? p : 0);
                                                }, 0);
                                                const newPerRata = +(sumAdded / rate).toFixed(2);
                                                setTimeout(() => toast.info(
                                                  `🔄 Premio per rata ricalcolato: ${formatCurrency(newPerRata)} (Σ garanzie ${formatCurrency(sumAdded)} ÷ ${rate})`,
                                                  { duration: 4000 }
                                                ), 0);
                                                return { ...d, sum_mode: "replace", amount: String(newPerRata) };
                                              });
                                            }}
                                            className={`h-6 px-1.5 text-[9.5px] rounded border whitespace-nowrap transition-colors ${
                                              editVariantEffectiveSumMode === "replace"
                                                ? "bg-indigo-100 border-indigo-400 text-indigo-800 font-semibold"
                                                : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                                            }`}
                                            title="Azzera il Premio per rata iniziale e ricalcola SOLO come somma delle nuove garanzie aggiunte (default: attivo se le garanzie hanno già un importo)"
                                            data-testid={`edit-variant-sum-mode-replace-${v.id}`}
                                          >
                                            🔄 Solo aggiunte
                                          </button>
                                        </div>
                                      </div>
                                      {hasCovs && (
                                        <div
                                          className={`mt-0.5 text-[10px] tabular-nums font-medium leading-tight px-1 py-0.5 rounded border ${
                                            isMatch ? "bg-emerald-50 border-emerald-300 text-emerald-800" :
                                            isOver ? "bg-amber-50 border-amber-300 text-amber-800" :
                                            "bg-rose-50 border-rose-300 text-rose-800"
                                          }`}
                                          data-testid={`edit-variant-cov-sum-badge-${v.id}`}
                                          title={isMatch ? "Somma garanzie coincide con il totale variante." :
                                            isOver ? `Il totale variante supera la somma delle garanzie di ${formatCurrency(Math.abs(diff))}. Verifica se manca qualche garanzia.` :
                                            `La somma delle garanzie supera il totale variante di ${formatCurrency(Math.abs(diff))}. Auto-fill dovrebbe correggere in automatico.`}
                                        >
                                          {isMatch ? "✓" : isOver ? "⚠" : "!"} Σ garanzie: {formatCurrency(sumCovs)} {!isMatch && (isOver ? `(premio iniziale è di ${formatCurrency(Math.abs(diff))})` : `(supera di ${formatCurrency(Math.abs(diff))})`)}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1.5">
                                  Decorrenza polizza
                                  {linkedQuoteStart && editVariantDraft.effective_date && editVariantDraft.effective_date !== linkedQuoteStart && (
                                    <span className="normal-case text-[9px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1" title={`Preventivo: ${new Date(linkedQuoteStart).toLocaleDateString("it-IT")}`}>⚠ diversa dal preventivo</span>
                                  )}
                                </label>
                                <DatePicker value={editVariantDraft.effective_date || ""}
                                  onChange={(e) => setEditVariantDraft((d) => ({
                                    ...d,
                                    effective_date: e.target.value,
                                    // 🆕 Feb 2026 — Auto-ricalcola SEMPRE la scadenza a +1 anno quando si cambia la decorrenza
                                    expiry_date: e.target.value ? addOneYearIso(e.target.value) : d.expiry_date,
                                  }))}
                                  data-testid={`edit-variant-effective-date-${v.id}`} />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1.5">
                                  Scadenza polizza
                                  {linkedQuoteEnd && editVariantDraft.expiry_date && editVariantDraft.expiry_date !== linkedQuoteEnd ? (
                                    <span className="normal-case text-[9px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1" title={`Preventivo: ${new Date(linkedQuoteEnd).toLocaleDateString("it-IT")}`}>⚠ diversa dal preventivo</span>
                                  ) : (
                                    <span className="text-[9px] font-normal normal-case text-slate-500">(auto: +1 anno)</span>
                                  )}
                                </label>
                                <DatePicker value={editVariantDraft.expiry_date || ""}
                                  onChange={(e) => setEditVariantDraft((d) => ({ ...d, expiry_date: e.target.value }))}
                                  data-testid={`edit-variant-expiry-date-${v.id}`} />
                              </div>
                              {/* 🆕 Tipo guida per variante (default: eredita dall'offerta) */}
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Tipo guida</label>
                                <select
                                  className="w-full h-9 px-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                  value={editVariantDraft.driving_type || ""}
                                  onChange={(e) => setEditVariantDraft((d) => ({ ...d, driving_type: e.target.value }))}
                                  data-testid={`edit-variant-driving-type-${v.id}`}
                                >
                                  <option value="">— eredita dall&apos;offerta —</option>
                                  <option value="esclusiva">Esclusiva</option>
                                  <option value="esperta">Esperta</option>
                                  <option value="libera">Libera</option>
                                </select>
                              </div>
                            </div>

                            {/* 📦 Prodotto (catalogo) RIMOSSO dalle varianti 09/07/2026 — resta solo consultabile dalla pagina /agency/products */}
                            {/* 🆕 Rate personalizzate: mostra editor N importi se frazionamento ≠ annuale e rate > 1 */}
                            {(() => {
                              const raten = parseInt(editVariantDraft.installments_count, 10) || 1;
                              const frazNotAnnual = editVariantDraft.frazionamento && editVariantDraft.frazionamento !== "annuale";
                              if (!frazNotAnnual || raten < 2) return null;
                              const perRate = parseFloat(editVariantDraft.amount) || 0;
                              const amounts = Array.isArray(editVariantDraft.installments_amounts) && editVariantDraft.installments_amounts.length === raten
                                ? editVariantDraft.installments_amounts
                                : Array(raten).fill(perRate);
                              const sumAmts = amounts.reduce((s, a) => s + (Number(a) || 0), 0);
                              return (
                                <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2" data-testid={`edit-variant-installments-panel-${v.id}`}>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                                      💶 Importi per rata ({raten} rate {editVariantDraft.frazionamento})
                                    </span>
                                    <div className="flex gap-1 items-center">
                                      <span className="text-[10px] text-amber-800 tabular-nums">Σ rate: <strong>{formatCurrency(sumAmts)}</strong></span>
                                      <Button
                                        type="button" size="sm" variant="outline" className="h-6 text-[9.5px] px-2"
                                        onClick={() => setEditVariantDraft((d) => ({ ...d, installments_amounts: Array(raten).fill(parseFloat(d.amount) || 0) }))}
                                        title="Ripristina importi uguali basati sul 'Premio per rata'"
                                        data-testid={`edit-variant-installments-reset-${v.id}`}
                                      >
                                        🔄 Uguali
                                      </Button>
                                    </div>
                                  </div>
                                  {/* 🆕 Warning inline: piano incompleto */}
                                  {(() => {
                                    const nums = amounts.map((x) => Number(x) || 0);
                                    const allFilled = nums.length > 0 && nums.every((x) => x > 0);
                                    const anyFilled = nums.some((x) => x > 0);
                                    if (allFilled || !anyFilled) return null;
                                    return (
                                      <div
                                        className="mb-1.5 text-[10px] text-rose-800 bg-rose-50 border border-rose-300 rounded px-1.5 py-1 leading-tight"
                                        data-testid={`edit-variant-installments-incomplete-${v.id}`}
                                      >
                                        ⚠ <strong>Piano incompleto</strong>: compila TUTTE le rate (&gt; 0) per attivare il piano personalizzato. Con importi mancanti si applica il piano uniforme del &quot;Premio per rata&quot;.
                                      </div>
                                    );
                                  })()}
                                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                                    {Array.from({ length: raten }).map((_, i) => (
                                      <div key={i} className="flex flex-col gap-0.5">
                                        <label className="text-[9px] text-amber-700 font-semibold">{i + 1}° rata</label>
                                        <MoneyInput
                                          className="h-8"
                                          value={amounts[i] ?? ""}
                                          placeholder="0,00"
                                          onChange={(rawVal) => {
                                            const next = [...amounts];
                                            next[i] = rawVal;
                                            // 🆕 NON sovrascrivere `amount` (=Premio per rata). Il totale della variante
                                            // viene calcolato dalla somma delle rate al momento del salvataggio.
                                            setEditVariantDraft((d) => ({
                                              ...d,
                                              installments_amounts: next,
                                            }));
                                          }}
                                          data-testid={`edit-variant-installment-amount-${v.id}-${i}`}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                  <p className="text-[9.5px] text-amber-700 mt-1">
                                    💡 Modifica gli importi solo se le rate hanno costi DIVERSI (es. 1° rata €100, 2° rata €80). Altrimenti clicca &quot;Uguali&quot; per riportarle al valore del &quot;Premio per rata&quot;.
                                  </p>
                                </div>
                              );
                            })()}
                            {/* 🆕 Editor garanzie della variante */}
                            <div className="mt-3 rounded-md bg-white border border-blue-200 p-2">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">
                                  Garanzie variante ({(editVariantDraft.coverages || []).length})
                                </span>
                                <div className="flex gap-1">
                                  <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => addEditVariantCov("coverage")} data-testid={`edit-variant-add-cov-${v.id}`}>
                                    + Garanzia
                                  </Button>
                                  <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => addEditVariantCov("line_item")} data-testid={`edit-variant-add-item-${v.id}`}>
                                    + Voce accessoria
                                  </Button>
                                </div>
                              </div>
                              {(editVariantDraft.coverages || []).length === 0 && (
                                <p className="text-[11px] text-slate-500 italic px-1 py-2">Nessuna garanzia. Aggiungi le voci che compongono il premio della variante (es. RCA €450, Furto €80, Cristalli €20).</p>
                              )}
                              {/* 🆕 Didascalie colonne (visibili solo se ci sono righe) */}
                              {(editVariantDraft.coverages || []).length > 0 && (
                                <div className="hidden sm:grid grid-cols-12 gap-1.5 px-1 pb-1 border-b border-blue-100 mb-1 text-[9px] uppercase tracking-wider text-slate-500 font-semibold" data-testid={`edit-variant-cov-headers-${v.id}`}>
                                  <div className="col-span-3">Nome garanzia</div>
                                  <div className="col-span-2 text-center">Massimale (€)</div>
                                  <div className="col-span-2 text-center leading-tight">
                                    Premio (€)
                                    {(() => {
                                      // 🆕 10/07/2026 — Mostra indicazione del frazionamento per chiarire che l'importo è per rata.
                                      const fr = editVariantDraft.frazionamento || "annuale";
                                      const rateN = parseInt(editVariantDraft.installments_count, 10) || (FRAZ_RATE_MAP[fr] || 1);
                                      const label = fr === "annuale" || rateN <= 1 ? "annuo" : `per rata (${fr}${rateN >= 2 ? ` · ${rateN} rate` : ""})`;
                                      return (
                                        <><br/><span className="font-normal normal-case text-[8px] text-blue-600">{label}</span></>
                                      );
                                    })()}
                                  </div>
                                  <div className="col-span-2 text-center">Franchigia</div>
                                  <div className="col-span-2 text-center leading-tight">Visibilità cliente<br/><span className="font-normal normal-case text-[8px] text-slate-400">Garanzia · Prezzo</span></div>
                                  <div className="col-span-1 text-center">Rimuovi</div>
                                </div>
                              )}
                              {(editVariantDraft.coverages || []).map((c, ci) => (
                                <div key={ci} className="grid grid-cols-1 sm:grid-cols-12 gap-1.5 mb-1.5 items-center" data-testid={`edit-variant-cov-row-${v.id}-${ci}`}>
                                  {/* 🆕 Feb 2026 — Badge/chip garanzia non collegata + etichetta originale AI */}
                                  {(c._unmatched || (c.extracted_name && c.extracted_name !== c.name)) && (
                                    <div className="sm:col-span-12 flex items-center gap-1.5 flex-wrap -mb-0.5">
                                      {c._unmatched && (
                                        <span
                                          data-testid={`edit-variant-cov-unmatched-${v.id}-${ci}`}
                                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-300 px-1.5 py-0.5 rounded"
                                          title="Garanzia non collegata al listino compagnia. Aggiungi un alias sulla voce master."
                                        >
                                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500" />
                                          Non collegata
                                        </span>
                                      )}
                                      {c.extracted_name && c.extracted_name !== c.name && (
                                        <span
                                          data-testid={`edit-variant-cov-extracted-${v.id}-${ci}`}
                                          className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-600 border border-slate-300 px-1.5 py-0.5 rounded max-w-[240px]"
                                          title={`Etichetta originale estratta dal PDF: "${c.extracted_name}"`}
                                        >
                                          📄 <span className="truncate">{c.extracted_name}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <div className="sm:col-span-3 flex flex-col gap-0.5">
                                    {c.kind === "line_item" && (lineItems || []).length > 0 && (() => {
                                      const knownLi = (lineItems || []).find((a) => a.name === c.name);
                                      const isCustomLi = c._manual === true || (!!c.name && !knownLi);
                                      return (
                                        <select
                                          className="h-7 px-2 text-[11px] border border-indigo-300 bg-indigo-50 text-indigo-800 rounded font-medium focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                          value={isCustomLi ? "__custom__" : (knownLi?.id || "")}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "__custom__") {
                                              // Modalità manuale: pulisci nome e attiva flag per far apparire l'Input libero
                                              updateEditVariantCov(ci, "name", "");
                                              updateEditVariantCov(ci, "_manual", true);
                                              return;
                                            }
                                            if (!val) {
                                              updateEditVariantCov(ci, "name", "");
                                              updateEditVariantCov(ci, "_manual", false);
                                              return;
                                            }
                                            const preset = (lineItems || []).find((a) => a.id === val);
                                            if (!preset) return;
                                            updateEditVariantCov(ci, "name", preset.name);
                                            updateEditVariantCov(ci, "_manual", false);
                                            if (preset.default_premium != null) updateEditVariantCov(ci, "premium", String(preset.default_premium));
                                            if (preset.description) updateEditVariantCov(ci, "description", preset.description);
                                          }}
                                          data-testid={`var-line-item-select-${v.id}-${ci}`}
                                          title="Seleziona una voce accessoria dall'elenco (o scegli 'Altro' per inserimento manuale)"
                                        >
                                          <option value="">📋 Scegli voce…</option>
                                          {(lineItems || []).filter((a) => a.is_active !== false).map((a) => (
                                            <option key={a.id} value={a.id}>
                                              {a.name}{a.default_premium != null ? ` — ${formatCurrency(a.default_premium)}` : ""}
                                            </option>
                                          ))}
                                          <option value="__custom__">➕ Altro / manuale</option>
                                        </select>
                                      );
                                    })()}
                                    {/* 🆕 Per garanzie: dropdown dalla lista "Garanzie Polizze" (lookup lists) con fallback manuale */}
                                    {c.kind !== "line_item" && (policyCoverages || []).length > 0 && (() => {
                                      const known = (policyCoverages || []).find((p) => (p.label === c.name) || (p.code && p.code === c.code));
                                      const isCustom = c._manual === true || (!!c.name && !known);
                                      return (
                                        <select
                                          className="h-7 px-2 text-[11px] border border-blue-300 bg-blue-50 text-blue-800 rounded font-medium focus:outline-none focus:ring-2 focus:ring-blue-300"
                                          value={isCustom ? "__custom__" : (known?.code || "")}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "__custom__") {
                                              // Modalità manuale: pulisci nome/codice e attiva flag per far apparire l'Input libero
                                              updateEditVariantCov(ci, "name", "");
                                              updateEditVariantCov(ci, "code", null);
                                              updateEditVariantCov(ci, "_manual", true);
                                              return;
                                            }
                                            if (!val) {
                                              updateEditVariantCov(ci, "name", "");
                                              updateEditVariantCov(ci, "code", null);
                                              updateEditVariantCov(ci, "_manual", false);
                                              return;
                                            }
                                            const preset = (policyCoverages || []).find((p) => p.code === val);
                                            if (!preset) return;
                                            updateEditVariantCov(ci, "name", preset.label || preset.code);
                                            updateEditVariantCov(ci, "code", preset.code || null);
                                            updateEditVariantCov(ci, "_manual", false);
                                            if (preset.description && !c.description) {
                                              updateEditVariantCov(ci, "description", preset.description);
                                            }
                                            if (preset.default_sum_insured != null && (c.sum_insured === "" || c.sum_insured == null)) {
                                              updateEditVariantCov(ci, "sum_insured", String(preset.default_sum_insured));
                                            }
                                            if (preset.default_deductible != null && !c.deductible) {
                                              updateEditVariantCov(ci, "deductible", String(preset.default_deductible));
                                            }
                                          }}
                                          data-testid={`edit-variant-cov-name-select-${v.id}-${ci}`}
                                          title="Seleziona una garanzia dalla lista preventivo (o scegli 'Altro' per inserimento manuale)"
                                        >
                                          <option value="">🛡️ Scegli garanzia…</option>
                                          {sortedPolicyCoveragesForType.map((p) => (
                                            <option key={p.code} value={p.code}>
                                              {p.label || p.code}
                                            </option>
                                          ))}
                                          <option value="__custom__">➕ Altro / manuale</option>
                                        </select>
                                      );
                                    })()}
                                    {/* 🆕 Input libero: mostrato in modalità manuale (custom).
                                        - Se `_manual === true` (dopo click su "Altro / manuale"): SEMPRE visibile.
                                        - Altrimenti: solo se il nome NON matcha un preset (retrocompatibilità legacy). */}
                                    {(() => {
                                      if (c._manual === true) return true;
                                      if (c.kind === "line_item") {
                                        const knownLi = (lineItems || []).find((a) => a.name === c.name);
                                        return !!c.name && !knownLi;
                                      }
                                      return !((policyCoverages || []).find((p) => (p.label === c.name) || (p.code && p.code === c.code))) && !!c.name;
                                    })() && (
                                      <div className="flex gap-1 items-center">
                                        <Input
                                          className="h-7 text-xs flex-1"
                                          placeholder={c.kind === "line_item" ? "Nome voce (manuale)" : "Nome garanzia (manuale)"}
                                          value={c.name}
                                          onChange={(e) => {
                                            const txt = e.target.value;
                                            updateEditVariantCov(ci, "name", txt);
                                            if (c.kind !== "line_item") {
                                              const preset = (policyCoverages || []).find((p) => p.label === txt);
                                              if (preset) {
                                                updateEditVariantCov(ci, "code", preset.code || null);
                                                if (preset.description && !c.description) {
                                                  updateEditVariantCov(ci, "description", preset.description);
                                                }
                                              }
                                            }
                                          }}
                                          data-testid={`edit-variant-cov-name-input-${v.id}-${ci}`}
                                        />
                                        {c.kind === "line_item" ? (
                                          <button
                                            type="button"
                                            className="h-7 px-1.5 text-[11px] rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 shrink-0"
                                            title="Salva questa voce nell'elenco Voci accessorie per riutilizzarla nelle prossime offerte"
                                            data-testid={`edit-variant-save-lineitem-${v.id}-${ci}`}
                                            onClick={() => saveLineItemToList({ name: c.name, default_premium: c.premium, description: c.description })}
                                            disabled={!c.name || !String(c.name).trim()}
                                          >
                                            💾
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="h-7 px-1.5 text-[11px] rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 shrink-0"
                                            title="Salva questa garanzia nell'elenco Garanzie Polizze per riutilizzarla nelle prossime offerte"
                                            data-testid={`edit-variant-save-coverage-${v.id}-${ci}`}
                                            onClick={() => savePolicyCoverageToList({ label: c.name, description: c.description, sum_insured: c.sum_insured, deductible: c.deductible })}
                                            disabled={!c.name || !String(c.name).trim()}
                                          >
                                            💾
                                          </button>
                                        )}
                                      </div>
                                    )}
                                    {/* 🆕 Compagnia per singola garanzia (utile per broker con più compagnie) */}
                                    <select
                                      className={`h-6 px-1.5 text-[10px] rounded border font-medium focus:outline-none focus:ring-1 focus:ring-purple-300 ${
                                        c.company_id
                                          ? "border-purple-300 bg-purple-50 text-purple-800"
                                          : "border-slate-200 bg-slate-50 text-slate-500"
                                      }`}
                                      value={c.company_id || ""}
                                      onChange={(e) => {
                                        const cid = e.target.value || null;
                                        updateEditVariantCov(ci, "company_id", cid);
                                        const meta = (companies || []).find((x) => x.id === cid);
                                        updateEditVariantCov(ci, "company_name", meta?.name || null);
                                      }}
                                      title={c.company_id ? `Questa garanzia è collocata con ${c.company_name || "un'altra compagnia"}` : `Se null, eredita la compagnia della variante (${editVariantDraft.company || "—"})`}
                                      data-testid={`edit-variant-cov-company-${v.id}-${ci}`}
                                    >
                                      <option value="">🏢 stessa variante</option>
                                      {(companies || []).map((co) => (
                                        <option key={co.id} value={co.id}>{co.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <MoneyInput
                                    className="sm:col-span-2 h-7 text-xs"
                                    placeholder="Massimale €"
                                    value={c.sum_insured ?? ""}
                                    onChange={(val) => updateEditVariantCov(ci, "sum_insured", val)}
                                  />
                                  <MoneyInput
                                    className="sm:col-span-2 h-7 text-xs"
                                    placeholder={(() => {
                                      const fr = editVariantDraft.frazionamento || "annuale";
                                      const rateN = parseInt(editVariantDraft.installments_count, 10) || (FRAZ_RATE_MAP[fr] || 1);
                                      return fr === "annuale" || rateN <= 1 ? "Premio annuo €" : `Premio per rata € (${fr})`;
                                    })()}
                                    value={c.premium ?? ""}
                                    onChange={(val) => updateEditVariantCov(ci, "premium", val)}
                                  />
                                  <Input
                                    className="sm:col-span-2 h-7 text-xs"
                                    placeholder="Franchigia"
                                    value={c.deductible || ""}
                                    onChange={(e) => updateEditVariantCov(ci, "deductible", e.target.value)}
                                  />
                                  <div className="sm:col-span-2 flex flex-col gap-0.5" data-testid={`edit-variant-cov-visibility-${v.id}-${ci}`}>
                                    <label
                                      className={`h-6 px-2 inline-flex items-center justify-center gap-1 text-[10px] rounded border cursor-pointer select-none ${
                                        c.client_visible !== false
                                          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                          : "border-slate-300 bg-slate-100 text-slate-600 line-through"
                                      }`}
                                      title={c.client_visible !== false ? "Riga visibile al cliente (email/PDF)" : "Riga NASCOSTA al cliente"}
                                      data-testid={`edit-variant-cov-visible-${v.id}-${ci}`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="h-3 w-3"
                                        checked={c.client_visible !== false}
                                        onChange={(e) => updateEditVariantCov(ci, "client_visible", e.target.checked)}
                                      />
                                      {c.client_visible !== false ? "👁️ Garanzia" : "🚫 Nascosta"}
                                    </label>
                                    <label
                                      className={`h-6 px-2 inline-flex items-center justify-center gap-1 text-[10px] rounded border cursor-pointer select-none ${
                                        c.client_visible === false
                                          ? "border-slate-200 bg-slate-50 text-slate-400"
                                          : c.client_price_visible !== false
                                            ? "border-blue-300 bg-blue-50 text-blue-700"
                                            : "border-amber-300 bg-amber-50 text-amber-700 line-through"
                                      }`}
                                      title={
                                        c.client_visible === false
                                          ? "La riga è nascosta: la visibilità del prezzo non ha effetto"
                                          : c.client_price_visible !== false
                                            ? "Importo (premio) visibile al cliente"
                                            : "Solo la garanzia sarà visibile, il premio verrà OMESSO nel PDF/email"
                                      }
                                      data-testid={`edit-variant-cov-price-visible-${v.id}-${ci}`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="h-3 w-3"
                                        checked={c.client_price_visible !== false}
                                        disabled={c.client_visible === false}
                                        onChange={(e) => updateEditVariantCov(ci, "client_price_visible", e.target.checked)}
                                      />
                                      {c.client_price_visible !== false ? "💰 Prezzo" : "🚫 Prezzo"}
                                    </label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeEditVariantCov(ci)}
                                    className="sm:col-span-1 h-7 inline-flex items-center justify-center rounded border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-700"
                                    title="Rimuovi"
                                    data-testid={`edit-variant-cov-remove-${v.id}-${ci}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-end gap-1.5 mt-2">
                              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingVariantId(null)}>Annulla</Button>
                              <Button type="button" size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={saveEditVariant} data-testid={`save-variant-edit-${v.id}`}>Salva</Button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={v.id}
                          data-testid={`offer-variant-${v.id}`}
                          className={`rounded-lg border-2 p-3 ${
                            isWinner ? "border-emerald-500 bg-emerald-50" : isCheapest ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"
                          } ${v.is_featured ? "ring-2 ring-violet-500 ring-offset-1 border-l-[6px] border-l-violet-600" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-x-3 gap-y-1.5 flex-wrap">
                            <div className="contents">
                              <div className="order-1 flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                                <Checkbox
                                  checked={selectedVariantIds.has(v.id)}
                                  onCheckedChange={() => toggleVariantSelected(v.id)}
                                  data-testid={`variant-select-${v.id}`}
                                  aria-label={`Seleziona ${v.company}`}
                                />
                                {v.is_featured && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-600 text-white px-2 py-0.5 rounded" data-testid={`variant-featured-badge-${v.id}`}>⭐ In evidenza</span>
                                )}
                                {isCheapest && !isWinner && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded">🥇 Migliore</span>
                                )}
                                {isWinner && (
                                  <>
                                    <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded" data-testid={`variant-winner-${v.id}`}>🏆 Finalizzata</span>
                                    {/* 🆕 Pulsante per togliere la finalizzazione */}
                                    <button
                                      type="button"
                                      onClick={unfinalizeVariant}
                                      className="text-[10px] font-semibold uppercase tracking-wider bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 px-1.5 py-0.5 rounded"
                                      data-testid={`unfinalize-variant-${v.id}`}
                                      title="Togli finalizzazione e ripristina lo stato di confronto varianti"
                                    >
                                      ✖ Togli
                                    </button>
                                  </>
                                )}
                                <strong className="text-base">{v.company}</strong>
                              </div>
                              <div
                                className="order-3 basis-full flex items-center gap-x-2 flex-nowrap overflow-x-auto whitespace-nowrap text-[11px] text-slate-700 pb-0.5"
                                data-testid={`variant-compact-row-${v.id}`}
                              >
                                {v.created_at && (
                                  <span
                                    className="text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-300 px-1.5 py-0.5 rounded tabular-nums"
                                    title={`Inserita il ${formatDateTimeSeconds(v.created_at)}`}
                                    data-testid={`variant-created-at-${v.id}`}
                                  >
                                    🕒 {formatDateTimeSeconds(v.created_at)}
                                  </span>
                                )}
                                {(() => {
                                  // 🆕 Feb 2026 — Badge Tipo di guida per variante (con fallback ereditato da offerta/preventivo)
                                  const dt = (v.driving_type || form.driving_type || linkedQuote?.details?.driving_type || "").toLowerCase();
                                  if (!dt) return null;
                                  const CFG = {
                                    esclusiva: { label: "Guida Esclusiva", cls: "bg-violet-100 text-violet-800 border-violet-300", icon: "🔒" },
                                    esperta: { label: "Guida Esperta", cls: "bg-blue-100 text-blue-800 border-blue-300", icon: "🎓" },
                                    libera: { label: "Guida Libera", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: "🚗" },
                                  };
                                  const cfg = CFG[dt];
                                  if (!cfg) return null;
                                  const inherited = !v.driving_type;
                                  return (
                                    <span
                                      className={`text-[10px] font-semibold uppercase tracking-wide border px-1.5 py-0.5 rounded ${cfg.cls}`}
                                      title={inherited ? `${cfg.label} (ereditato dal preventivo)` : cfg.label}
                                      data-testid={`variant-driving-type-badge-${v.id}`}
                                    >
                                      {cfg.icon} {cfg.label}{inherited && <span className="ml-1 opacity-70 normal-case font-normal">(ered.)</span>}
                                    </span>
                                  );
                                })()}
                                {v.broker_name && (
                                  <span
                                    className="text-[10px] font-medium bg-indigo-100 text-indigo-800 border border-indigo-300 px-1.5 py-0.5 rounded"
                                    title="Broker / Agenzia partner"
                                    data-testid={`variant-broker-badge-${v.id}`}
                                  >
                                    🤝 {v.broker_name}
                                  </span>
                                )}
                                {(() => {
                                  // 🆕 Costo principale mostrato = PREMIO PER RATA (il totale rimane in badge sotto)
                                  const raten = parseInt(v.installments_count, 10);
                                  const amt = parseFloat(v.amount);
                                  const validRate = Number.isFinite(raten) && raten >= 1;
                                  const validAmt = Number.isFinite(amt) && amt > 0;
                                  // 🆕 Se ci sono rate personalizzate NON uniformi E tutte > 0, mostra il TOTALE (somma rate) invece di amt/rate.
                                  const _amts = Array.isArray(v.installments_amounts) ? v.installments_amounts.map((x) => Number(x) || 0) : [];
                                  const _allFilled = _amts.length >= 2 && _amts.every((a) => a > 0);
                                  const hasCustomPlan = _allFilled && !_amts.every((a) => Math.abs(a - _amts[0]) < 0.005);
                                  const _sumPlan = hasCustomPlan ? _amts.reduce((s, a) => s + a, 0) : 0;
                                  const perRate = hasCustomPlan ? _sumPlan : (validAmt && validRate ? amt / raten : amt);
                                  return (
                                    <span
                                      className="text-base font-bold tabular-nums text-blue-700"
                                      title={hasCustomPlan ? `Totale piano rateale (${_amts.length} rate diverse)` : (validRate ? `Premio per rata (totale ${formatCurrency(amt)})` : "Premio")}
                                      data-testid={`variant-per-rate-main-${v.id}`}
                                    >
                                      {formatCurrency(perRate)}
                                      {!hasCustomPlan && validRate && raten > 1 && <span className="text-[10px] font-normal text-slate-500 ml-1">/rata</span>}
                                      {hasCustomPlan && <span className="text-[10px] font-normal text-amber-700 ml-1">tot. piano</span>}
                                    </span>
                                  );
                                })()}
                                  {v.frazionamento && (
                                    <span title="Frazionamento variante">
                                      💳 <strong>Fraz.:</strong> {String(v.frazionamento).charAt(0).toUpperCase() + String(v.frazionamento).slice(1)}
                                    </span>
                                  )}
                                  {(() => {
                                    // 🆕 N° rate + importo per rata + calcolo (premio per rata × rate = totale)
                                    const raten = parseInt(v.installments_count, 10);
                                    const amt = parseFloat(v.amount);
                                    if (!Number.isFinite(raten) || raten < 1 || !Number.isFinite(amt) || amt <= 0) return null;
                                    // 🆕 Se ci sono rate personalizzate NON uniformi (tutte > 0), nascondi il badge "× rate = totale"
                                    // (fuorviante: il campo amount ora contiene il totale del piano). Mostra solo Piano rateale.
                                    const _amts = Array.isArray(v.installments_amounts) ? v.installments_amounts.map((x) => Number(x) || 0) : [];
                                    const _allFilled = _amts.length >= 2 && _amts.every((a) => a > 0);
                                    if (_allFilled && !_amts.every((a) => Math.abs(a - _amts[0]) < 0.005)) return null;
                                    // amt (backend) è il totale; ricalcolo il premio per rata per la label
                                    const perRate = Math.round((amt / raten) * 100) / 100;
                                    return (
                                      <span
                                        className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-blue-800 tabular-nums font-semibold"
                                        title={`Premio per rata ${formatCurrency(perRate)} × ${raten} rate = ${formatCurrency(amt)} totale`}
                                        data-testid={`variant-installments-calc-${v.id}`}
                                      >
                                        🧮 {formatCurrency(perRate)} × {raten} rate = <strong>{formatCurrency(amt)}</strong>
                                      </span>
                                    );
                                  })()}
                                  {/* 🆕 Feb 2026 — Badge coerenza: ✓ coerente / ⚠ Totale non coerente (click per fix) */}
                                  {(() => {
                                    const raten = parseInt(v.installments_count, 10);
                                    const amt = parseFloat(v.amount);
                                    if (!Number.isFinite(raten) || raten < 1 || !Number.isFinite(amt) || amt <= 0) return null;
                                    // Piano personalizzato non uniforme → la coerenza è definita diversamente, salta.
                                    const _amts = Array.isArray(v.installments_amounts) ? v.installments_amounts.map((x) => Number(x) || 0) : [];
                                    const _allFilled = _amts.length >= 2 && _amts.every((a) => a > 0);
                                    if (_allFilled && !_amts.every((a) => Math.abs(a - _amts[0]) < 0.005)) return null;
                                    // Σ di tutte le voci visibili (garanzie + accessorie es. commissioni broker), come nel dettaglio
                                    const sumCov = Math.round((v.coverages || []).reduce((s, c) => {
                                      if (c.client_visible === false) return s;
                                      const p = Number(c.premium) || 0;
                                      return s + (p > 0 ? p : 0);
                                    }, 0) * 100) / 100;
                                    if (sumCov <= 0) return null;
                                    const expectedTotal = Math.round(sumCov * raten * 100) / 100;
                                    const isCoherent = Math.abs(amt - expectedTotal) < 0.01;
                                    if (isCoherent) {
                                      return (
                                        <span
                                          className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-300 rounded px-1.5 py-0.5 text-emerald-800 font-semibold"
                                          title={`Totale coerente: Σ garanzie ${formatCurrency(sumCov)} × ${raten} rate = ${formatCurrency(expectedTotal)}`}
                                          data-testid={`variant-coherence-ok-${v.id}`}
                                        >
                                          ✓ coerente
                                        </span>
                                      );
                                    }
                                    return (
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (!window.confirm(`Il totale della variante (${formatCurrency(amt)}) non coincide con Σ garanzie × rate (${formatCurrency(expectedTotal)}).\n\nAggiorno il totale a ${formatCurrency(expectedTotal)}?`)) return;
                                          try {
                                            await api.post(`/quote-offers/${editing.id}/variants/${v.id}/fix-amount`);
                                            toast.success(`Totale aggiornato: ${formatCurrency(amt)} → ${formatCurrency(expectedTotal)}`);
                                            const refreshed = await api.get(`/quote-offers/${editing.id}`);
                                            setVariants(refreshed.data?.variants || []);
                                          } catch (e) { toast.error(formatApiError(e)); }
                                        }}
                                        className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 text-amber-900 font-semibold hover:bg-amber-100 cursor-pointer transition-colors"
                                        title={`Clic per allineare il totale: amount attuale ${formatCurrency(amt)} ≠ Σ garanzie ${formatCurrency(sumCov)} × ${raten} rate = ${formatCurrency(expectedTotal)}`}
                                        data-testid={`variant-coherence-fix-${v.id}`}
                                      >
                                        ⚠ amount ≠ Σcov × rate
                                      </button>
                                    );
                                  })()}
                                  {/* 🆕 Piano rateale: se ci sono rate DIVERSE, mostrale in dettaglio nella card */}
                                  {(() => {
                                    const amts = Array.isArray(v.installments_amounts) ? v.installments_amounts.map((x) => Number(x) || 0) : [];
                                    if (amts.length < 2) return null;
                                    const allFilled = amts.every((a) => a > 0);
                                    if (!allFilled) return null;
                                    const allEqual = amts.every((a) => Math.abs(a - amts[0]) < 0.005);
                                    if (allEqual) return null;
                                    const sumPlan = amts.reduce((s, a) => s + a, 0);
                                    return (
                                      <span
                                        className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 text-amber-900 tabular-nums font-semibold flex-wrap"
                                        title={`Piano rateale personalizzato — totale ${formatCurrency(sumPlan)}`}
                                        data-testid={`variant-installments-plan-${v.id}`}
                                      >
                                        💶 Piano:
                                        {amts.map((a, i) => (
                                          <span key={i} className="ml-0.5">
                                            {i + 1}° {formatCurrency(a)}{i < amts.length - 1 ? " ·" : ""}
                                          </span>
                                        ))}
                                        <span className="ml-1 text-amber-900">= <strong>{formatCurrency(sumPlan)}</strong></span>
                                      </span>
                                    );
                                  })()}
                                  {v.effective_date && (
                                    <span title="Data di decorrenza polizza">
                                      📅 <strong>Dec.:</strong> {formatDate(v.effective_date)}
                                    </span>
                                  )}
                                  {v.expiry_date && (
                                    <span title="Data di scadenza polizza">
                                      ⏰ <strong>Scad.:</strong> {formatDate(v.expiry_date)}
                                    </span>
                                  )}
                              </div>
                              {v.notes && <p className="order-3 basis-full text-[11px] text-slate-600 italic">{v.notes}</p>}
                              {v.source_filename && <p className="order-3 basis-full text-[10px] text-slate-400">📎 da: {v.source_filename}</p>}
                              {/* 🆕 Mostra le garanzie della variante (read-only) */}
                              {(v.coverages || []).length > 0 && (
                                <div className="order-3 basis-full flex items-center gap-1 flex-wrap text-[10.5px]" data-testid={`variant-coverages-readonly-${v.id}`}>
                                  <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold mr-0.5">
                                    Garanzie ({v.coverages.length}):
                                  </span>
                                  {v.coverages.map((c, ci) => {
                                    const hidden = c.client_visible === false;
                                    const acc = c.kind === "accessory";
                                    return (
                                      <span
                                        key={ci}
                                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${acc ? "border-slate-300 bg-slate-50 text-slate-600" : "border-blue-200 bg-blue-50/70 text-blue-900"} ${hidden ? "opacity-50" : ""}`}
                                        title={[c.name, c.sum_insured ? `massimale ${formatCurrency(c.sum_insured)}` : null, c.deductible ? `franchigia ${c.deductible}` : null, hidden ? "nascosta al cliente" : null].filter(Boolean).join(" · ")}
                                        data-testid={`variant-coverage-chip-${v.id}-${ci}`}
                                      >
                                        {hidden && <span className="text-rose-600">🚫</span>}
                                        <span className={hidden ? "line-through" : ""}>{c.name}</span>
                                        {c.deductible && <span className="text-slate-400">(fr. {c.deductible})</span>}
                                        {c.premium != null && c.premium !== "" && (
                                          <span className="tabular-nums font-semibold">{formatCurrency(c.premium)}</span>
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                              {/* 🆕 Feb 2026 — Note della variante (read-only, sotto le garanzie) */}
                              {(v.notes || "").trim() !== "" && (
                                <div className="order-3 basis-full text-[11px] rounded border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-2" data-testid={`variant-notes-readonly-${v.id}`}>
                                  <div className="text-[10px] uppercase tracking-wide text-amber-800 dark:text-amber-200 font-semibold mb-0.5">
                                    Note
                                  </div>
                                  <div className="text-slate-800 dark:text-slate-100 italic whitespace-pre-wrap break-words">
                                    {v.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="order-2 flex gap-1.5 flex-wrap">
                              {!isWinner && (
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                                  data-testid={`finalize-variant-${v.id}`}
                                  onClick={() => finalizeVariant(v.id, v.company)}
                                >
                                  ⭐ Finalizza
                                </Button>
                              )}
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                                data-testid={`edit-variant-${v.id}`}
                                onClick={() => startEditVariant(v)}
                                title="Modifica compagnia / premio / note"
                              >
                                <Pencil className="h-3 w-3 mr-1" /> Modifica
                              </Button>
                              {/* 🆕 Pulsante genera PDF cliente per QUESTA variante */}
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/quote-offers/${editing?.id || ""}/print-pdf?variant_id=${v.id}&_t=${Date.now()}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 h-7 px-2 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-xs font-medium"
                                title={`Genera PDF offerta cliente con i dati di "${v.company}"`}
                                data-testid={`variant-pdf-btn-${v.id}`}
                              >
                                📄 PDF
                              </a>
                              {/* 🆕 Duplica variante */}
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await api.post(`/quote-offers/${editing.id}/variants/${v.id}/duplicate`);
                                    toast.success(`✓ Variante "${v.company}" duplicata`);
                                    // 🛠 Fix: la lista varianti in UI legge dallo stato `variants`, non da `editing.variants`.
                                    // Aggiorno ENTRAMBI per far comparire subito la nuova variante senza chiudere il dialog.
                                    const refreshed = await api.get(`/quote-offers/${editing.id}`);
                                    setEditing(refreshed.data);
                                    setVariants(refreshed.data?.variants || []);
                                    load();
                                  } catch (e) { toast.error(formatApiError(e)); }
                                }}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-medium"
                                title={`Duplica la variante "${v.company}" con tutti i dati (garanzie, importi, note)`}
                                data-testid={`duplicate-variant-${v.id}`}
                              >
                                📋 Duplica
                              </button>
                              {/* 🆕 10/07/2026 — Traccia "cliente avvisato" per singola variante */}
                              {(() => {
                                const notifiedAt = v.notified_client_at;
                                const notifiedBy = v.notified_client_by;
                                const handleClick = async () => {
                                  try {
                                    const { data } = await api.patch(
                                      `/quote-offers/${editing.id}/variants/${v.id}/notify-client`,
                                      { notified: true },
                                    );
                                    setVariants((arr) => arr.map((vv) => vv.id === v.id ? { ...vv, notified_client_at: data.notified_client_at, notified_client_by: data.notified_client_by } : vv));
                                    toast.success(notifiedAt ? "✓ Data avviso aggiornata" : `✓ Cliente avvisato per "${v.company}"`);
                                  } catch (e) { toast.error(formatApiError(e)); }
                                };
                                const handleClear = async () => {
                                  if (!window.confirm("Rimuovere il flag \"cliente avvisato\" per questa variante?")) return;
                                  try {
                                    await api.patch(
                                      `/quote-offers/${editing.id}/variants/${v.id}/notify-client`,
                                      { notified: false },
                                    );
                                    setVariants((arr) => arr.map((vv) => vv.id === v.id ? { ...vv, notified_client_at: null, notified_client_by: null } : vv));
                                    toast.success("Flag avviso rimosso");
                                  } catch (e) { toast.error(formatApiError(e)); }
                                };
                                if (!notifiedAt) {
                                  return (
                                    <button
                                      type="button"
                                      onClick={handleClick}
                                      className="inline-flex items-center gap-1 h-7 px-2 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 text-xs font-medium"
                                      title="Registra data/ora di avviso al cliente per questa variante"
                                      data-testid={`notify-client-${v.id}`}
                                    >
                                      📢 Avvisa cliente
                                    </button>
                                  );
                                }
                                return (
                                  <div className="inline-flex items-center gap-1 h-7 px-2 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 text-[10.5px] font-medium" data-testid={`notified-badge-${v.id}`}>
                                    ✓ Avvisato {formatDateTime(notifiedAt)}
                                    {notifiedBy && <span className="text-emerald-600 text-[9px]">· {notifiedBy}</span>}
                                    <button
                                      type="button"
                                      onClick={handleClick}
                                      className="text-emerald-700 hover:text-emerald-900 ml-0.5"
                                      title="Aggiorna data/ora avviso"
                                      data-testid={`notify-client-update-${v.id}`}
                                    >↻</button>
                                    <button
                                      type="button"
                                      onClick={handleClear}
                                      className="text-rose-600 hover:text-rose-800 ml-0.5"
                                      title="Rimuovi flag"
                                      data-testid={`notify-client-clear-${v.id}`}
                                    >✕</button>
                                  </div>
                                );
                              })()}
                              <button
                                type="button"
                                onClick={() => setPartnerEmailVariant(v)}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded border border-sky-300 text-sky-700 hover:bg-sky-50 text-xs font-medium"
                                title="Invia un'email al partner/broker con i dettagli di questa variante"
                                data-testid={`partner-email-btn-${v.id}`}
                              >
                                📧 Invia email partner
                              </button>
                              <button
                                type="button"
                                onClick={() => removeVariant(v.id, v.company)}
                                className="text-rose-600 hover:text-rose-800 px-2"
                                title="Rimuovi variante"
                                data-testid={`remove-variant-${v.id}`}
                              >
                                <XIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          {(v.partner_emails || []).length > 0 && (
                            <div className="mt-2 flex flex-col gap-1" data-testid={`partner-email-trace-${v.id}`}>
                              {[...v.partner_emails].reverse().map((pe, i) => {
                                const replies = (v.partner_replies || []).filter((r) => pe.message_id && r.reply_to_message_id === pe.message_id);
                                return (
                                  <div key={i} className="flex flex-col gap-1">
                                    <span className="self-start inline-flex items-center gap-1 rounded border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10.5px] font-medium text-sky-800" title={pe.subject}>
                                      {pe.in_reply_to_inbox_id ? "↪️ Risposta inviata" : "✉️ Inviata al partner"} {formatDateTime(pe.sent_at)} · {(pe.to || []).join(", ")}
                                      {pe.cc?.length > 0 && <span className="text-sky-600">· cc {pe.cc.join(", ")}</span>}
                                      {pe.attachments?.length > 0 && <span className="text-sky-600" title={pe.attachments.join("\n")}>· 📎 {pe.attachments.length}</span>}
                                      {pe.sent_by && <span className="text-sky-600 text-[9px]">· {pe.sent_by}</span>}
                                      {pe.message_id && !replies.length && <span className="text-slate-500 text-[9px] italic">· in attesa di risposta</span>}
                                    </span>
                                    {replies.map((r) => (
                                      <button
                                        key={r.inbox_email_id}
                                        type="button"
                                        onClick={() => setPartnerReply({ emailId: r.inbox_email_id, variantId: v.id })}
                                        className="ml-4 self-start text-left max-w-full inline-flex items-start gap-1.5 rounded border border-violet-300 bg-violet-50 hover:bg-violet-100 px-2 py-1 text-[10.5px] text-violet-900 transition-colors"
                                        title="Apri la risposta completa"
                                        data-testid={`partner-reply-${r.inbox_email_id}`}
                                      >
                                        <span>↩️</span>
                                        <span className="min-w-0">
                                          <span className="font-semibold">Risposta da {r.from}</span> · {formatDateTime(r.received_at)}
                                          {r.attachments_count > 0 && <span> · 📎 {r.attachments_count}</span>}
                                          {r.snippet && <span className="block text-violet-700 truncate max-w-[560px]">{r.snippet}</span>}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Form aggiungi variante manuale */}
                <div className="rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50/40 p-3" data-testid="add-variant-form">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700 mb-2">➕ Aggiungi variante manuale</div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                    <div className="sm:col-span-3 min-w-0 flex flex-col gap-1">
                      <select
                        className="min-w-0 h-9 px-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        value={newVariant.broker_id || ""}
                        onChange={(e) => setNewVariant((n) => ({ ...n, broker_id: e.target.value, company_id: "" }))}
                        data-testid="new-variant-broker"
                        title="Broker / Agenzia partner che ha fornito la quotazione (opz.)"
                      >
                        <option value="">Broker / Agenzia (opz.)</option>
                        {(brokers || []).map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}{b.type ? ` · ${b.type === "agenzia" ? "Agenzia" : "Broker"}` : ""}
                          </option>
                        ))}
                      </select>
                      {/* 🆕 Badge conteggio compagnie collegate al broker selezionato (cliccabile) */}
                      {newVariant.broker_id && (() => {
                        const cnt = getLinkedCompaniesCount(newVariant.broker_id);
                        const onOpen = () => navigate(`/brokers?expand=${newVariant.broker_id}&return_to_offer=${editing?.id || ""}`);
                        return cnt !== null ? (
                          <button
                            type="button"
                            onClick={onOpen}
                            className="text-[10.5px] text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1 self-start"
                            title="Gestisci compagnie collegate a questo partner"
                            data-testid="new-variant-broker-links-count"
                          >
                            🔗 <span className="font-medium">{cnt}</span>&nbsp;{cnt === 1 ? "compagnia collegata" : "compagnie collegate"} · gestisci →
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={onOpen}
                            className="text-[10.5px] text-amber-700 hover:text-amber-900 hover:underline flex items-center gap-1 self-start"
                            title="Configura le compagnie collegate a questo partner"
                            data-testid="new-variant-broker-links-count"
                          >
                            ⚠️ nessuna compagnia collegata · configura →
                          </button>
                        );
                      })()}
                    </div>
                    {/* 🆕 Compagnia: dropdown compagnie collegate al broker (con override "Altro" per input manuale) */}
                    <div className="sm:col-span-3 min-w-0 flex gap-1 items-stretch">
                      {newVariantCompanyMode === "select" ? (
                        <select
                          className="flex-1 min-w-0 h-9 px-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                          value={newVariant.company_id || (newVariant.company ? "__custom__" : "")}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "__other__") {
                              setNewVariantCompanyMode("manual");
                              setNewVariant((n) => ({ ...n, company: "", company_id: "" }));
                              return;
                            }
                            const cmp = getCompaniesForBroker(newVariant.broker_id).find((c) => c.id === val);
                            setNewVariant((n) => ({ ...n, company: cmp?.name || "", company_id: cmp?.id || "" }));
                          }}
                          data-testid="new-variant-company"
                        >
                          <option value="">Seleziona compagnia…</option>
                          {getCompaniesForBroker(newVariant.broker_id).map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                          <option value="__other__">➕ Altro / inserisci manualmente…</option>
                        </select>
                      ) : (
                        <>
                          <Input
                            className="flex-1 min-w-0"
                            placeholder="Compagnia (manuale)"
                            value={newVariant.company}
                            onChange={(e) => {
                              const val = e.target.value;
                              const cid = findCompanyIdByName(val, newVariant.broker_id);
                              setNewVariant((n) => ({ ...n, company: val, company_id: cid }));
                            }}
                            data-testid="new-variant-company"
                          />
                          <button
                            type="button"
                            className="px-2 text-xs text-slate-500 hover:text-slate-800 border border-slate-300 rounded"
                            onClick={() => {
                              setNewVariantCompanyMode("select");
                              setNewVariant((n) => ({ ...n, company: "", company_id: "" }));
                            }}
                            title="Torna alla lista compagnie"
                            data-testid="new-variant-company-back-to-select"
                          >
                            ↺
                          </button>
                        </>
                      )}
                    </div>
                    <MoneyInput
                      className="sm:col-span-2 min-w-0"
                      placeholder="Premio per rata €"
                      value={newVariant.amount}
                      onChange={(v) => setNewVariant((n) => ({ ...n, amount: v }))}
                      data-testid="new-variant-amount"
                    />
                    {/* 🆕 Numero di rate modificabile (default dal frazionamento) + calcolo totale = premio × rate */}
                    <div className="sm:col-span-2 min-w-0 flex items-stretch gap-1">
                      <Input
                        type="number"
                        min={1}
                        max={60}
                        className="w-16 h-9 text-center tabular-nums shrink-0"
                        placeholder="Rate"
                        value={newVariant.installments_count}
                        onChange={(e) => setNewVariant((n) => ({ ...n, installments_count: e.target.value.replace(/[^0-9]/g, "") }))}
                        data-testid="new-variant-installments-count"
                        title="Numero di rate (default dal frazionamento, modificabile)"
                      />
                      {(() => {
                        const perRate = parseFloat(newVariant.amount);
                        const raten = parseInt(newVariant.installments_count, 10);
                        if (!perRate || !Number.isFinite(raten) || raten < 1) return null;
                        const total = perRate * raten;
                        // 🆕 Confronto somma garanzie inserite vs totale
                        const sumCovs = (newVariantCovs || []).reduce((s, c) => {
                          const p = Number(c.premium);
                          return s + (Number.isFinite(p) && p > 0 ? p : 0);
                        }, 0);
                        const hasCovs = sumCovs > 0;
                        const diff = Math.round((total - sumCovs) * 100) / 100;
                        const isMatch = hasCovs && Math.abs(diff) < 0.01;
                        const bgColor = !hasCovs ? "bg-blue-50 border-blue-200 text-blue-800" :
                          isMatch ? "bg-emerald-50 border-emerald-300 text-emerald-800" :
                          diff > 0 ? "bg-amber-50 border-amber-300 text-amber-800" :
                          "bg-rose-50 border-rose-300 text-rose-800";
                        return (
                          <div
                            className={`flex-1 min-w-0 h-9 flex flex-col items-center justify-center px-2 border rounded text-[11px] font-semibold tabular-nums leading-tight ${bgColor}`}
                            data-testid="new-variant-per-rate"
                            title={
                              !hasCovs ? `Premio per rata ${formatCurrency(perRate)} × ${raten} rate = ${formatCurrency(total)} totale` :
                              isMatch ? `Σ garanzie ${formatCurrency(sumCovs)} coincide con il totale ${formatCurrency(total)}` :
                              diff > 0 ? `Manca ${formatCurrency(Math.abs(diff))} nella somma delle garanzie` :
                              `Σ garanzie supera il totale di ${formatCurrency(Math.abs(diff))}`
                            }
                          >
                            <span className="truncate">= {formatCurrency(total)}</span>
                            {hasCovs && !isMatch && (
                              <span className="text-[9px] font-normal truncate">{diff > 0 ? "⚠" : "!"} Σcov: {formatCurrency(sumCovs)}</span>
                            )}
                            {hasCovs && isMatch && <span className="text-[9px] font-normal">✓ Σcov match</span>}
                          </div>
                        );
                      })()}
                    </div>
                    <Input
                      className="sm:col-span-4 min-w-0"
                      placeholder="Note (opz.)"
                      value={newVariant.notes}
                      onChange={(e) => setNewVariant((n) => ({ ...n, notes: e.target.value }))}
                      data-testid="new-variant-notes"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Frazionamento</label>
                      <select
                        className="w-full h-9 px-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        value={newVariant.frazionamento || ""}
                        onChange={(e) => setNewVariant((n) => ({ ...n, frazionamento: e.target.value }))}
                        data-testid="new-variant-frazionamento"
                      >
                        <option value="">— eredita dall&apos;offerta —</option>
                        <option value="annuale">Annuale</option>
                        <option value="semestrale">Semestrale</option>
                        <option value="quadrimestrale">Quadrimestrale</option>
                        <option value="trimestrale">Trimestrale</option>
                        <option value="mensile">Mensile</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1.5">
                        Decorrenza polizza
                        {linkedQuoteStart && newVariant.effective_date === linkedQuoteStart && (
                          <span className="normal-case text-[9px] font-normal text-blue-700 bg-blue-50 border border-blue-200 rounded px-1" title="Valore preso dal preventivo collegato">dal preventivo</span>
                        )}
                        {linkedQuoteStart && newVariant.effective_date && newVariant.effective_date !== linkedQuoteStart && (
                          <span className="normal-case text-[9px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1" title={`Preventivo: ${new Date(linkedQuoteStart).toLocaleDateString("it-IT")}`}>⚠ diversa dal preventivo</span>
                        )}
                      </label>
                      <DatePicker value={newVariant.effective_date || ""}
                        onChange={(e) => setNewVariant((n) => ({
                          ...n,
                          effective_date: e.target.value,
                          // 🆕 Feb 2026 — Auto-ricalcola SEMPRE la scadenza a +1 anno quando l'utente cambia la decorrenza.
                          // Prima: solo se scadenza non era già impostata → generava confusione se la scadenza
                          // era stata pre-compilata dal preventivo e l'utente cambiava solo la decorrenza.
                          expiry_date: e.target.value ? addOneYearIso(e.target.value) : n.expiry_date,
                        }))}
                        data-testid="new-variant-effective-date" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold flex items-center gap-1.5">
                        Scadenza polizza
                        {linkedQuoteEnd && newVariant.expiry_date === linkedQuoteEnd ? (
                          <span className="normal-case text-[9px] font-normal text-blue-700 bg-blue-50 border border-blue-200 rounded px-1" title="Valore preso dal preventivo collegato">dal preventivo</span>
                        ) : linkedQuoteEnd && newVariant.expiry_date && newVariant.expiry_date !== linkedQuoteEnd ? (
                          <span className="normal-case text-[9px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1" title={`Preventivo: ${new Date(linkedQuoteEnd).toLocaleDateString("it-IT")}`}>⚠ diversa dal preventivo</span>
                        ) : (
                          <span className="text-[9px] font-normal normal-case text-slate-500">(auto: +1 anno)</span>
                        )}
                      </label>
                      <DatePicker value={newVariant.expiry_date || ""}
                        onChange={(e) => setNewVariant((n) => ({ ...n, expiry_date: e.target.value }))}
                        data-testid="new-variant-expiry-date" />
                    </div>
                    {/* 🆕 09/07/2026 — Tipo di guida per la variante (default: eredita dal preventivo/offerta).
                        Anche se lasciato vuoto, il backend valorizza automaticamente il campo con
                        il tipo ereditato dal preventivo collegato, così il PDF si aggiorna correttamente. */}
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                        Tipo di guida
                        {form.driving_type || (linkedQuote?.details?.driving_type) ? (
                          <span className="text-[9px] font-normal normal-case text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1" title="Valore ereditato dal preventivo/offerta">✓ ereditato</span>
                        ) : (
                          <span className="text-[9px] font-normal normal-case text-slate-400">(opzionale)</span>
                        )}
                      </label>
                      <select
                        className="w-full h-9 px-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        value={newVariant.driving_type || ""}
                        onChange={(e) => setNewVariant((n) => ({ ...n, driving_type: e.target.value }))}
                        data-testid="new-variant-driving-type"
                      >
                        <option value="">— eredita dall&apos;offerta —</option>
                        <option value="esclusiva">Esclusiva</option>
                        <option value="esperta">Esperta</option>
                        <option value="libera">Libera</option>
                      </select>
                    </div>
                  </div>

                  {/* 📦 Prodotto (catalogo) RIMOSSO dalle varianti 09/07/2026 — resta solo consultabile dalla pagina /agency/products */}
                  {/* 🆕 Rate personalizzate per la NUOVA variante (stesso comportamento di Modifica variante) */}
                  {(() => {
                    const _fraz = newVariant.frazionamento;
                    const _raten = parseInt(newVariant.installments_count, 10) || 1;
                    if (!_fraz || _fraz === "annuale" || _raten < 2) return null;
                    const _amts = Array.isArray(newVariant.installments_amounts) && newVariant.installments_amounts.length === _raten
                      ? newVariant.installments_amounts
                      : Array(_raten).fill("");
                    const _nums = _amts.map((x) => Number(x) || 0);
                    const _allFilled = _nums.length > 0 && _nums.every((x) => x > 0);
                    const _anyFilled = _nums.some((x) => x > 0);
                    const _sum = _nums.reduce((s, x) => s + x, 0);
                    return (
                      <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2" data-testid="new-variant-installments-panel">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                            💶 Importi per rata (opzionale — {_raten} rate {_fraz})
                          </span>
                          <span className="text-[10px] text-amber-800 tabular-nums">Σ rate: <strong>{formatCurrency(_sum)}</strong></span>
                        </div>
                        {_anyFilled && !_allFilled && (
                          <div className="mb-1.5 text-[10px] text-rose-800 bg-rose-50 border border-rose-300 rounded px-1.5 py-1 leading-tight">
                            ⚠ <strong>Piano incompleto</strong>: compila TUTTE le rate (&gt; 0) per attivare il piano personalizzato. Con importi mancanti verrà usato il piano uniforme del &quot;Premio per rata&quot;.
                          </div>
                        )}
                        <div className={`grid gap-1 grid-cols-${Math.min(_raten, 6)}`}>
                          {_amts.map((val, i) => (
                            <div key={i} className="flex flex-col">
                              <span className="text-[9px] font-semibold text-amber-700 tracking-wider">{i + 1}° RATA</span>
                              <MoneyInput
                                className="h-8"
                                placeholder="0,00"
                                value={val}
                                onChange={(rawVal) => {
                                  const next = Array(_raten).fill("").map((_, k) => k < _amts.length ? _amts[k] : "");
                                  next[i] = rawVal;
                                  setNewVariant((n) => ({ ...n, installments_amounts: next }));
                                }}
                                data-testid={`new-variant-installment-${i}`}
                              />
                            </div>
                          ))}
                        </div>
                        {_allFilled && !_nums.every((a) => Math.abs(a - _nums[0]) < 0.005) && (
                          <div className="mt-1.5 flex items-center justify-between text-[11px] font-semibold text-amber-900 bg-amber-100 border border-amber-300 rounded px-2 py-1">
                            <span>🔒 Piano personalizzato attivo</span>
                            <span>Totale piano: <strong className="tabular-nums">{formatCurrency(_sum)}</strong></span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* 🆕 Garanzie inline per la NUOVA variante (opzionali) — stesso dropdown della variante in edit */}
                  <div className="mt-3 rounded-md bg-white border border-blue-200 p-2" data-testid="new-variant-covs-section">
                    <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">
                        Garanzie variante ({newVariantCovs.length})
                        {newVariantCovs.length === 0 && (
                          <span className="ml-1.5 text-[10px] font-normal text-slate-500 lowercase tracking-normal">— se vuote, verranno ereditate dalle &quot;Garanzie richieste dal cliente&quot;</span>
                        )}
                      </span>
                      <div className="flex gap-1 flex-wrap">
                        {/* 🆕 Suggerimento template: copia garanzie da ultima variante della stessa compagnia */}
                        {companyTemplate && newVariantCovs.length === 0 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] px-2 border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                            onClick={applyCompanyTemplate}
                            title={`Copia le ${(companyTemplate.coverages || []).length} garanzie dall'ultima variante di questa compagnia (offerta ${companyTemplate.source_offer_number || ""}). I premi vanno re-inseriti.`}
                            data-testid="new-variant-apply-template"
                          >
                            🔄 Copia da {companyTemplate.source_offer_number || "variante precedente"} ({(companyTemplate.coverages || []).length})
                          </Button>
                        )}
                        <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => addNewVariantCov("coverage")} data-testid="new-variant-add-cov">
                          + Garanzia
                        </Button>
                        <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => addNewVariantCov("line_item")} data-testid="new-variant-add-item">
                          + Voce accessoria
                        </Button>
                      </div>
                    </div>
                    {/* 🆕 Didascalie colonne (visibili solo se ci sono righe) */}
                    {newVariantCovs.length > 0 && (
                      <div className="hidden sm:grid grid-cols-12 gap-1.5 px-1 pb-1 border-b border-blue-100 mb-1 text-[9px] uppercase tracking-wider text-slate-500 font-semibold" data-testid="new-variant-cov-headers">
                        <div className="col-span-4">Nome garanzia</div>
                        <div className="col-span-2 text-center">Massimale (€)</div>
                        <div className="col-span-2 text-center leading-tight">
                          Premio (€)
                          {(() => {
                            const fr = newVariant.frazionamento || "annuale";
                            const rateN = parseInt(newVariant.installments_count, 10) || (FRAZ_RATE_MAP[fr] || 1);
                            const label = fr === "annuale" || rateN <= 1 ? "annuo" : `per rata (${fr}${rateN >= 2 ? ` · ${rateN} rate` : ""})`;
                            return (<><br/><span className="font-normal normal-case text-[8px] text-blue-600">{label}</span></>);
                          })()}
                        </div>
                        <div className="col-span-2 text-center">Franchigia</div>
                        <div className="col-span-1 text-center">Visibilità</div>
                        <div className="col-span-1 text-center">Rimuovi</div>
                      </div>
                    )}
                    {newVariantCovs.map((c, ci) => (
                      <div key={ci} className="grid grid-cols-1 sm:grid-cols-12 gap-1.5 mb-1.5 items-center" data-testid={`new-variant-cov-row-${ci}`}>
                        {/* 🆕 Feb 2026 — Badge/chip garanzia non collegata + etichetta originale AI (span 12 cols per essere sopra la riga controlli) */}
                        {(c._unmatched || (c.extracted_name && c.extracted_name !== c.name)) && (
                          <div className="sm:col-span-12 flex items-center gap-1.5 flex-wrap -mb-0.5">
                            {c._unmatched && (
                              <span
                                data-testid={`new-variant-cov-unmatched-${ci}`}
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-300 px-1.5 py-0.5 rounded"
                                title="Garanzia non collegata al listino compagnia. Aggiungi un alias sulla voce master."
                              >
                                <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500" />
                                Non collegata
                              </span>
                            )}
                            {c.extracted_name && c.extracted_name !== c.name && (
                              <span
                                data-testid={`new-variant-cov-extracted-${ci}`}
                                className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-600 border border-slate-300 px-1.5 py-0.5 rounded max-w-[240px]"
                                title={`Etichetta originale estratta dal PDF: "${c.extracted_name}"`}
                              >
                                📄 <span className="truncate">{c.extracted_name}</span>
                              </span>
                            )}
                          </div>
                        )}
                        <div className="sm:col-span-4 flex flex-col gap-0.5">
                          {c.kind === "line_item" && (lineItems || []).length > 0 && (() => {
                            const knownLi = (lineItems || []).find((a) => a.name === c.name);
                            const isCustomLi = c._manual === true || (!!c.name && !knownLi);
                            return (
                              <select
                                className="h-7 px-2 text-[11px] border border-indigo-300 bg-indigo-50 text-indigo-800 rounded font-medium focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                value={isCustomLi ? "__custom__" : (knownLi?.id || "")}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "__custom__") {
                                    updateNewVariantCov(ci, "name", "");
                                    updateNewVariantCov(ci, "_manual", true);
                                    return;
                                  }
                                  if (!val) {
                                    updateNewVariantCov(ci, "name", "");
                                    updateNewVariantCov(ci, "_manual", false);
                                    return;
                                  }
                                  const preset = (lineItems || []).find((a) => a.id === val);
                                  if (!preset) return;
                                  updateNewVariantCov(ci, "name", preset.name);
                                  updateNewVariantCov(ci, "_manual", false);
                                  if (preset.default_premium != null) updateNewVariantCov(ci, "premium", String(preset.default_premium));
                                  if (preset.description) updateNewVariantCov(ci, "description", preset.description);
                                }}
                                data-testid={`new-variant-line-item-select-${ci}`}
                                title="Seleziona una voce accessoria dall'elenco (o scegli 'Altro' per inserimento manuale)"
                              >
                                <option value="">📋 Scegli voce…</option>
                                {(lineItems || []).filter((a) => a.is_active !== false).map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.name}{a.default_premium != null ? ` — ${formatCurrency(a.default_premium)}` : ""}
                                  </option>
                                ))}
                                <option value="__custom__">➕ Altro / manuale</option>
                              </select>
                            );
                          })()}
                          {c.kind !== "line_item" && (policyCoverages || []).length > 0 && (() => {
                            const known = (policyCoverages || []).find((p) => (p.label === c.name) || (p.code && p.code === c.code));
                            const isCustom = c._manual === true || (!!c.name && !known);
                            return (
                              <select
                                className="h-7 px-2 text-[11px] border border-blue-300 bg-blue-50 text-blue-800 rounded font-medium focus:outline-none focus:ring-2 focus:ring-blue-300"
                                value={isCustom ? "__custom__" : (known?.code || "")}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "__custom__") {
                                    updateNewVariantCov(ci, "name", "");
                                    updateNewVariantCov(ci, "code", null);
                                    updateNewVariantCov(ci, "_manual", true);
                                    return;
                                  }
                                  if (!val) {
                                    updateNewVariantCov(ci, "name", "");
                                    updateNewVariantCov(ci, "code", null);
                                    updateNewVariantCov(ci, "_manual", false);
                                    return;
                                  }
                                  const preset = (policyCoverages || []).find((p) => p.code === val);
                                  if (!preset) return;
                                  updateNewVariantCov(ci, "name", preset.label || preset.code);
                                  updateNewVariantCov(ci, "code", preset.code || null);
                                  updateNewVariantCov(ci, "_manual", false);
                                  if (preset.description && !c.description) updateNewVariantCov(ci, "description", preset.description);
                                  if (preset.default_sum_insured != null && (c.sum_insured === "" || c.sum_insured == null)) updateNewVariantCov(ci, "sum_insured", String(preset.default_sum_insured));
                                  if (preset.default_deductible != null && !c.deductible) updateNewVariantCov(ci, "deductible", String(preset.default_deductible));
                                }}
                                data-testid={`new-variant-cov-name-select-${ci}`}
                                title="Seleziona una garanzia dalla lista preventivo (o scegli 'Altro' per inserimento manuale)"
                              >
                                <option value="">🛡️ Scegli garanzia…</option>
                                {sortedPolicyCoveragesForType.map((p) => (
                                  <option key={p.code} value={p.code}>
                                    {p.label || p.code}
                                  </option>
                                ))}
                                <option value="__custom__">➕ Altro / manuale</option>
                              </select>
                            );
                          })()}
                          {/* 🆕 Input libero: mostrato in modalità manuale (custom) */}
                          {(() => {
                            if (c._manual === true) return true;
                            if (c.kind === "line_item") {
                              const knownLi = (lineItems || []).find((a) => a.name === c.name);
                              return !!c.name && !knownLi;
                            }
                            return !((policyCoverages || []).find((p) => (p.label === c.name) || (p.code && p.code === c.code))) && !!c.name;
                          })() && (
                            <div className="flex gap-1 items-center">
                              <Input
                                className="h-7 text-xs flex-1"
                                placeholder={c.kind === "line_item" ? "Nome voce (manuale)" : "Nome garanzia (manuale)"}
                                value={c.name}
                                onChange={(e) => updateNewVariantCov(ci, "name", e.target.value)}
                                data-testid={`new-variant-cov-name-input-${ci}`}
                              />
                              {c.kind === "line_item" ? (
                                <button
                                  type="button"
                                  className="h-7 px-1.5 text-[11px] rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 shrink-0"
                                  title="Salva questa voce nell'elenco Voci accessorie per riutilizzarla nelle prossime offerte"
                                  data-testid={`new-variant-save-lineitem-${ci}`}
                                  onClick={() => saveLineItemToList({ name: c.name, default_premium: c.premium, description: c.description })}
                                  disabled={!c.name || !String(c.name).trim()}
                                >
                                  💾
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="h-7 px-1.5 text-[11px] rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 shrink-0"
                                  title="Salva questa garanzia nell'elenco Garanzie Polizze per riutilizzarla nelle prossime offerte"
                                  data-testid={`new-variant-save-coverage-${ci}`}
                                  onClick={() => savePolicyCoverageToList({ label: c.name, description: c.description, sum_insured: c.sum_insured, deductible: c.deductible })}
                                  disabled={!c.name || !String(c.name).trim()}
                                >
                                  💾
                                </button>
                              )}
                            </div>
                          )}
                          {/* 🆕 Compagnia per singola garanzia (allineato al comportamento di edit variant) */}
                          <select
                            className={`h-6 px-1.5 text-[10px] rounded border font-medium focus:outline-none focus:ring-1 focus:ring-purple-300 ${
                              c.company_id
                                ? "border-purple-300 bg-purple-50 text-purple-800"
                                : "border-slate-200 bg-slate-50 text-slate-500"
                            }`}
                            value={c.company_id || ""}
                            onChange={(e) => {
                              const cid = e.target.value || null;
                              updateNewVariantCov(ci, "company_id", cid);
                              const meta = (companies || []).find((x) => x.id === cid);
                              updateNewVariantCov(ci, "company_name", meta?.name || null);
                            }}
                            title={c.company_id ? `Questa garanzia è collocata con ${c.company_name || "un'altra compagnia"}` : `Se null, eredita la compagnia della variante (${newVariant.company || "—"})`}
                            data-testid={`new-variant-cov-company-${ci}`}
                          >
                            <option value="">🏢 stessa variante</option>
                            {(companies || []).map((co) => (
                              <option key={co.id} value={co.id}>{co.name}</option>
                            ))}
                          </select>
                        </div>
                        <MoneyInput className="sm:col-span-2 h-7 text-xs" placeholder="Massimale €" value={c.sum_insured ?? ""} onChange={(val) => updateNewVariantCov(ci, "sum_insured", val)} />
                        <MoneyInput className="sm:col-span-2 h-7 text-xs" placeholder={(() => {
                          const fr = newVariant.frazionamento || "annuale";
                          const rateN = parseInt(newVariant.installments_count, 10) || (FRAZ_RATE_MAP[fr] || 1);
                          return fr === "annuale" || rateN <= 1 ? "Premio annuo €" : `Premio per rata € (${fr})`;
                        })()} value={c.premium ?? ""} onChange={(val) => updateNewVariantCov(ci, "premium", val)} />
                        <Input className="sm:col-span-2 h-7 text-xs" placeholder="Franchigia" value={c.deductible || ""} onChange={(e) => updateNewVariantCov(ci, "deductible", e.target.value)} />
                        <div className="sm:col-span-1 flex flex-col gap-0.5">
                          <label
                            className={`h-6 px-1.5 inline-flex items-center justify-center gap-0.5 text-[9px] rounded border cursor-pointer select-none ${c.client_visible !== false ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-100 text-slate-600 line-through"}`}
                            title={c.client_visible !== false ? "Riga visibile al cliente" : "Riga NASCOSTA al cliente"}
                          >
                            <input type="checkbox" className="h-3 w-3" checked={c.client_visible !== false} onChange={(e) => updateNewVariantCov(ci, "client_visible", e.target.checked)} />
                            <span>{c.client_visible !== false ? "👁" : "🚫"}</span>
                          </label>
                          <label
                            className={`h-6 px-1.5 inline-flex items-center justify-center gap-0.5 text-[9px] rounded border cursor-pointer select-none ${
                              c.client_visible === false
                                ? "border-slate-200 bg-slate-50 text-slate-400"
                                : c.client_price_visible !== false
                                  ? "border-blue-300 bg-blue-50 text-blue-700"
                                  : "border-amber-300 bg-amber-50 text-amber-700 line-through"
                            }`}
                            title={
                              c.client_visible === false
                                ? "Riga nascosta: la visibilità del prezzo non ha effetto"
                                : c.client_price_visible !== false
                                  ? "Importo (premio) visibile al cliente"
                                  : "Solo la garanzia sarà visibile, il premio verrà OMESSO"
                            }
                          >
                            <input
                              type="checkbox"
                              className="h-3 w-3"
                              checked={c.client_price_visible !== false}
                              disabled={c.client_visible === false}
                              onChange={(e) => updateNewVariantCov(ci, "client_price_visible", e.target.checked)}
                            />
                            <span>{c.client_price_visible !== false ? "💰" : "🚫€"}</span>
                          </label>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeNewVariantCov(ci)}
                          className="sm:col-span-1 h-7 text-rose-600 hover:text-rose-800 border border-rose-200 rounded text-xs"
                          title="Rimuovi riga"
                          data-testid={`new-variant-cov-remove-${ci}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button type="button" size="sm" onClick={addVariant} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-variant-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1.5">💡 Le coperture/garanzie inserite nella sezione qui sotto verranno copiate nella nuova variante. L&apos;importo dell&apos;offerta principale si aggiorna automaticamente alla variante più economica.</p>
                </div>
              </div>
            )}

            <div className="border-t border-slate-200 pt-4">
              {(() => {
                const selectedQuote = quotes.find((qq) => qq.id === form.quote_id);
                const pType = selectedQuote?.type || "";
                return (
                  <CoveragePicker
                    testid="offer-coverages"
                    policyType={pType}
                    value={form.selected_coverages || []}
                    onChange={(codes, labels) => {
                      setForm((f) => {
                        // Sincronizza la tabella coverages: prima rimuovi quelle auto-create dalle precedenti selezioni
                        // (riconoscibili da `_auto_code`), poi aggiungile aggiornate.
                        const manuallyAdded = (f.coverages || []).filter((c) => !c._auto_code);
                        // 🆕 Conserva `_added_in_session` per righe auto-code già create prima
                        const prevAutoByCode = new Map();
                        (f.coverages || []).forEach((c) => { if (c._auto_code) prevAutoByCode.set(c._auto_code, c); });
                        const autoRows = codes.map((code, i) => {
                          const existing = prevAutoByCode.get(code);
                          return {
                            ...emptyCov,
                            _auto_code: code,
                            name: labels[i] || code,
                            // Se la riga esisteva già, mantieni il flag; altrimenti se stiamo modificando, marca come nuova sessione
                            _added_in_session: existing ? !!existing._added_in_session : !!editing,
                            // Ripristina eventuale premium già inserito
                            ...(existing ? { premium: existing.premium, sum_insured: existing.sum_insured, deductible: existing.deductible, description: existing.description } : {}),
                          };
                        });
                        const merged = [...autoRows, ...manuallyAdded];
                        return { ...f, selected_coverages: codes, coverages: merged.length ? merged : [{ ...emptyCov }] };
                      });
                      // 🆕 Sincronizza sul preventivo collegato (best-effort, no blocking)
                      if (selectedQuote?.id) {
                        const newDetails = { ...(selectedQuote.details || {}), selected_coverages: codes };
                        api.put(`/quotes/${selectedQuote.id}`, { details: newDetails }).catch(() => {
                          // silent — non rompere l'UX se il preventivo è già chiuso/locked
                        });
                      }
                    }}
                  />
                );
              })()}
              <p className="text-[11px] text-slate-500 mt-2">Le garanzie selezionate vengono passate automaticamente ad ogni nuova variante. Inserisci gli importi all&apos;interno della singola variante (sezione &quot;Varianti compagnia&quot; sopra).</p>
            </div>

            {/* 🚫 Sezione &quot;Dettaglio coperture (importi e franchigie)&quot; rimossa su richiesta:
                ora gli importi vengono presi esclusivamente dalle garanzie della variante finalizzata. */}

            <div>
              <Label>Note interne visibili dal cliente nell&apos;offerta</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note visibili al cliente nell'offerta" />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <strong>Scadenza automatica a 5 giorni</strong> dalla creazione. Il cliente vede un avviso che se non paga entro la scadenza, l&apos;offerta potrà essere annullata e l&apos;importo potrebbe variare.
              </div>
            </div>
          </div>
          <DialogFooter>
            {editing?.id && (
              <span className="text-[11px] text-slate-500 mr-auto inline-flex items-center gap-1" data-testid="autosave-indicator">
                {autoSaveAt
                  ? <>💾 Bozza salvata automaticamente alle {autoSaveAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</>
                  : <>💾 Autosave attivo (ogni 30s)</>}
              </span>
            )}
            <Button variant="outline" onClick={() => closeOfferDialog()} data-testid="cancel-offer-edit">Annulla</Button>
            {editing?.share_token && (
              <Button
                type="button"
                variant="outline"
                data-testid="preview-client-view"
                disabled={busy}
                onClick={async () => {
                  // 🆕 Prima salva l'offerta corrente, poi apri l'anteprima cliente
                  setBusy(true);
                  try {
                    await persistOfferFromForm();
                  } finally {
                    setBusy(false);
                  }
                  setPreviewVersion((v) => v + 1);
                  setPreviewOpen(true);
                }}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                title="Salva l'offerta e apri l'anteprima della vista cliente (portale)"
              >
                <Eye className="h-4 w-4 mr-2" /> {busy ? "Salvataggio…" : "Anteprima portale"}
              </Button>
            )}
            {/* 🆕 Feb 2026 — Preview dell'EMAIL cliente (HTML del messaggio che verrà inviato) */}
            {editing?.id && (
              <Button
                type="button"
                variant="outline"
                data-testid="preview-client-email"
                disabled={busy}
                onClick={async () => {
                  // Salva prima l'offerta così l'anteprima include le ultime modifiche
                  setBusy(true);
                  try { await persistOfferFromForm(); } finally { setBusy(false); }
                  setEmailPreviewCtx({
                    typeKey: "offer_to_client_from_editor",
                    title: `Anteprima email cliente — Offerta ${editing.offer_number || ""}`,
                    fetchPreview: async () => {
                      const { data } = await api.post(`/quote-offers/${editing.id}/send?include_coverages=true&dry_run=true`);
                      return data;
                    },
                    onConfirm: async () => {
                      try {
                        await api.post(`/quote-offers/${editing.id}/send?include_coverages=true`);
                        toast.success("Email inviata al cliente (con coperture)");
                        setOpen(false); // chiudi il modulo di modifica
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                    },
                  });
                }}
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                title="Vedi l'HTML dell'email prima di inviarla al cliente"
              >
                <Mail className="h-4 w-4 mr-2" /> {busy ? "Salvataggio…" : "Anteprima & invia"}
              </Button>
            )}
            <Button data-testid="save-offer-btn" onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? "Salvataggio…" : (editing ? "Salva modifiche" : "Crea offerta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog "Segna come pagata" */}
      <Dialog open={!!markPaidOpen} onOpenChange={(o) => { if (!o) setMarkPaidOpen(null); }}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Euro className="h-5 w-5 text-green-600" /> Segna come pagata</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Conferma il pagamento offline di <strong>{markPaidOpen?.offer_number}</strong> ({formatCurrency(markPaidOpen?.amount)}).
            </p>
            <div>
              <Label>Metodo di pagamento ricevuto</Label>
              <PaymentMethodSelect value={paidMethod} onChange={setPaidMethod} testid="mark-paid-method" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidOpen(null)}>Annulla</Button>
            <Button onClick={markPaid} className="bg-green-600 hover:bg-green-700 text-white">Conferma pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog "Forza pagamento" — conferma esplicita per registrare pagamento + creare polizza */}
      <Dialog open={!!forcePaidOpen} onOpenChange={(o) => { if (!o) setForcePaidOpen(null); }}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="force-paid-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-fuchsia-700">
              <Zap className="h-5 w-5" /> Forza pagamento offerta
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-fuchsia-200 bg-fuchsia-50 p-3 text-sm text-fuchsia-900">
              Sei sicuro di voler <strong>forzare il pagamento</strong> dell&apos;offerta{" "}
              <strong>{forcePaidOpen?.offer_number}</strong>
              {forcePaidOpen?.amount != null && <> ({formatCurrency(forcePaidOpen?.amount)})</>}?
              <div className="mt-2 text-[12px] text-fuchsia-800">
                ⚠️ L&apos;offerta verrà <strong>immediatamente segnata come pagata</strong> e la polizza verrà generata in bozza.
                <br />
                📄 <strong>La ricevuta di pagamento NON verrà inviata al cliente</strong> (viene solo archiviata).
              </div>
            </div>
            <div>
              <Label>Metodo di pagamento</Label>
              <PaymentMethodSelect value={forcePaidMethod} onChange={setForcePaidMethod} testid="force-paid-method" />
            </div>
            {/* 🆕 Feb 2026 — Opt-in avvisi al cliente (email di conferma + push). Off di default. */}
            <label className="flex items-start gap-2 cursor-pointer rounded-md border border-slate-200 bg-slate-50 p-3 hover:border-fuchsia-300 transition">
              <Checkbox
                checked={forcePaidNotifyClient}
                onCheckedChange={(v) => setForcePaidNotifyClient(!!v)}
                data-testid="force-paid-notify-client"
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700 leading-snug">
                <strong>Invia avvisi al cliente</strong>
                <br />
                <span className="text-[12px] text-slate-500">
                  Se attivo, il cliente riceverà l&apos;email di conferma pagamento e la notifica push.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForcePaidOpen(null)} disabled={forcePaidBusy}>Annulla</Button>
            <Button onClick={confirmForcePaid} disabled={forcePaidBusy} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="force-paid-confirm">
              {forcePaidBusy ? "Forzo…" : "Sì, forza pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 09/07/2026 — Dialog di conferma dopo il pagamento: notifica che la polizza
          è stata creata (bozza) nella sezione Polizze e chiede se aprirla subito o dopo. */}
      <Dialog open={!!policyCreatedInfo} onOpenChange={(o) => { if (!o) setPolicyCreatedInfo(null); }}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="policy-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              ✅ Offerta pagata — Polizza creata
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <p className="mb-1">
                L&apos;offerta <strong>{policyCreatedInfo?.offerNumber}</strong>
                {policyCreatedInfo?.clientName && <> di <strong>{policyCreatedInfo.clientName}</strong></>}
                {" "}è stata segnata come <strong>pagata</strong>.
              </p>
              <p className="text-emerald-800">
                📝 È stata generata una <strong>polizza in bozza</strong> nella sezione <strong>Polizze</strong>.
                Completa i dati mancanti (numero polizza, compagnia, ecc.) per finalizzarla.
              </p>
            </div>
            <p className="text-sm text-slate-600">
              Vuoi aprire la polizza adesso?
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPolicyCreatedInfo(null)}
              data-testid="policy-created-later"
            >
              La aprirò dopo
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="policy-created-open-now"
              onClick={() => {
                const pid = policyCreatedInfo?.policyId;
                setPolicyCreatedInfo(null);
                if (pid) navigate(`/policies?focus=${pid}`);
              }}
            >
              Apri polizza adesso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog "Offerta accettata" — richiede 2 flag, segna pagata e apre la polizza */}
      <Dialog open={!!acceptedOpen} onOpenChange={(o) => { if (!o) setAcceptedOpen(null); }}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="offer-accepted-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              ✅ Conferma offerta accettata
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Offerta <strong>{acceptedOpen?.offer_number}</strong> ({formatCurrency(acceptedOpen?.amount)}) — <strong>{acceptedOpen?.client_name}</strong>.<br />
              Conferma entrambe le condizioni per creare la polizza:
            </p>
            <label className="flex items-start gap-3 p-3 rounded border border-emerald-200 bg-emerald-50 cursor-pointer hover:bg-emerald-100">
              <input
                type="checkbox"
                data-testid="accept-flag-accepted"
                checked={acceptedFlags.accepted}
                onChange={(e) => setAcceptedFlags({ ...acceptedFlags, accepted: e.target.checked })}
                className="mt-0.5 h-5 w-5 accent-emerald-600 cursor-pointer"
              />
              <span className="text-sm font-medium text-emerald-900">L&apos;offerta è stata <strong>accettata</strong> dal cliente?</span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded border border-emerald-200 bg-emerald-50 cursor-pointer hover:bg-emerald-100">
              <input
                type="checkbox"
                data-testid="accept-flag-paid"
                checked={acceptedFlags.paid}
                onChange={(e) => setAcceptedFlags({ ...acceptedFlags, paid: e.target.checked })}
                className="mt-0.5 h-5 w-5 accent-emerald-600 cursor-pointer"
              />
              <span className="text-sm font-medium text-emerald-900">L&apos;offerta è stata <strong>pagata</strong>?</span>
            </label>
            {acceptedOpen?.status === "pagata" ? (
              <div className="text-xs px-3 py-2 rounded bg-blue-50 border border-blue-200 text-blue-800">
                ℹ️ Questa offerta risulta <strong>già pagata</strong> ({acceptedOpen?.payment_method || "—"}). Procedendo verrà solo aperta la scheda della polizza collegata.
              </div>
            ) : (
              <div>
                <Label className="text-xs">Metodo di pagamento ricevuto</Label>
                <PaymentMethodSelect value={paidMethod} onChange={setPaidMethod} testid="accept-payment-method" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptedOpen(null)} data-testid="accept-cancel">Annulla</Button>
            <Button
              onClick={confirmOfferAccepted}
              disabled={!acceptedFlags.accepted || !acceptedFlags.paid}
              data-testid="accept-confirm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-300"
            >
              Conferma e crea polizza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma invio offerta (email + WhatsApp opzionale) */}
      <Dialog open={!!sendOpen} onOpenChange={(o) => { if (!o) setSendOpen(null); }}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-blue-600" /> Invia offerta al cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Inviare l&apos;offerta <strong>{sendOpen?.offer.offer_number}</strong> ({formatCurrency(sendOpen?.offer.amount)}) a <strong>{sendOpen?.offer.client_name}</strong>?
            </p>
            {/* 🆕 Mini riepilogo cliente per evitare invii sbagliati */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1" data-testid="send-client-summary">
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Cliente</span>
                <span className="font-medium text-slate-800 text-right break-words">{sendOpen?.offer.client_name || "—"}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">📧 Email</span>
                <span className={`text-right break-all ${sendOpen?.offer.client_email ? "text-slate-800" : "text-rose-600 italic"}`}>
                  {sendOpen?.offer.client_email || "— (mancante)"}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">📱 Cellulare</span>
                <span className={`text-right break-all ${sendOpen?.offer.client_mobile ? "text-slate-800" : "text-slate-400 italic"}`}>
                  {sendOpen?.offer.client_mobile || "—"}
                </span>
              </div>
            </div>
            <div className="rounded-md border border-green-300 bg-green-50 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="send-offer-also-whatsapp"
                  checked={!!sendOpen?.alsoWhatsapp}
                  onChange={(e) => setSendOpen({ ...sendOpen, alsoWhatsapp: e.target.checked })}
                  disabled={!sendOpen?.offer.client_mobile}
                  className="mt-0.5 h-4 w-4 cursor-pointer accent-green-600 disabled:opacity-40"
                />
                <span>
                  <strong>📱 Apri WhatsApp dopo l&apos;invio</strong>
                  <span className="block text-xs text-slate-600 mt-0.5">
                    {sendOpen?.offer.client_mobile
                      ? `Apre WhatsApp verso ${sendOpen.offer.client_mobile} con messaggio precompilato e link diretto al pagamento.`
                      : "Cliente senza numero di cellulare: opzione non disponibile."}
                  </span>
                </span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none mt-2 pt-2 border-t border-slate-100">
                <input
                  type="checkbox"
                  data-testid="include-coverages-toggle"
                  checked={sendOpen?.includeCoverages !== false}
                  onChange={(e) => setSendOpen({ ...sendOpen, includeCoverages: e.target.checked })}
                  className="h-4 w-4 cursor-pointer accent-blue-600"
                />
                <span className="text-sm flex flex-col">
                  <span className="font-medium">📋 Includi dettaglio coperture</span>
                  <span className="text-[11px] text-slate-500">
                    {sendOpen?.includeCoverages !== false
                      ? "Email + PDF mostrano garanzie, massimali, franchigie e voci accessorie."
                      : "⚠️ Email + PDF mostreranno solo l'importo da pagare (nessun dettaglio garanzie/voci)."}
                  </span>
                </span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none mt-2 pt-2 border-t border-slate-100">
                <input
                  type="checkbox"
                  data-testid="request-signature-toggle"
                  checked={!!sendOpen?.requestSignature}
                  onChange={(e) => setSendOpen({ ...sendOpen, requestSignature: e.target.checked })}
                  disabled={!sendOpen?.offer.client_id}
                  className="h-4 w-4 cursor-pointer accent-emerald-600"
                />
                <span className="text-sm flex flex-col">
                  <span className="font-medium">✍️ Richiedi firma OTP</span>
                  <span className="text-[11px] text-slate-500">
                    {sendOpen?.offer.client_id
                      ? "Crea richiesta firma · il cliente firma via codice OTP (email/WhatsApp)"
                      : <span className="text-rose-600">Cliente non associato — firma non disponibile</span>}
                  </span>
                </span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(null)}>Annulla</Button>
            {(() => {
              const hasEmail = !!sendOpen?.offer.client_email;
              const offerVariants = Array.isArray(sendOpen?.offer.variants) ? sendOpen.offer.variants : [];
              const hasVariants = offerVariants.length > 0;
              const hasFinalized = !!sendOpen?.offer.winner_variant_id;
              const disabled = !hasEmail || !hasVariants || !hasFinalized;
              return (
                <div className="flex flex-col items-end gap-1">
                  {disabled && (
                    <span className="text-[10px] text-rose-600 text-right">
                      {!hasEmail && <span>Email cliente mancante</span>}
                      {!hasEmail && (!hasVariants || !hasFinalized) && <span> · </span>}
                      {!hasVariants && <span>Nessuna variante inserita</span>}
                      {hasVariants && !hasFinalized && <span>Nessuna variante finalizzata</span>}
                    </span>
                  )}
                  <Button data-testid="confirm-send-offer-btn" onClick={confirmSend} disabled={disabled} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Send className="h-4 w-4 mr-2" /> Invia
                  </Button>
                </div>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Dialog Anteprima allegato offerta (PDF/immagine inline) */}
      <Dialog open={!!previewAtt} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="w-[95vw] max-w-5xl h-[90vh] max-h-[90vh] flex flex-col p-0" data-testid="offer-attachment-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Eye className="h-5 w-5 text-emerald-700" />
              <span className="truncate">{previewAtt?.att?.filename || "Anteprima allegato"}</span>
              {previewAtt?.att && (
                <a
                  href={previewAtt.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-xs text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                  data-testid="offer-attachment-preview-open-tab"
                  title="Apri in nuova scheda"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Nuova scheda
                </a>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-100">
            {previewAtt && (
              (previewAtt.att?.content_type || previewAtt.mime || "").startsWith("image/") ? (
                <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                  <img
                    src={previewAtt.url}
                    alt={previewAtt.att.filename}
                    className="max-w-full max-h-full object-contain"
                    data-testid="offer-attachment-preview-image"
                  />
                </div>
              ) : (
                // 🆕 Usa react-pdf (PDF.js) invece di <object>/<iframe> per garantire il rendering inline
                // su tutti i browser (Chrome, Safari, Firefox) senza dipendere dai plugin nativi.
                <PdfPreview fileUrl={previewAtt.url} />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Dialog Riepilogo offerta (sola lettura, compatto) */}
      <Dialog open={!!viewOpen} onOpenChange={(o) => { if (!o) setViewOpen(null); }}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="offer-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-slate-600" /> Riepilogo offerta
            </DialogTitle>
          </DialogHeader>
          {viewOpen && (() => {
            const st = STATUS_LABEL[viewOpen.status] || STATUS_LABEL.draft;
            return (
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between gap-2 flex-wrap pb-2 border-b border-slate-200">
                  <div className="font-mono font-semibold text-base text-slate-800" data-testid="view-offer-number">
                    {viewOpen.offer_number}
                  </div>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${st.cls}`}>{st.label}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Cliente</div>
                    <div className="text-slate-800 font-medium break-words">{viewOpen.client_name || "—"}</div>
                    {viewOpen.client_email && <div className="text-xs text-slate-500 break-all">{viewOpen.client_email}</div>}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Preventivo</div>
                    <div className="font-mono text-slate-800">{viewOpen.quote_number || "—"}</div>
                    <div className="text-xs text-slate-500">{viewOpen.quote_type || ""}</div>
                  </div>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Importo</div>
                  <div className="text-2xl font-bold text-slate-900 tabular-nums">{formatCurrency(viewOpen.amount)}</div>
                  <div className="text-xs text-slate-500 mt-1">Scadenza: <strong>{formatDate(viewOpen.valid_until)}</strong></div>
                </div>
                {Array.isArray(viewOpen.coverages) && viewOpen.coverages.length > 0 && (() => {
                  const covOnly = viewOpen.coverages.filter((c) => (c.kind || "coverage") !== "line_item");
                  const liItems = viewOpen.coverages.filter((c) => (c.kind || "coverage") === "line_item");
                  const sumCov = covOnly.reduce((a, c) => a + (Number(c.premium) || 0), 0);
                  const sumLi = liItems.reduce((a, c) => a + (Number(c.premium) || 0), 0);
                  const computed = sumCov + sumLi;
                  const totalRow = (sumCov > 0 || sumLi > 0);
                  return (
                    <>
                      {covOnly.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Coperture</div>
                            {sumCov > 0 && <div className="text-[11px] font-mono text-slate-600">Subtotale: <strong>{formatCurrency(sumCov)}</strong></div>}
                          </div>
                          <ul className="space-y-1">
                            {covOnly.map((c, i) => (
                              <li key={i} className="flex justify-between gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                                <div className="min-w-0">
                                  <div className="font-medium text-slate-800 break-words">{c.name}</div>
                                  {(c.sum_insured || c.deductible || c.description) && (
                                    <div className="text-[11px] text-slate-500 break-words">
                                      {c.sum_insured ? `Massimale ${formatCurrency(c.sum_insured)}` : ""}
                                      {c.deductible ? ` · Franchigia ${c.deductible}` : ""}
                                      {c.description ? ` · ${c.description}` : ""}
                                    </div>
                                  )}
                                </div>
                                {c.premium != null && (
                                  <span className="font-mono text-slate-700 whitespace-nowrap">{formatCurrency(c.premium)}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {liItems.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Voci accessorie</div>
                            {sumLi > 0 && <div className="text-[11px] font-mono text-indigo-700">Subtotale: <strong>{formatCurrency(sumLi)}</strong></div>}
                          </div>
                          <ul className="space-y-1">
                            {liItems.map((c, i) => (
                              <li key={i} className="flex justify-between gap-2 text-xs bg-indigo-50/40 border border-indigo-200 rounded px-2 py-1.5">
                                <div className="min-w-0">
                                  <div className="font-medium text-slate-800 break-words">{c.name}</div>
                                  {c.description && <div className="text-[11px] text-slate-500 break-words">{c.description}</div>}
                                </div>
                                {c.premium != null && (
                                  <span className="font-mono text-indigo-700 whitespace-nowrap">{formatCurrency(c.premium)}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {totalRow && (() => {
                        const delta = +(Number(viewOpen.amount) - computed).toFixed(2);
                        const ok = Math.abs(delta) <= 1;
                        return (
                          <div className={`rounded-md border px-3 py-2 ${ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-300"}`} data-testid="view-subtotals">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-slate-600">Subtotale coperture</span>
                              <span className="font-mono tabular-nums text-slate-800">{formatCurrency(sumCov)}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs mt-1">
                              <span className="text-slate-600">Subtotale voci accessorie</span>
                              <span className="font-mono tabular-nums text-slate-800">{formatCurrency(sumLi)}</span>
                            </div>
                            <div className="border-t border-slate-300 mt-1.5 pt-1.5 flex justify-between items-center">
                              <span className="text-xs font-semibold text-slate-700">Totale calcolato</span>
                              <span className="text-sm font-bold font-mono tabular-nums text-slate-900">{formatCurrency(computed)}</span>
                            </div>
                            {!ok && (
                              <div className="text-[10px] text-amber-800 mt-1.5 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Importo offerta {formatCurrency(viewOpen.amount)} — differenza {formatCurrency(delta)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
                {viewOpen.notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Note</div>
                    <div className="text-xs text-slate-700 italic bg-amber-50 border border-amber-200 rounded p-2">{viewOpen.notes}</div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-2 border-t border-slate-200">
                  {viewOpen.created_at && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Creata</div>
                      <div className="text-slate-700">{formatDateTime(viewOpen.created_at)}</div>
                      {viewOpen.created_by_name && <div className="text-slate-500">da {viewOpen.created_by_name}</div>}
                    </div>
                  )}
                  {viewOpen.sent_at && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Inviata</div>
                      <div className="text-slate-700">{formatDateTime(viewOpen.sent_at)}</div>
                    </div>
                  )}
                  {viewOpen.paid_at && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Pagata</div>
                      <div className="text-slate-700">{formatDateTime(viewOpen.paid_at)}</div>
                      {viewOpen.payment_method && <div className="text-slate-500">via {viewOpen.payment_method}</div>}
                    </div>
                  )}
                  {viewOpen.cancelled_at && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Annullata</div>
                      <div className="text-slate-700">{formatDateTime(viewOpen.cancelled_at)}</div>
                      {viewOpen.cancelled_by_name && <div className="text-slate-500">da {viewOpen.cancelled_by_name}</div>}
                      {viewOpen.cancelled_reason && <div className="text-slate-500 italic">{viewOpen.cancelled_reason}</div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            {viewOpen?.status === "inviata" && (
              <Button variant="outline" onClick={() => copyLink(viewOpen)} data-testid="view-copy-link-btn">
                <Copy className="h-4 w-4 mr-2" /> Copia link pagamento
              </Button>
            )}
            <Button onClick={() => setViewOpen(null)} data-testid="view-close-btn">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Esporta offerte */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-600" /> Esporta offerte finali
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">Filtra opzionalmente per periodo di creazione e stato. Le offerte nel cestino sono sempre escluse.</p>
            <div>
              <Label className="text-xs">Formato</Label>
              <Select value={exportForm.fmt} onValueChange={(v) => setExportForm({ ...exportForm, fmt: v })}>
                <SelectTrigger data-testid="export-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                  <SelectItem value="csv">CSV (compatibile Excel italiano)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Da</Label>
                <DatePicker data-testid="export-date-from" value={exportForm.date_from} onChange={(e) => setExportForm({ ...exportForm, date_from: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">A</Label>
                <DatePicker data-testid="export-date-to" value={exportForm.date_to} onChange={(e) => setExportForm({ ...exportForm, date_to: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={exportForm.status} onValueChange={(v) => setExportForm({ ...exportForm, status: v })}>
                <SelectTrigger data-testid="export-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="draft">Bozza</SelectItem>
                  <SelectItem value="inviata">Inviata</SelectItem>
                  <SelectItem value="pagata">Pagata</SelectItem>
                  <SelectItem value="annullata">Annullata</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)} disabled={exporting}>Annulla</Button>
            <Button data-testid="confirm-export-btn" onClick={doExport} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Download className="h-4 w-4 mr-2" /> {exporting ? "Esportazione…" : "Scarica file"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog conferma creazione/modifica offerta con riepilogo */}
      <Dialog open={confirmCreateOpen} onOpenChange={(o) => { if (!o) setConfirmCreateOpen(false); }}>
        <DialogContent className="w-[95vw] max-w-md max-h-[90vh] flex flex-col" data-testid="confirm-create-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" /> {editing ? "Conferma modifica offerta" : "Conferma creazione offerta"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6 py-1">
          {(() => {
            const selectedQuote = quotes.find((qq) => qq.id === form.quote_id);
            const client = selectedQuote ? clientsMap[selectedQuote.client_id] : null;
            const clientFullName = client ? `${client.surname || ""} ${client.name || ""}`.trim() : "—";
            const TYPE_LBL = { auto: "Auto", moto: "Moto", casa: "Casa", vita: "Vita", salute: "Salute", infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "Resp. civile" };
            const namedCovs = (form.coverages || []).filter((c) => c.name?.trim());
            const premiums = namedCovs.map((c) => Number(c.premium) || 0).filter((n) => n > 0);
            const sumPremiums = premiums.reduce((a, b) => a + b, 0);
            const amount = Number(form.amount) || 0;
            const diff = +(amount - sumPremiums).toFixed(2);
            const mismatch = premiums.length > 0 && Math.abs(diff) > 1;
            return (
              <div className="space-y-3 text-sm">
                <p className="text-slate-600">Verifica i dati prima di {editing ? "salvare le modifiche" : "creare l'offerta"}:</p>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1.5">
                  <div className="flex justify-between gap-2 text-xs">
                    <span className="text-slate-500">Cliente</span>
                    <span className="font-medium text-slate-800 text-right break-words">{clientFullName}</span>
                  </div>
                  <div className="flex justify-between gap-2 text-xs">
                    <span className="text-slate-500">Email</span>
                    <span className={`text-right break-all ${client?.email ? "text-slate-800" : "text-rose-600 italic"}`}>
                      {client?.email || (selectedQuote?.details?.owner_email
                        ? <>{selectedQuote.details.owner_email} <span className="text-[10px] text-amber-700">(da preventivo)</span></>
                        : "— mancante")}
                    </span>
                  </div>
                  {(client?.mobile || selectedQuote?.details?.owner_phone) && (
                    <div className="flex justify-between gap-2 text-xs">
                      <span className="text-slate-500">Cellulare</span>
                      <span className="text-slate-800 text-right break-all">{client?.mobile || selectedQuote?.details?.owner_phone}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2 text-xs">
                    <span className="text-slate-500">Preventivo</span>
                    <span className="font-mono text-slate-800">
                      {selectedQuote?.quote_number || "—"} · {TYPE_LBL[selectedQuote?.type] || selectedQuote?.type || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 text-xs">
                    <span className="text-slate-500">Coperture</span>
                    <span className="text-slate-800">{namedCovs.length} {namedCovs.length === 1 ? "garanzia" : "garanzie"}</span>
                  </div>
                </div>
                {/* 🆕 Riepilogo variante finalizzata (se presente) */}
                {(() => {
                  const winner = (variants || []).find((v) => v.id === winnerVariantId);
                  if (!winner) return null;
                  const covCount = Array.isArray(winner.coverages) ? winner.coverages.length : 0;
                  return (
                    <button
                      type="button"
                      data-testid="finalized-variant-recap"
                      onClick={() => {
                        setConfirmCreateOpen(false);
                        setTimeout(() => {
                          const el = document.getElementById("offer-variants-section");
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 250);
                      }}
                      className="w-full text-left rounded-md bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors px-3 py-2.5 cursor-pointer"
                      title="Clicca per tornare a modificare la variante finalizzata"
                    >
                      <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold flex items-center justify-between gap-2">
                        <span>✅ Variante finalizzata</span>
                        <span className="text-[10px] text-blue-600 normal-case font-normal">↩ modifica</span>
                      </div>
                      <div className="mt-1 text-xs text-blue-900">
                        <strong>{winner.company || "—"}</strong>
                        {winner.amount != null && <> · <span className="font-mono tabular-nums">{formatCurrency(Number(winner.amount) || 0)}</span></>}
                        {covCount > 0 && <> · {covCount} garanzi{covCount === 1 ? "a" : "e"}</>}
                      </div>
                      {winner.notes && (
                        <div className="text-[11px] text-blue-700 mt-1 italic truncate" title={winner.notes}>{winner.notes}</div>
                      )}
                    </button>
                  );
                })()}
                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Importo da pagare</div>
                  <div className="text-2xl font-bold text-emerald-900 tabular-nums">{formatCurrency(amount)}</div>
                  {premiums.length > 0 && (
                    <div className="text-[11px] text-emerald-700 mt-0.5">
                      Somma premi coperture: {formatCurrency(sumPremiums)}
                    </div>
                  )}
                </div>
                {namedCovs.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-md">
                    <ul className="divide-y divide-slate-100">
                      {namedCovs.map((c, i) => (
                        <li key={i} className="px-3 py-1.5 text-xs flex justify-between gap-2">
                          <span className="text-slate-700 truncate">{c.name}</span>
                          {Number(c.premium) > 0 && <span className="font-mono text-slate-500 whitespace-nowrap">{formatCurrency(Number(c.premium))}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {mismatch && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      <strong>Info:</strong> l&apos;importo differisce dalla somma dei premi (differenza {formatCurrency(diff)}). Puoi salvare comunque — la somma è indicativa.
                    </span>
                  </div>
                )}
                {/* Opzione "Crea e invia subito" — solo per nuove offerte o draft modificate */}
                {(!editing || editing.status === "draft") && (
                  <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${confirmAlsoSend ? "bg-blue-50 border-blue-300" : "bg-slate-50 border-slate-200"}`}>
                    <input
                      type="checkbox"
                      data-testid="confirm-also-send"
                      checked={confirmAlsoSend}
                      onChange={(e) => setConfirmAlsoSend(e.target.checked)}
                      className="mt-0.5 h-4 w-4 cursor-pointer accent-blue-600"
                    />
                    <span className="text-xs">
                      <strong className="text-slate-800">📧 Invia subito al cliente</strong>
                      <span className="block text-slate-600 mt-0.5">
                        {(client?.email || selectedQuote?.details?.owner_email)
                          ? <>Dopo il salvataggio invia automaticamente email + notifica push a <strong>{client?.email || selectedQuote.details.owner_email}</strong>.</>
                          : "Cliente senza email: l'invio automatico verrà saltato."}
                      </span>
                    </span>
                  </label>
                )}
                <p className="text-[11px] text-slate-500 italic">
                  Scadenza automatica a 5 giorni dalla creazione. Dopo la conferma puoi sempre inviarla al cliente, modificarla o annullarla.
                </p>
              </div>
            );
          })()}
          </div>
          <DialogFooter className="flex-shrink-0 border-t border-slate-200 pt-3 mt-1">
            <Button variant="outline" onClick={() => setConfirmCreateOpen(false)} disabled={busy} data-testid="confirm-create-back-btn">
              Torna a modifica
            </Button>
            {(() => {
              const selectedQuoteSub = quotes.find((qq) => qq.id === form.quote_id);
              const subClient = selectedQuoteSub ? clientsMap[selectedQuoteSub.client_id] : null;
              const subEmail = subClient?.email || selectedQuoteSub?.details?.owner_email || "";
              const hasVariants = Array.isArray(variants) && variants.length > 0;
              const hasFinalized = !!winnerVariantId;
              const willSend = confirmAlsoSend && (!editing || editing.status === "draft");
              const sendDisabled = willSend && (!subEmail || !hasVariants || !hasFinalized);
              return (
                <div className="flex flex-col items-end gap-1">
                  {sendDisabled && (
                    <span className="text-[10px] text-rose-600 text-right">
                      {!subEmail && <span>Email cliente mancante</span>}
                      {!subEmail && (!hasVariants || !hasFinalized) && <span> · </span>}
                      {!hasVariants && <span>Nessuna variante inserita</span>}
                      {hasVariants && !hasFinalized && <span>Nessuna variante finalizzata</span>}
                    </span>
                  )}
                  <Button data-testid="confirm-create-submit-btn" onClick={doSubmit} disabled={busy || sendDisabled} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Check className="h-4 w-4 mr-2" />
                    {busy
                      ? "Salvataggio…"
                      : (willSend
                        ? (editing ? "Salva e invia" : "Crea e invia")
                        : (editing ? "Conferma e salva" : "Conferma e crea"))}
                  </Button>
                </div>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog anteprima cliente con iframe */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl w-[95vw] h-[85vh] p-0 flex flex-col" data-testid="preview-client-dialog">
          <DialogHeader className="px-4 py-3 border-b border-slate-200 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Anteprima cliente — Offerta {editing?.offer_number}
              <button
                type="button"
                onClick={() => setPreviewVersion((v) => v + 1)}
                data-testid="preview-client-reload"
                className="ml-auto inline-flex items-center gap-1 text-xs font-semibold border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded px-2 py-1"
                title="Ricarica l'anteprima (mostra l'ultima versione salvata)"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Ricarica
              </button>
              <a
                href={editing?.share_token ? `${window.location.origin}/offerta/${editing.share_token}` : "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline font-normal"
              >
                Apri in nuova scheda ↗
              </a>
            </DialogTitle>
            <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1">
              ✓ L&apos;offerta è stata <strong>salvata automaticamente</strong> prima dell&apos;anteprima. Usa <strong>Ricarica</strong> se hai effettuato altre modifiche.
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-100">
            {editing?.share_token && previewOpen && (
              <iframe
                key={previewVersion}
                src={`${window.location.origin}/offerta/${editing.share_token}?_t=${previewVersion}`}
                title="Anteprima cliente"
                className="w-full h-full border-0 bg-white"
                data-testid="preview-client-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* 🆕 Dialog "Rinvia offerta aggiornata al cliente?" — l'operatore sceglie esplicitamente */}
      <Dialog open={!!resendPrompt} onOpenChange={(o) => { if (!o && !resending) setResendPrompt(null); }}>
        <DialogContent className="max-w-md" data-testid="resend-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              Rinviare l&apos;offerta al cliente?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>
              Hai modificato l&apos;offerta <strong>{resendPrompt?.offerNumber}</strong> che era
              già stata inviata al cliente.
            </p>
            <p className="text-slate-600">
              Vuoi <strong>RINVIARE</strong> l&apos;offerta aggiornata? L&apos;email/PDF aggiornati
              saranno inviati di nuovo (segnato come &quot;rinvio&quot;).
            </p>
            <p className="text-[12px] text-slate-500 italic">
              Le modifiche sono già state salvate: se scegli &quot;No&quot;, l&apos;offerta resta
              aggiornata ma non viene rinviata.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setResendPrompt(null)}
              disabled={resending}
              data-testid="resend-prompt-no-btn"
            >
              <XIcon className="h-4 w-4 mr-2" />
              No, salva e basta
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={resending}
              data-testid="resend-prompt-yes-btn"
              onClick={async () => {
                if (!resendPrompt) return;
                setResending(true);
                try {
                  await api.post(`/quote-offers/${resendPrompt.offerId}/send?include_coverages=true`);
                  toast.success("Offerta rinviata al cliente");
                  setResendPrompt(null);
                  load();
                } catch (e) {
                  toast.error(`Modifiche salvate ma rinvio fallito: ${formatApiError(e)}`);
                } finally {
                  setResending(false);
                }
              }}
            >
              <Send className="h-4 w-4 mr-2" />
              {resending ? "Invio in corso…" : "Sì, rinvia al cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Datalists globali: garanzie polizze + voci accessorie predefinite */}
      <datalist id="policy-coverages-options">
        {(policyCoverages || []).map((p) => (
          <option key={p.id || p.code} value={p.label}>{p.description || ""}</option>
        ))}
      </datalist>
      <datalist id="accessory-items-options">
        {(accessoryItems || []).filter((a) => a.is_active !== false).map((a) => (
          <option key={a.id} value={a.name}>
            {a.default_premium != null ? formatCurrency(a.default_premium) : ""}
            {a.default_description ? ` · ${a.default_description}` : ""}
          </option>
        ))}
      </datalist>

      {/* 🆕 Dialog anteprima email prima dell'invio (unificato per tutti i flussi) */}
      <EmailPreviewDialog
        open={!!emailPreviewCtx}
        onOpenChange={(o) => { if (!o) setEmailPreviewCtx(null); }}
        emailTypeKey={emailPreviewCtx?.typeKey}
        title={emailPreviewCtx?.title || "Anteprima email prima dell'invio"}
        fetchPreview={emailPreviewCtx?.fetchPreview || (async () => ({ subject: "", html_body: "", to: [] }))}
        readOnly={emailPreviewCtx?.readOnly === true}
        onConfirmSend={async () => {
          if (emailPreviewCtx?.onConfirm) await emailPreviewCtx.onConfirm();
          setEmailPreviewCtx(null);
        }}
      />

      {/* 🆕 Dialog rinomina file allegati offerta PRIMA di caricarli */}
      <Dialog open={!!pendingOfferUpload} onOpenChange={(o) => { if (!o && !attUploadBusy) setPendingOfferUpload(null); }}>
        <DialogContent className="max-w-lg" data-testid="offer-attachment-rename-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <FileText className="h-5 w-5" /> Rinomina file prima di caricare
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-slate-600">Personalizza il nome di ogni file. L&apos;estensione originale viene mantenuta automaticamente.</p>
            {pendingOfferUpload?.files.map((it, idx) => (
              <div key={idx} className="border border-slate-200 rounded p-2 bg-slate-50 space-y-1" data-testid={`offer-attachment-rename-row-${idx}`}>
                <div className="text-[11px] text-slate-500 truncate" title={it.file.name}>
                  📄 Originale: <strong className="text-slate-700">{it.file.name}</strong> ({formatFileSize(it.file.size)})
                </div>
                <Input
                  data-testid={`offer-attachment-rename-input-${idx}`}
                  value={it.newName}
                  onChange={(e) => setPendingOfferUpload((prev) => {
                    if (!prev) return prev;
                    const files = [...prev.files];
                    files[idx] = { ...files[idx], newName: e.target.value };
                    return { ...prev, files };
                  })}
                  className="text-sm h-8"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingOfferUpload(null)} disabled={attUploadBusy} data-testid="offer-attachment-rename-cancel">
              Annulla
            </Button>
            <Button
              onClick={confirmOfferAttachmentUpload}
              disabled={attUploadBusy || !pendingOfferUpload?.files.length}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="offer-attachment-rename-confirm"
            >
              <FileText className="h-4 w-4 mr-1" /> Carica {pendingOfferUpload?.files.length ? `(${pendingOfferUpload.files.length})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// 🆕 Intestazione colonna ordinabile per la tabella Offerte Finali
function SortableTh({ label, sortKey, sortConfig, onSort, align = "left", disabled = false }) {
  const active = !disabled && sortConfig.key === sortKey;
  const dir = active ? sortConfig.dir : null;
  const alignCls = align === "right" ? "text-right" : "text-left";
  const justifyCls = align === "right" ? "justify-end" : "justify-start";
  return (
    <th className={`px-4 py-3 ${alignCls}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-blue-700 ${active ? "text-blue-700 font-semibold" : ""} ${justifyCls} w-full ${disabled ? "opacity-60" : ""}`}
        data-testid={`offers-sort-${sortKey}`}
        title={disabled ? `Ordina per ${label} (disattiva il raggruppamento)` : `Ordina per ${label}`}
      >
        <span>{label}</span>
        <span className="text-[10px] leading-none inline-flex flex-col">
          <ChevronUp className={`h-2.5 w-2.5 ${active && dir === "asc" ? "text-blue-700" : "text-slate-300"}`} />
          <ChevronDown className={`h-2.5 w-2.5 -mt-0.5 ${active && dir === "desc" ? "text-blue-700" : "text-slate-300"}`} />
        </span>
      </button>
    </th>
  );
}


// 🆕 Feb 2026 — Contatore real-time di ciò che il cliente vedrà nel portale/PDF/email.
// Replica la logica di `_resolve_offer_client_coverages` (backend):
//   - Se esiste una variante finalizzata (is_winner): USA SOLO le sue coverages (fonte di verità)
//   - Se non esiste winner e offer.coverages è vuota: usa la variante più economica
//   - Altrimenti: usa offer.coverages
//   - Filtra client_visible=false e segnala prezzi nascosti (client_price_visible=false)
function ClientVisibilityCounter({ form, variants }) {
  const offerCovs = Array.isArray(form.coverages) ? form.coverages : [];
  const winner = Array.isArray(variants) ? variants.find((v) => v.is_winner) : null;

  let srcCovs = [];
  let sourceLabel = "offerta principale";

  if (winner) {
    srcCovs = [...(winner.coverages || [])];
    sourceLabel = `variante ${winner.company} (finalizzata)`;
  } else if (!offerCovs.length && Array.isArray(variants) && variants.length > 0) {
    const cheapest = [...variants].sort((a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0))[0];
    if (cheapest) {
      srcCovs = [...(cheapest.coverages || [])];
      sourceLabel = `variante più economica (${cheapest.company})`;
    }
  } else {
    srcCovs = [...offerCovs];
  }
  const visible = srcCovs.filter((c) => c.client_visible !== false);
  const guarantees = visible.filter((c) => (c.kind || "coverage") !== "line_item");
  const lineItems = visible.filter((c) => (c.kind || "coverage") === "line_item");
  const hiddenRows = srcCovs.length - visible.length;
  const hiddenPrices = visible.filter((c) => c.client_price_visible === false).length;
  const total = guarantees.length + lineItems.length;
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3" data-testid="offer-client-visibility-counter">
      <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-1.5 flex items-center gap-1">
        👁️ Il cliente vedrà
        <span className="ml-auto text-slate-500 font-normal normal-case text-[10px]">Fonte: {sourceLabel}</span>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="inline-flex items-center gap-1 bg-white border border-emerald-300 rounded px-2 py-0.5 font-medium text-emerald-800" data-testid="visibility-count-guarantees">
          ✅ {guarantees.length} {guarantees.length === 1 ? "garanzia" : "garanzie"}
        </span>
        <span className="inline-flex items-center gap-1 bg-white border border-indigo-300 rounded px-2 py-0.5 font-medium text-indigo-800" data-testid="visibility-count-lineitems">
          📦 {lineItems.length} {lineItems.length === 1 ? "voce accessoria" : "voci accessorie"}
        </span>
        {hiddenRows > 0 && (
          <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-300 rounded px-2 py-0.5 text-slate-600" title="Righe nascoste al cliente (client_visible=false)" data-testid="visibility-count-hidden-rows">
            🚫 {hiddenRows} nascoste
          </span>
        )}
        {hiddenPrices > 0 && (
          <span className="inline-flex items-center gap-1 bg-amber-100 border border-amber-300 rounded px-2 py-0.5 text-amber-800" title="Righe visibili al cliente ma con prezzo omesso" data-testid="visibility-count-hidden-prices">
            💰🚫 {hiddenPrices} senza prezzo
          </span>
        )}
      </div>
      {total === 0 && (
        <p className="text-[10px] text-amber-700 mt-2">
          ⚠ Nessuna riga visibile al cliente. Aggiungi almeno una garanzia o voce accessoria (o rimuovi il flag &quot;nascosta&quot;) prima di inviare.
        </p>
      )}
    </div>
  );
}

